import React, { useState, useEffect } from 'react';
import './SuperAdminView.css';
import TransactionsTable from './TransactionsTable';
import MediaStorageGraph from './MediaStorageGraph';

const API_BASE_URL = 'https://imselling-marketplace-production.up.railway.app/api';

// ============================================================================
// SAFE TOKEN HELPER — never crashes if localStorage is unavailable
// ============================================================================
const getToken = () => {
  try { return localStorage.getItem('token') || ''; }
  catch { return ''; }
};

// ============================================================================
// INLINE API HELPERS
// ============================================================================
const api = {
  headers: () => ({
    'Authorization': `Token ${getToken()}`,
    'Content-Type': 'application/json',
  }),

  async fetchAdmins() {
    const res = await fetch(`${API_BASE_URL}/admin/admins/`, { headers: this.headers() });
    if (!res.ok) throw new Error(`fetchAdmins: ${res.status}`);
    const data = await res.json();
    return Array.isArray(data) ? data : (data.results || []);
  },

  async createAdmin(payload) {
    const res = await fetch(`${API_BASE_URL}/admin/admins/`, {
      method: 'POST',
      headers: this.headers(),
      body: JSON.stringify(payload),
    });
    return res.json();
  },

  async updateAdmin(id, payload) {
    const res = await fetch(`${API_BASE_URL}/admin/users/${id}/`, {
      method: 'PATCH',
      headers: this.headers(),
      body: JSON.stringify(payload),
    });
    return res.json();
  },

  async toggleAdminStatus(id) {
    const res = await fetch(`${API_BASE_URL}/admin/users/${id}/toggle-status/`, {
      method: 'POST',
      headers: this.headers(),
    });
    return res.json();
  },

  async removeAdminRights(id) {
    const res = await fetch(`${API_BASE_URL}/admin/admins/${id}/`, {
      method: 'DELETE',
      headers: this.headers(),
    });
    if (res.status === 204) return { success: true };
    return res.json();
  },

  async verifyUser(id, verdict) {
    const res = await fetch(`${API_BASE_URL}/admin/users/${id}/verify/`, {
      method: 'POST',
      headers: this.headers(),
      body: JSON.stringify({ verdict }),
    });
    return res.json();
  },

  async deleteUser(id) {
    const res = await fetch(`${API_BASE_URL}/admin/users/${id}/`, {
      method: 'DELETE',
      headers: this.headers(),
    });
    if (res.status === 204 || res.status === 200) return { success: true };
    return res.json();
  },

  async fetchActivityLogs() {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/dashboard/activity/`, {
        headers: this.headers(),
      });
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    } catch {
      return [];
    }
  },
};

// ============================================================================
// EMPTY FORM STATES
// ============================================================================
const EMPTY_NEW_ADMIN = {
  email: '', phone: '', first_name: '', last_name: '',
  password: '', role: 'admin',
};

const EMPTY_EDIT_ADMIN = {
  first_name: '', last_name: '', email: '', phone: '',
  username: '', is_superuser: false, is_active: true,
};

// ============================================================================
// SYSTEM CONFIG TABS — 6 tabs with full panel content
// ============================================================================
const CONFIG_TABS = [
  {
    id: 'security',
    icon: '🔐',
    label: 'Security',
    title: 'Security Settings',
    subtitle: 'Manage authentication, 2FA, session control and access policies',
    color: '#6366f1',
    lightColor: '#eef2ff',
    fields: [
      { label: 'Two-Factor Authentication', type: 'toggle', value: true,  hint: 'Require 2FA for all admin logins' },
      { label: 'Session Timeout (minutes)', type: 'number', value: 30,    hint: 'Auto-logout after inactivity' },
      { label: 'Max Login Attempts',        type: 'number', value: 5,     hint: 'Lockout after failed attempts' },
      { label: 'Password Expiry (days)',    type: 'number', value: 90,    hint: 'Force password reset period' },
      { label: 'IP Whitelist',              type: 'text',   value: '',    hint: 'Comma-separated allowed IPs' },
      { label: 'Audit Log Retention (days)',type: 'number', value: 365,   hint: 'How long to keep activity logs' },
    ],
  },
  {
    id: 'payment',
    icon: '💳',
    label: 'Payment',
    title: 'Payment Gateway',
    subtitle: 'API keys, commission rates, payout schedules and currency settings',
    color: '#059669',
    lightColor: '#ecfdf5',
    fields: [
      { label: 'M-Pesa API Key',         type: 'password', value: '',    hint: 'Safaricom Daraja API key' },
      { label: 'M-Pesa Shortcode',       type: 'text',     value: '',    hint: 'Business short code' },
      { label: 'Platform Commission (%)', type: 'number',  value: 5,     hint: 'Percentage taken per transaction' },
      { label: 'Minimum Withdrawal',     type: 'number',   value: 100,   hint: 'Minimum KES for payout request' },
      { label: 'Payout Schedule',        type: 'select',   value: 'weekly', options: ['daily','weekly','monthly'], hint: 'How often payouts are processed' },
      { label: 'Auto-approve Payouts',   type: 'toggle',   value: false, hint: 'Skip manual review for payouts' },
    ],
  },
  {
    id: 'email',
    icon: '📧',
    label: 'Email & SMS',
    title: 'Email & SMS',
    subtitle: 'Configure SMTP, SMS providers and notification templates',
    color: '#0ea5e9',
    lightColor: '#f0f9ff',
    fields: [
      { label: 'SMTP Host',           type: 'text',     value: 'smtp.gmail.com', hint: 'Outgoing mail server' },
      { label: 'SMTP Port',           type: 'number',   value: 587,              hint: '587 for TLS, 465 for SSL' },
      { label: 'SMTP Username',       type: 'text',     value: '',               hint: 'Email account username' },
      { label: 'SMTP Password',       type: 'password', value: '',               hint: 'Email account password' },
      { label: 'SMS Provider',        type: 'select',   value: 'africas_talking', options: ['africas_talking','twilio','nexmo'], hint: 'Bulk SMS gateway provider' },
      { label: 'SMS API Key',         type: 'password', value: '',               hint: 'Provider API key' },
    ],
  },
  {
    id: 'backup',
    icon: '🗄️',
    label: 'Backup',
    title: 'Database Backup',
    subtitle: 'Automated backup schedules, retention policies and restore options',
    color: '#d97706',
    lightColor: '#fffbeb',
    fields: [
      { label: 'Auto Backup',               type: 'toggle', value: true,    hint: 'Enable scheduled backups' },
      { label: 'Backup Frequency',          type: 'select', value: 'daily', options: ['hourly','daily','weekly'], hint: 'How often backups run' },
      { label: 'Retain Backups (days)',      type: 'number', value: 30,      hint: 'Delete backups older than this' },
      { label: 'Backup Storage Path',       type: 'text',   value: '/backups', hint: 'Server directory for backups' },
      { label: 'Notify on Failure',         type: 'toggle', value: true,    hint: 'Send email alert if backup fails' },
      { label: 'Compress Backups',          type: 'toggle', value: true,    hint: 'GZIP compression to save space' },
    ],
  },
  {
    id: 'premium',
    icon: '👑',
    label: 'Premium',
    title: 'Premium Plans & Offers',
    subtitle: 'Subscription tiers, featured listing packages and promotional deals',
    color: '#7c3aed',
    lightColor: '#f5f3ff',
    // Premium uses a custom renderer — planCards + extras
    planCards: [
      {
        key: 'basic',
        title: 'Basic Ad Posting Rule',
        subtitle: 'Base configuration for this ad tier',
        accent: '#7c3aed',
        fields: [
          { key: 'price',     label: 'Price (KES)',  value: 500  },
          { key: 'maxImages', label: 'Max Images',   value: 2    },
          { key: 'days',      label: 'Days',         value: 14   },
        ],
      },
      {
        key: 'pro',
        title: 'Pro Ad Posting Rule',
        subtitle: 'Professional level ad configuration',
        accent: '#7c3aed',
        fields: [
          { key: 'price',     label: 'Price (KES)',  value: 1500 },
          { key: 'maxImages', label: 'Max Images',   value: 5    },
          { key: 'days',      label: 'Days',         value: 30   },
        ],
      },
      {
        key: 'featuredBasic',
        title: 'Featured Listing Fee (Basic)',
        subtitle: 'Cost to feature a single listing',
        accent: '#7c3aed',
        fields: [
          { key: 'price',     label: 'Price (KES)',  value: 200  },
          { key: 'maxImages', label: 'Max Images',   value: 10   },
          { key: 'days',      label: 'Days',         value: 30   },
        ],
      },
      {
        key: 'featuredPro',
        title: 'Featured Listing Fee (Pro)',
        subtitle: 'Cost to feature a single listing',
        accent: '#7c3aed',
        fields: [
          { key: 'price',     label: 'Price (KES)',  value: 200  },
          { key: 'maxImages', label: 'Max Images',   value: 10   },
          { key: 'days',      label: 'Days',         value: 30   },
        ],
      },
    ],
    extras: [
      { key: 'freeTrial',   label: 'Free Trial Days',    type: 'number', value: 7,    hint: 'Days before billing starts' },
      { key: 'promoCodes',  label: 'Promo Codes Enabled',type: 'toggle', value: true, hint: 'Allow discount code redemption' },
      { key: 'maxPromos',   label: 'Max Active Promos',  type: 'number', value: 10,   hint: 'Simultaneous active promotions' },
    ],
    fields: [], // empty — premium uses planCards + extras instead
  },
  {
    id: 'analytics',
    icon: '📊',
    label: 'Analytics',
    title: 'Analytics & Reporting',
    subtitle: 'Data retention, report schedules and export format configuration',
    color: '#db2777',
    lightColor: '#fdf2f8',
    fields: [
      { label: 'Data Retention (months)',    type: 'number', value: 24,      hint: 'How long raw analytics are kept' },
      { label: 'Report Schedule',            type: 'select', value: 'weekly', options: ['daily','weekly','monthly'], hint: 'Auto-send report frequency' },
      { label: 'Report Recipients',          type: 'text',   value: '',      hint: 'Comma-separated email addresses' },
      { label: 'Export Format',              type: 'select', value: 'csv',   options: ['csv','xlsx','pdf'],          hint: 'Default download format' },
      { label: 'Track User Behaviour',       type: 'toggle', value: true,    hint: 'Collect page-view analytics' },
      { label: 'Anonymise Data',             type: 'toggle', value: false,   hint: 'Strip PII from analytics data' },
    ],
  },
];

// ============================================================================
// MAIN COMPONENT
// ============================================================================
function SuperAdminView() {
  const [activityLogs, setActivityLogs]       = useState([]);
  const [logsLoading, setLogsLoading]         = useState(true);

  const [admins, setAdmins]                   = useState([]);
  const [adminsLoading, setAdminsLoading]     = useState(true);
  const [adminSearchTerm, setAdminSearchTerm] = useState('');

  const [activeConfigTab, setActiveConfigTab] = useState('security');
  const [editingTab, setEditingTab]           = useState(null);
  const [configValues, setConfigValues]       = useState(() =>
    Object.fromEntries(CONFIG_TABS.map(t => [t.id, Object.fromEntries(t.fields.map(f => [f.label, f.value]))]))
  );
  const [draftConfig, setDraftConfig]         = useState(null);
  const [draftPremiumCards, setDraftPremiumCards]   = useState(null);
  const [draftPremiumExtras, setDraftPremiumExtras] = useState(null);

  const premiumTab = CONFIG_TABS.find(t => t.id === 'premium');
  const [premiumCards, setPremiumCards] = useState(() =>
    Object.fromEntries(premiumTab.planCards.map(c => [
      c.key,
      Object.fromEntries(c.fields.map(f => [f.key, f.value])),
    ]))
  );
  const [premiumExtras, setPremiumExtras] = useState(() =>
    Object.fromEntries(premiumTab.extras.map(e => [e.key, e.value]))
  );

  const [showAddModal, setShowAddModal]       = useState(false);
  const [showEditModal, setShowEditModal]     = useState(false);
  const [selectedAdmin, setSelectedAdmin]     = useState(null);
  const [activeDropdown, setActiveDropdown]   = useState(null);

  const [newAdminData, setNewAdminData]       = useState(EMPTY_NEW_ADMIN);
  const [editAdminData, setEditAdminData]     = useState(EMPTY_EDIT_ADMIN);
  const [submitting, setSubmitting]           = useState(false);
  const [error, setError]                     = useState(null);

  // ── Communications Center state ─────────────────────────────────────────────
  const [commTab, setCommTab]                 = useState('welcome');   // 'welcome' | 'broadcast' | 'history' | 'scheduled'
  // Welcome message editor
  const [welcomeMsg, setWelcomeMsg]           = useState(
    "👋 Welcome to I'm Selling! Kenya's fastest-growing marketplace.\n\nBuy and sell anything easily — from electronics to property.\n\n✅ Post your first ad today and reach thousands of buyers!"
  );
  const [welcomeEditing, setWelcomeEditing]   = useState(false);
  const [welcomeDraft, setWelcomeDraft]       = useState('');
  const [welcomeSaving, setWelcomeSaving]     = useState(false);
  // Broadcast message composer
  const [broadcastTarget, setBroadcastTarget] = useState('all');
  const [broadcastMsg, setBroadcastMsg]       = useState('');
  const [broadcastTitle, setBroadcastTitle]   = useState('');
  const [userSearch, setUserSearch]           = useState('');
  const [allUsers, setAllUsers]               = useState([]);
  const [selectedUsers, setSelectedUsers]     = useState([]);
  const [usersLoading, setUsersLoading]       = useState(false);
  const [broadcasting, setBroadcasting]       = useState(false);
  const [broadcastResult, setBroadcastResult] = useState(null);
  // Message history
  const [msgHistory, setMsgHistory]           = useState([]);
  const [historyLoading, setHistoryLoading]   = useState(false);
  const [historySearch, setHistorySearch]     = useState('');
  const [deletingMsgId, setDeletingMsgId]     = useState(null);
  // Scheduled messages list
  const [scheduledMsgs, setScheduledMsgs]     = useState([]);
  const [scheduleForm, setScheduleForm]       = useState({ title: '', message: '', target: 'all', sendAt: '' });
  const [showScheduleForm, setShowScheduleForm] = useState(false);

  // --------------------------------------------------------------------------
  useEffect(() => {
    loadLogs();
    loadAdmins();
    loadHistory(); // ✅ Pre-fetch history so it's ready when user clicks the tab
  }, []);

  useEffect(() => {
    const close = () => setActiveDropdown(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  // --------------------------------------------------------------------------
  const loadLogs = async () => {
    setLogsLoading(true);
    try {
      const logs = await api.fetchActivityLogs();
      setActivityLogs(logs);
    } catch (err) {
      console.warn('⚠️ Logs fetch failed:', err.message);
      setActivityLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  const loadAdmins = async () => {
    setAdminsLoading(true);
    setError(null);
    try {
      const data = await api.fetchAdmins();
      setAdmins(data);
    } catch (err) {
      console.warn('⚠️ Admins fetch failed:', err.message);
      setAdmins([]);
      setError('Could not load admins. Check your connection or permissions.');
    } finally {
      setAdminsLoading(false);
    }
  };

  // --------------------------------------------------------------------------
  const filteredAdmins = admins.filter(a =>
    [a.username, a.email, a.first_name, a.last_name]
      .some(v => v?.toLowerCase().includes(adminSearchTerm.toLowerCase()))
  );

  const formatDate = (val) => {
    if (!val) return 'N/A';
    try {
      return new Date(val).toLocaleString('en-US', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: true,
      });
    } catch { return 'N/A'; }
  };

  const getInitials = (a) => {
    if (a.first_name && a.last_name) return `${a.first_name[0]}${a.last_name[0]}`.toUpperCase();
    if (a.username) return a.username.substring(0, 2).toUpperCase();
    return 'A';
  };

  const getRoleBadge = (a) =>
    a.is_superuser
      ? { label: 'Super Admin', cls: 'super-admin' }
      : { label: 'Admin', cls: 'admin' };

  const getStatusBadge = (a) =>
    a.is_active !== false
      ? { label: 'Active', cls: 'active' }
      : { label: 'Suspended', cls: 'suspended' };

  const getLogBadgeClass = (action = '') => {
    const l = action.toLowerCase();
    if (l.includes('approved') || l.includes('activated')) return 'active';
    if (l.includes('suspended') || l.includes('deleted'))  return 'inactive';
    if (l.includes('created')  || l.includes('added'))     return 'new';
    if (l.includes('updated')  || l.includes('modified'))  return 'updated';
    return 'default';
  };

  // --------------------------------------------------------------------------
  const handleAddAdmin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.createAdmin(newAdminData);
      if (res.success !== false) {
        alert('Admin created successfully!');
        setShowAddModal(false);
        setNewAdminData(EMPTY_NEW_ADMIN);
        loadAdmins();
      } else {
        alert(res.error || 'Failed to create admin');
      }
    } catch (err) {
      console.error('❌ createAdmin:', err);
      alert('Failed to create admin');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (admin) => {
    setSelectedAdmin(admin);
    setEditAdminData({
      first_name:   admin.first_name   || '',
      last_name:    admin.last_name    || '',
      email:        admin.email        || '',
      phone:        admin.phone_number || admin.phone || '',
      username:     admin.username     || '',
      is_superuser: admin.is_superuser || false,
      is_active:    admin.is_active    !== false,
    });
    setShowEditModal(true);
    setActiveDropdown(null);
  };

  const handleEditAdmin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.updateAdmin(selectedAdmin.id, editAdminData);
      if (res.success !== false) {
        alert('Admin updated successfully!');
        setShowEditModal(false);
        setSelectedAdmin(null);
        loadAdmins();
      } else {
        alert(res.error || 'Failed to update admin');
      }
    } catch (err) {
      console.error('❌ updateAdmin:', err);
      alert('Failed to update admin');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (admin) => {
    const action = admin.is_active !== false ? 'suspend' : 'activate';
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} ${admin.username}?`)) return;
    try {
      const res = await api.toggleAdminStatus(admin.id);
      if (res.success !== false) {
        setAdmins(prev => prev.map(a =>
          a.id === admin.id ? { ...a, is_active: !a.is_active } : a
        ));
      } else {
        alert(res.error || `Failed to ${action} admin`);
      }
    } catch (err) {
      console.error('❌ toggleStatus:', err);
      alert(`Failed to ${action} admin`);
    }
    setActiveDropdown(null);
  };

  const handleRevokeAdmin = async (admin) => {
    if (!window.confirm(`Revoke admin rights for ${admin.username}?`)) return;
    try {
      const res = await api.removeAdminRights(admin.id);
      if (res.success !== false) {
        alert('Admin rights revoked.');
        loadAdmins();
      } else {
        alert(res.error || 'Failed to revoke admin rights');
      }
    } catch (err) {
      console.error('❌ revokeAdmin:', err);
      alert('Failed to revoke admin rights');
    }
    setActiveDropdown(null);
  };

  const handleVerifyAdmin = async (admin) => {
    const currentStatus = admin.verification_status || 'unverified';
    const isVerified = currentStatus === 'verified';
    const verdict = isVerified ? 'pending_reverification' : 'verified';
    const label   = isVerified ? 'Mark for re-verification' : 'verify';
    if (!window.confirm(`${label.charAt(0).toUpperCase() + label.slice(1)} ${admin.username}?`)) return;
    try {
      const res = await api.verifyUser(admin.id, verdict);
      if (res.success !== false) {
        alert(`✅ ${admin.username} marked as ${res.verification_status || verdict}.`);
        loadAdmins(); // re-fetch so verification_status reflects DB value
      } else {
        alert(res.error || 'Failed to update verification');
      }
    } catch (err) {
      console.error('❌ verifyAdmin:', err);
      alert('Failed to update verification');
    }
    setActiveDropdown(null);
  };

  const handleDeleteAdmin = async (admin) => {
    if (!window.confirm(`⚠️ Permanently delete ${admin.username}? This cannot be undone.`)) return;
    try {
      const res = await api.deleteUser(admin.id);
      if (res.success !== false) {
        setAdmins(prev => prev.filter(a => a.id !== admin.id));
        alert(`🗑️ ${admin.username} deleted successfully.`);
      } else {
        alert(res.error || 'Failed to delete admin');
      }
    } catch (err) {
      console.error('❌ deleteAdmin:', err);
      alert('Failed to delete admin');
    }
    setActiveDropdown(null);
  };

  // --------------------------------------------------------------------------
  // CONFIG EDIT MODE HANDLERS
  // --------------------------------------------------------------------------
  const handleConfigEdit = (tabId) => {
    // Snapshot current values into draft
    setDraftConfig({ ...configValues });
    setDraftPremiumCards(JSON.parse(JSON.stringify(premiumCards)));
    setDraftPremiumExtras({ ...premiumExtras });
    setEditingTab(tabId);
  };

  const handleConfigSave = async (tab) => {
    // Commit drafts to saved state
    if (tab.id === 'premium') {
      setPremiumCards(draftPremiumCards);
      setPremiumExtras(draftPremiumExtras);
      // TODO: await api.saveSystemConfig('premium', { cards: draftPremiumCards, extras: draftPremiumExtras });
    } else {
      setConfigValues(draftConfig);
      // TODO: await api.saveSystemConfig(tab.id, draftConfig[tab.id]);
    }
    alert(`${tab.title} settings saved!`);
    setEditingTab(null);
    setDraftConfig(null);
    setDraftPremiumCards(null);
    setDraftPremiumExtras(null);
  };

  const handleConfigCancel = () => {
    setEditingTab(null);
    setDraftConfig(null);
    setDraftPremiumCards(null);
    setDraftPremiumExtras(null);
  };

  // ── Communications Center handlers ──────────────────────────────────────────
  const handleWelcomeSave = async () => {
    setWelcomeSaving(true);
    try {
      await fetch(`${API_BASE_URL}/auth/admin/broadcast/`, {
        method: 'POST',
        headers: { 'Authorization': `Token ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: "Welcome Message Update", message: welcomeDraft, target: 'all' }),
      });
      setWelcomeMsg(welcomeDraft);
      setWelcomeEditing(false);
      alert("✅ Welcome message updated! New users will see this when they sign up.");
    } catch {
      setWelcomeMsg(welcomeDraft);
      setWelcomeEditing(false);
      alert("✅ Welcome message saved locally.");
    } finally {
      setWelcomeSaving(false);
    }
  };

  const loadAllUsers = async () => {
    setUsersLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/users/?page_size=200`, {
        headers: { 'Authorization': `Token ${getToken()}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAllUsers(data.results || data || []);
      }
    } catch { setAllUsers([]); }
    finally { setUsersLoading(false); }
  };

  useEffect(() => {
    if (commTab === 'broadcast') loadAllUsers();
    if (commTab === 'history')   loadHistory();
  }, [commTab]);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/admin/broadcast-history/`, {
        headers: { 'Authorization': `Token ${getToken()}` },
      });
      const data = await res.json();
      if (res.ok) {
        setMsgHistory(data.results || []);
      } else {
        console.error('broadcast_history error:', data.error || data);
        setMsgHistory([]);
      }
    } catch (e) {
      console.error('broadcast_history network error:', e);
      setMsgHistory([]);
    } finally { setHistoryLoading(false); }
  };

  const handleDeleteBroadcast = async (msgId, title) => {
    if (!window.confirm(
      `Delete "${title || 'this broadcast'}" from ALL users' I'm Selling inbox?\n\nThis cannot be undone.`
    )) return;
    setDeletingMsgId(msgId);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/admin/broadcast-history/${msgId}/`, {
        method: 'DELETE',
        headers: { 'Authorization': `Token ${getToken()}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMsgHistory(prev => prev.filter(m => m.id !== msgId));
        alert(`✅ Deleted from ${data.deleted} user inbox${data.deleted !== 1 ? 'es' : ''}.`);
      } else {
        alert('Failed to delete. Please try again.');
      }
    } catch {
      alert('Network error. Please try again.');
    } finally {
      setDeletingMsgId(null);
    }
  };

  const toggleUserSelect = (uid) => {
    setSelectedUsers(prev =>
      prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]
    );
  };

  const handleBroadcast = async () => {
    if (!broadcastMsg.trim()) { alert('Please enter a message.'); return; }
    if (broadcastTarget === 'selected' && selectedUsers.length === 0) {
      alert('Please select at least one user.'); return;
    }
    if (!window.confirm(
      broadcastTarget === 'all'
        ? 'Send this message to ALL users?'
        : `Send this message to ${selectedUsers.length} selected user(s)?`
    )) return;

    setBroadcasting(true);
    setBroadcastResult(null);
    try {
      const payload = {
        title:   broadcastTitle || "Message from I'm Selling",
        message: broadcastMsg,
        target:  broadcastTarget,
        ...(broadcastTarget === 'selected' ? { user_ids: selectedUsers } : {}),
      };
      const res = await fetch(`${API_BASE_URL}/auth/admin/broadcast/`, {
        method: 'POST',
        headers: { 'Authorization': `Token ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        setBroadcastResult({ success: true, sent: data.sent, failed: data.failed || 0 });
        setBroadcastMsg(''); setBroadcastTitle(''); setSelectedUsers([]);
        loadHistory();
      } else {
        setBroadcastResult({ success: false, error: data.error || 'Send failed.' });
      }
    } catch (e) {
      setBroadcastResult({ success: false, error: 'Network error. Check connection.' });
    } finally {
      setBroadcasting(false);
    }
  };

  const handleAddScheduled = () => {
    if (!scheduleForm.message.trim() || !scheduleForm.sendAt) {
      alert('Please fill in message and send date/time.'); return;
    }
    setScheduledMsgs(prev => [
      ...prev,
      { id: Date.now(), ...scheduleForm, status: 'scheduled', createdAt: new Date().toISOString() }
    ]);
    setScheduleForm({ title: '', message: '', target: 'all', sendAt: '' });
    setShowScheduleForm(false);
  };

  const handleDeleteScheduled = (id) => {
    if (!window.confirm('Delete this scheduled message?')) return;
    setScheduledMsgs(prev => prev.filter(m => m.id !== id));
  };

  // --------------------------------------------------------------------------
  return (
    <div className="super-admin-view">

      {/* ── System Configuration ─────────────────────────────────────────── */}
      <div className="system-settings-section">
        <div className="section-header">
          <h2>System Configuration</h2>
          <span className="config-badge">6 modules</span>
        </div>

        {/* ── Tab Nav Bar ── */}
        <div className="config-tab-bar">
          {CONFIG_TABS.map(tab => (
            <button
              key={tab.id}
              className={`config-tab-btn ${activeConfigTab === tab.id ? 'active' : ''}`}
              style={activeConfigTab === tab.id ? { '--tab-color': tab.color, '--tab-light': tab.lightColor } : {}}
              onClick={() => setActiveConfigTab(tab.id)}
            >
              <span className="config-tab-icon">{tab.icon}</span>
              <span className="config-tab-label">{tab.label}</span>
              {activeConfigTab === tab.id && (
                <span className="config-tab-indicator" style={{ background: tab.color }} />
              )}
            </button>
          ))}
        </div>

        {/* ── Tab Panel ── */}
        {CONFIG_TABS.map(tab => {
          if (tab.id !== activeConfigTab) return null;
          const isEditing = editingTab === tab.id;

          // Shared panel header with Edit / Editing / Save / Cancel buttons
          const PanelHeader = () => (
            <div className="config-panel-header" style={{ borderLeftColor: tab.color }}>
              <div className="config-panel-title-row">
                <div className="config-panel-icon" style={{ background: tab.lightColor, color: tab.color }}>
                  {tab.icon}
                </div>
                <div>
                  <h3 className="config-panel-title">{tab.title}</h3>
                  <p className="config-panel-subtitle">{tab.subtitle}</p>
                </div>
              </div>
              <div className="config-header-actions">
                {!isEditing ? (
                  /* ── View mode: only Edit button ── */
                  <button
                    className="config-edit-btn"
                    style={{ '--edit-color': tab.color }}
                    onClick={() => handleConfigEdit(tab.id)}
                  >
                    ✏️ Edit
                  </button>
                ) : (
                  /* ── Edit mode: Cancel + Save ── */
                  <>
                    <button
                      className="config-cancel-btn"
                      onClick={handleConfigCancel}
                    >
                      Cancel
                    </button>
                    <button
                      className="config-save-btn"
                      style={{ background: tab.color }}
                      onClick={() => handleConfigSave(tab)}
                    >
                      Save Changes
                    </button>
                  </>
                )}
              </div>
            </div>
          );

          // ── PREMIUM custom renderer ──────────────────────────────────────
          if (tab.id === 'premium') {
            const cards  = isEditing ? draftPremiumCards  : premiumCards;
            const extras = isEditing ? draftPremiumExtras : premiumExtras;

            return (
              <div key="premium" className={`config-panel ${isEditing ? 'editing' : ''}`}>
                <PanelHeader />

                {/* Edit mode banner */}
                {isEditing && (
                  <div className="config-editing-banner" style={{ background: `${tab.color}12`, borderColor: `${tab.color}40` }}>
                    <span style={{ color: tab.color }}>✏️</span>
                    <span>You are now editing — make your changes and click <strong>Save Changes</strong> to apply.</span>
                  </div>
                )}

                {/* Plan Cards — 2-column grid */}
                <div className="premium-plan-grid">
                  {tab.planCards.map(card => (
                    <div key={card.key} className={`premium-plan-card ${isEditing ? 'editable' : ''}`}>
                      <div className="premium-plan-card-header">
                        <div>
                          <p className="premium-plan-card-title">{card.title}</p>
                          <p className="premium-plan-card-sub">{card.subtitle}</p>
                        </div>
                      </div>
                      <div className="premium-plan-fields">
                        {card.fields.map(f => (
                          <div key={f.key} className="premium-plan-field">
                            <label className="premium-plan-field-label">{f.label}</label>
                            <input
                              className="premium-plan-input"
                              type="number"
                              value={cards[card.key][f.key]}
                              readOnly={!isEditing}
                              style={isEditing ? { '--focus-color': tab.color } : {}}
                              onChange={e =>
                                isEditing && setDraftPremiumCards(prev => ({
                                  ...prev,
                                  [card.key]: { ...prev[card.key], [f.key]: e.target.value },
                                }))
                              }
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Extras row — 3 columns */}
                <div className="premium-extras-grid">
                  {tab.extras.map(extra => (
                    <div key={extra.key} className={`config-field ${isEditing ? 'editable' : ''}`}>
                      <label className="config-field-label">{extra.label}</label>
                      <p className="config-field-hint">{extra.hint}</p>

                      {extra.type === 'toggle' && (
                        <button
                          className={`config-toggle ${extras[extra.key] ? 'on' : 'off'} ${!isEditing ? 'locked' : ''}`}
                          style={extras[extra.key] ? { '--toggle-color': tab.color } : {}}
                          onClick={() =>
                            isEditing && setDraftPremiumExtras(prev => ({ ...prev, [extra.key]: !prev[extra.key] }))
                          }
                        >
                          <span className="config-toggle-thumb" />
                          <span className="config-toggle-text">
                            {extras[extra.key] ? 'Enabled' : 'Disabled'}
                          </span>
                        </button>
                      )}

                      {extra.type === 'number' && (
                        <input
                          className="config-input"
                          type="number"
                          value={extras[extra.key]}
                          readOnly={!isEditing}
                          style={isEditing ? { '--focus-color': tab.color } : {}}
                          onChange={e =>
                            isEditing && setDraftPremiumExtras(prev => ({ ...prev, [extra.key]: e.target.value }))
                          }
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          }

          // ── DEFAULT renderer (all other tabs) ────────────────────────────
          const values = isEditing ? draftConfig : configValues;

          return (
            <div key={tab.id} className={`config-panel ${isEditing ? 'editing' : ''}`}>
              <PanelHeader />

              {/* Edit mode banner */}
              {isEditing && (
                <div className="config-editing-banner" style={{ background: `${tab.color}12`, borderColor: `${tab.color}40` }}>
                  <span style={{ color: tab.color }}>✏️</span>
                  <span>You are now editing — make your changes and click <strong>Save Changes</strong> to apply.</span>
                </div>
              )}

              {/* Fields grid */}
              <div className="config-fields-grid">
                {tab.fields.map(field => (
                  <div key={field.label} className={`config-field ${isEditing ? 'editable' : ''}`}>
                    <label className="config-field-label">{field.label}</label>
                    <p className="config-field-hint">{field.hint}</p>

                    {field.type === 'toggle' && (
                      <button
                        className={`config-toggle ${values[tab.id][field.label] ? 'on' : 'off'} ${!isEditing ? 'locked' : ''}`}
                        style={values[tab.id][field.label] ? { '--toggle-color': tab.color } : {}}
                        onClick={() =>
                          isEditing && setDraftConfig(prev => ({
                            ...prev,
                            [tab.id]: { ...prev[tab.id], [field.label]: !prev[tab.id][field.label] },
                          }))
                        }
                      >
                        <span className="config-toggle-thumb" />
                        <span className="config-toggle-text">
                          {values[tab.id][field.label] ? 'Enabled' : 'Disabled'}
                        </span>
                      </button>
                    )}

                    {field.type === 'select' && (
                      <select
                        className="config-input"
                        value={values[tab.id][field.label]}
                        disabled={!isEditing}
                        style={isEditing ? { '--focus-color': tab.color } : {}}
                        onChange={e =>
                          isEditing && setDraftConfig(prev => ({
                            ...prev,
                            [tab.id]: { ...prev[tab.id], [field.label]: e.target.value },
                          }))
                        }
                      >
                        {field.options.map(o => (
                          <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>
                        ))}
                      </select>
                    )}

                    {(field.type === 'text' || field.type === 'number' || field.type === 'password') && (
                      <input
                        className="config-input"
                        type={field.type}
                        value={values[tab.id][field.label]}
                        readOnly={!isEditing}
                        placeholder={field.type === 'password' ? '••••••••••••' : ''}
                        style={isEditing ? { '--focus-color': tab.color } : {}}
                        onChange={e =>
                          isEditing && setDraftConfig(prev => ({
                            ...prev,
                            [tab.id]: { ...prev[tab.id], [field.label]: e.target.value },
                          }))
                        }
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Media & Storage Graph ────────────────────────────────────────── */}
      <MediaStorageGraph />

      {/* ── Activity Logs ────────────────────────────────────────────────── */}
      <div className="activity-logs-section">
        <div className="section-header">
          <div className="logs-header-left">
            <h2>Admin Activity Logs</h2>
            {!logsLoading && activityLogs.length > 5 && (
              <span className="logs-count-badge">{activityLogs.length} entries</span>
            )}
          </div>
          <button className="btn-secondary" onClick={() => alert('Export coming soon!')}>
            ⬇️ Export Logs
          </button>
        </div>

        {logsLoading ? (
          <div className="loading-container">
            <div className="spinner" />
            <p>Loading activity logs…</p>
          </div>
        ) : (
          <>
            <div className="logs-table-wrapper">
              {/* Sticky header — sits outside scroll zone */}
              <table className="logs-table logs-table-head">
                <thead>
                  <tr>
                    <th style={{ width: '25%' }}>ADMIN</th>
                    <th style={{ width: '18%' }}>ACTION</th>
                    <th style={{ width: '25%' }}>RESOURCE</th>
                    <th style={{ width: '12%' }}>IP ADDRESS</th>
                    <th style={{ width: '20%' }}>TIMESTAMP</th>
                  </tr>
                </thead>
              </table>

              {/* Scrollable body — ~5 rows visible */}
              <div className="logs-scroll-body">
                <table className="logs-table logs-table-body">
                  <colgroup>
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '20%' }} />
                  </colgroup>
                  <tbody>
                    {activityLogs.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="empty-cell">
                          <div className="empty-state">
                            <span style={{ fontSize: 40 }}>📋</span>
                            <h3>No activity logs</h3>
                            <p>Admin actions will appear here</p>
                          </div>
                        </td>
                      </tr>
                    ) : activityLogs.map((log, i) => (
                      <tr key={log.id ?? i}>
                        <td>
                          <div className="admin-cell">
                            <div className="admin-avatar">
                              {(log.admin_email || log.user || 'A').charAt(0).toUpperCase()}
                            </div>
                            <div className="admin-info">
                              <div className="admin-email">
                                {log.admin_email || log.user || 'Unknown'}
                              </div>
                              <div className="admin-role">{log.admin_role || 'Admin'}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={`status-badge ${getLogBadgeClass(log.action || log.type)}`}>
                            {log.action || log.title || '—'}
                          </span>
                        </td>
                        <td className="resource-cell">
                          {log.resource_type && (
                            <span className="resource-type">{log.resource_type}</span>
                          )}
                          {log.resource_name || log.description || log.resource_id || 'N/A'}
                        </td>
                        <td className="ip-cell">{log.ip_address || 'N/A'}</td>
                        <td className="timestamp-cell">
                          {formatDate(log.created_at || log.timestamp)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {activityLogs.length > 5 && (
              <p className="logs-scroll-hint">
                ↕ Scroll to see all {activityLogs.length} entries
              </p>
            )}
          </>
        )}
      </div>

      {/* ════════════════════════════════════════════════════════════════════
          TRANSACTION LEDGER
          ════════════════════════════════════════════════════════════════════ */}
      <div style={{
        background: '#fff', borderRadius: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        marginBottom: 28, padding: '24px 28px', border: '1px solid #e2e8f0',
      }}>
        <TransactionsTable />
      </div>

      {/* ════════════════════════════════════════════════════════════════════
          COMMUNICATIONS CENTER — redesigned with UserCommunications style
          ════════════════════════════════════════════════════════════════════ */}
      <div style={{
        background: '#fff', borderRadius: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        marginBottom: 28, overflow: 'hidden', border: '1px solid #e2e8f0',
      }}>
        {/* ── Header ── */}
        <div style={{
          padding: '20px 28px 0', borderBottom: '1px solid #f1f5f9',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>📡 Communications Center</h2>
            <span style={{ background: '#eef2ff', color: '#6366f1', borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 700 }}>4 tools</span>
          </div>
          <p style={{ margin: '0 0 16px', fontSize: 12, color: '#94a3b8' }}>
            Manage welcome messages · Broadcast to users via I'm Selling inbox · View & delete sent messages
          </p>
          {/* Stats row */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            {[
              { label: 'Total Sent',  value: msgHistory.length,                          color: '#6366f1' },
              { label: 'Users',       value: allUsers.length || '—',                     color: '#0ea5e9' },
              { label: 'Scheduled',   value: scheduledMsgs.length,                       color: '#f59e0b' },
              { label: 'History',     value: msgHistory.reduce((s,m) => s + (m.sent_count||0), 0) || 0, color: '#10b981' },
            ].map(s => (
              <div key={s.label} style={{
                flex: 1, background: '#f8fafc', borderRadius: 8, padding: '8px 10px',
                textAlign: 'center', borderTop: `3px solid ${s.color}`,
              }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
          {/* Tab bar */}
          <div style={{ display: 'flex', gap: 4 }}>
            {[
              { id: 'welcome',   icon: '👋', label: 'Welcome Message' },
              { id: 'broadcast', icon: '📣', label: 'Broadcast' },
              { id: 'history',   icon: '📬', label: `History ${msgHistory.length > 0 ? `(${msgHistory.length})` : ''}` },
              { id: 'scheduled', icon: '🕐', label: `Scheduled ${scheduledMsgs.length > 0 ? `(${scheduledMsgs.length})` : ''}` },
            ].map(t => (
              <button key={t.id} onClick={() => setCommTab(t.id)} style={{
                padding: '10px 18px', border: 'none', borderRadius: '8px 8px 0 0',
                background: commTab === t.id ? '#fff' : 'transparent',
                color: commTab === t.id ? '#6366f1' : '#64748b',
                fontWeight: commTab === t.id ? 700 : 500,
                fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                borderBottom: commTab === t.id ? '2px solid #6366f1' : '2px solid transparent',
                transition: 'all 0.15s',
              }}>
                <span>{t.icon}</span><span>{t.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ─────────── WELCOME MESSAGE tab ─────────── */}
        {commTab === 'welcome' && (
          <div style={{ padding: 24 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginBottom: 4 }}>👋 Welcome Message</div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>Sent to every new user when they sign up — appears in their I'm Selling inbox</div>
              </div>
              {!welcomeEditing ? (
                <button onClick={() => { setWelcomeDraft(welcomeMsg); setWelcomeEditing(true); }} style={{
                  padding: '8px 18px', background: '#eef2ff', color: '#6366f1', border: '1.5px solid #c7d2fe',
                  borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                }}>✏️ Edit</button>
              ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setWelcomeEditing(false)} style={{ padding: '8px 16px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                  <button onClick={handleWelcomeSave} disabled={welcomeSaving} style={{ padding: '8px 18px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: welcomeSaving ? 0.6 : 1 }}>
                    {welcomeSaving ? 'Saving…' : '💾 Save'}
                  </button>
                </div>
              )}
            </div>
            {!welcomeEditing ? (
              <div style={{ background: '#f1f5f9', borderRadius: 12, padding: '16px 18px', maxWidth: 560 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>🏪</div>
                  <span style={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>I'm Selling</span>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>Official</span>
                </div>
                <div style={{ fontSize: 13.5, color: '#334155', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{welcomeMsg}</div>
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 8, padding: '9px 13px', marginBottom: 12, fontSize: 12, color: '#4338ca' }}>
                  <span>✏️</span><span>Edit the welcome message — plain text and emoji supported</span>
                </div>
                <textarea value={welcomeDraft} onChange={e => setWelcomeDraft(e.target.value)} rows={8} style={{
                  width: '100%', padding: '11px 13px', border: '1.5px solid #c7d2fe', borderRadius: 8,
                  fontSize: 13.5, fontFamily: 'inherit', color: '#1e293b', background: '#f8fafc',
                  resize: 'vertical', outline: 'none', boxSizing: 'border-box',
                }} />
                <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'right', marginTop: 4 }}>{welcomeDraft.length} characters</div>
              </div>
            )}
          </div>
        )}

        {/* ─────────── BROADCAST tab ─────────── */}
        {commTab === 'broadcast' && (
          <div style={{ padding: 24 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginBottom: 4 }}>📣 Broadcast Message</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 20 }}>Send a direct message via I'm Selling inbox to all users or selected users</div>

            {broadcastResult && (
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '11px 16px', borderRadius: 8, marginBottom: 18,
                background: broadcastResult.success ? '#dcfce7' : '#fee2e2',
                color: broadcastResult.success ? '#15803d' : '#dc2626',
                border: `1px solid ${broadcastResult.success ? '#86efac' : '#fca5a5'}`,
                fontSize: 13.5, fontWeight: 600,
              }}>
                <span>{broadcastResult.success
                  ? `✅ Sent to ${broadcastResult.sent} user${broadcastResult.sent !== 1 ? 's' : ''}${broadcastResult.failed > 0 ? ` (${broadcastResult.failed} failed)` : ''}!`
                  : `❌ ${broadcastResult.error}`}
                </span>
                <button onClick={() => setBroadcastResult(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'inherit', lineHeight: 1 }}>×</button>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
                  Message Title <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: 12 }}>(optional)</span>
                </label>
                <input value={broadcastTitle} onChange={e => setBroadcastTitle(e.target.value)}
                  placeholder="e.g. New Feature Alert 🎉"
                  style={{ width: '100%', height: 38, padding: '0 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13.5, background: '#f8fafc', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Message *</label>
                <textarea value={broadcastMsg} onChange={e => setBroadcastMsg(e.target.value)}
                  rows={5} placeholder="Type your message to users here…"
                  style={{ width: '100%', padding: '11px 13px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13.5, fontFamily: 'inherit', color: '#1e293b', background: '#f8fafc', resize: 'vertical', outline: 'none', boxSizing: 'border-box' }} />
                <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'right', marginTop: 3 }}>{broadcastMsg.length} characters</div>
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 8 }}>Send To</label>
                <div style={{ display: 'flex', gap: 10 }}>
                  {[{ v: 'all', label: `🌍 All Users`, count: allUsers.length }, { v: 'selected', label: '👤 Selected Users', count: selectedUsers.length }].map(opt => (
                    <button key={opt.v} onClick={() => setBroadcastTarget(opt.v)} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '9px 18px',
                      border: `2px solid ${broadcastTarget === opt.v ? '#6366f1' : '#e2e8f0'}`,
                      borderRadius: 9, background: broadcastTarget === opt.v ? '#eef2ff' : '#fff',
                      color: broadcastTarget === opt.v ? '#6366f1' : '#475569',
                      fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
                    }}>
                      {opt.label}
                      {opt.count > 0 && (
                        <span style={{ background: broadcastTarget === opt.v ? '#6366f1' : '#e2e8f0', color: broadcastTarget === opt.v ? '#fff' : '#475569', borderRadius: 12, padding: '1px 8px', fontSize: 11, fontWeight: 700 }}>{opt.count}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {broadcastTarget === 'selected' && (
                <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                  <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
                    <input value={userSearch} onChange={e => setUserSearch(e.target.value)}
                      placeholder="🔍 Search users by email or name…"
                      style={{ width: '100%', height: 34, padding: '0 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, background: '#fff', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                  </div>
                  {selectedUsers.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>
                      {selectedUsers.map(uid => {
                        const u = allUsers.find(x => x.id === uid);
                        return u ? (
                          <span key={uid} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#eef2ff', color: '#6366f1', border: '1px solid #c7d2fe', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>
                            {u.email || u.username}
                            <button onClick={() => toggleUserSelect(uid)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}
                  <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                    {usersLoading ? (
                      <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Loading users…</div>
                    ) : allUsers.filter(u => {
                      if (!userSearch) return true;
                      const q = userSearch.toLowerCase();
                      return (u.email||'').toLowerCase().includes(q) || (u.username||'').toLowerCase().includes(q) || (u.first_name||'').toLowerCase().includes(q);
                    }).slice(0, 30).map(u => (
                      <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 13px', cursor: 'pointer', borderBottom: '1px solid #f8fafc', background: selectedUsers.includes(u.id) ? '#f0f4ff' : 'white', transition: 'background 0.12s' }}>
                        <input type="checkbox" checked={selectedUsers.includes(u.id)} onChange={() => toggleUserSelect(u.id)} style={{ accentColor: '#6366f1' }} />
                        <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#6366f1', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                          {(u.email || u.username || '?')[0].toUpperCase()}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{u.first_name ? `${u.first_name} ${u.last_name||''}`.trim() : u.username}</div>
                          <div style={{ fontSize: 11, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <button onClick={handleBroadcast} disabled={broadcasting || !broadcastMsg.trim()} style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 28px',
                background: broadcasting || !broadcastMsg.trim() ? '#e2e8f0' : '#6366f1',
                color: broadcasting || !broadcastMsg.trim() ? '#94a3b8' : '#fff',
                border: 'none', borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: broadcasting || !broadcastMsg.trim() ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s', alignSelf: 'flex-start',
              }}>
                {broadcasting ? '⏳ Sending…' : broadcastTarget === 'all' ? '📣 Send to All Users' : `📣 Send to ${selectedUsers.length} User${selectedUsers.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        )}

        {/* ─────────── HISTORY tab ─────────── */}
        {commTab === 'history' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 12px' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginBottom: 4 }}>📬 Message History</div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>All admin broadcasts — delete removes from every user's I'm Selling inbox</div>
              </div>
              <button onClick={loadHistory} style={{ padding: '7px 16px', background: '#f5f3ff', color: '#7c3aed', border: '1.5px solid #ddd6fe', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>🔄 Refresh</button>
            </div>
            <div style={{ padding: '0 24px 14px' }}>
              <input value={historySearch} onChange={e => setHistorySearch(e.target.value)}
                placeholder="🔍 Search by title or content…"
                style={{ width: '100%', height: 38, padding: '0 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#f8fafc', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
            </div>
            {historyLoading ? (
              <div style={{ padding: '48px 24px', textAlign: 'center', color: '#94a3b8' }}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>⏳</div>
                <div style={{ fontSize: 13 }}>Loading message history…</div>
              </div>
            ) : msgHistory.length === 0 ? (
              <div style={{ padding: '52px 24px', textAlign: 'center', color: '#94a3b8' }}>
                <div style={{ fontSize: 44, marginBottom: 12 }}>📭</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#475569', marginBottom: 6 }}>No broadcast messages sent yet</div>
                <div style={{ fontSize: 13 }}>Messages you broadcast will appear here with delete controls.</div>
              </div>
            ) : (
              <div style={{ borderTop: '1px solid #f1f5f9' }}>
                {msgHistory.filter(m => {
                  if (!historySearch) return true;
                  const q = historySearch.toLowerCase();
                  return (m.title||'').toLowerCase().includes(q) || (m.body||'').toLowerCase().includes(q);
                }).map(m => (
                  <div key={m.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '16px 24px', borderBottom: '1px solid #f1f5f9', transition: 'background 0.12s' }}
                    onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background='white'}>
                    <div style={{ width: 42, height: 42, borderRadius: 10, background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>📣</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>{m.title || '(No title)'}</div>
                      <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.55, marginBottom: 7, whiteSpace: 'pre-wrap' }}>{(m.body||'').slice(0, 140)}{(m.body||'').length > 140 ? '…' : ''}</div>
                      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 11.5, color: '#64748b', fontWeight: 500 }}>
                        <span>📅 {m.created_at ? new Date(m.created_at).toLocaleString('en-KE',{day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : '—'}</span>
                        <span style={{ color: '#6366f1', fontWeight: 700 }}>👥 {m.sent_count} recipient{m.sent_count !== 1 ? 's' : ''}</span>
                        {m.recipients_preview?.length > 0 && (
                          <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>
                            {m.recipients_preview.slice(0,2).join(', ')}{m.sent_count > 2 ? ` +${m.sent_count-2} more` : ''}
                          </span>
                        )}
                      </div>
                    </div>
                    <button disabled={deletingMsgId === m.id} onClick={() => handleDeleteBroadcast(m.id, m.title)}
                      title="Delete from all inboxes"
                      style={{ background: 'none', border: 'none', cursor: deletingMsgId === m.id ? 'not-allowed' : 'pointer', fontSize: 18, padding: '6px 8px', borderRadius: 6, transition: 'background 0.12s', flexShrink: 0, opacity: deletingMsgId === m.id ? 0.5 : 1 }}
                      onMouseEnter={e => e.currentTarget.style.background='#fee2e2'}
                      onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                      {deletingMsgId === m.id ? '⏳' : '🗑️'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ─────────── SCHEDULED tab ─────────── */}
        {commTab === 'scheduled' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 16px', borderBottom: '1px solid #f1f5f9' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginBottom: 4 }}>🕐 Scheduled Messages</div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>Queue messages to send automatically at a future date and time</div>
              </div>
              <button onClick={() => setShowScheduleForm(s => !s)} style={{ padding: '8px 16px', background: '#fffbeb', color: '#d97706', border: '1.5px solid #fde68a', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                {showScheduleForm ? '✕ Cancel' : '+ Schedule Message'}
              </button>
            </div>

            {showScheduleForm && (
              <div style={{ padding: '18px 24px', borderBottom: '1px solid #f1f5f9', background: '#fffbeb' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 18px', marginBottom: 14 }}>
                  {[
                    { label: 'Title (optional)', key: 'title', type: 'text', placeholder: 'e.g. Weekly Tip', col: 1 },
                    { label: 'Send To', key: 'target', type: 'select', col: 1 },
                    { label: 'Send At *', key: 'sendAt', type: 'datetime-local', col: 1 },
                  ].map(f => (
                    <div key={f.key} style={{ gridColumn: f.col === 'all' ? '1/-1' : undefined }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>{f.label}</label>
                      {f.type === 'select' ? (
                        <select value={scheduleForm.target} onChange={e => setScheduleForm(p => ({...p, target: e.target.value}))}
                          style={{ width: '100%', height: 36, padding: '0 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, background: '#fff', fontFamily: 'inherit' }}>
                          <option value="all">All Users</option>
                          <option value="new">New Users (last 7 days)</option>
                          <option value="inactive">Inactive Users (30+ days)</option>
                        </select>
                      ) : (
                        <input type={f.type} value={scheduleForm[f.key]} placeholder={f.placeholder||''}
                          min={f.type==='datetime-local' ? new Date().toISOString().slice(0,16) : undefined}
                          onChange={e => setScheduleForm(p => ({...p, [f.key]: e.target.value}))}
                          style={{ width: '100%', height: 36, padding: '0 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, background: '#fff', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                      )}
                    </div>
                  ))}
                  <div style={{ gridColumn: '1/-1' }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>Message *</label>
                    <textarea value={scheduleForm.message} onChange={e => setScheduleForm(p => ({...p, message: e.target.value}))}
                      rows={3} placeholder="Message content…"
                      style={{ width: '100%', padding: '9px 11px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                </div>
                <button onClick={handleAddScheduled} style={{ padding: '9px 22px', background: '#d97706', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>🕐 Add to Schedule</button>
              </div>
            )}

            {scheduledMsgs.length === 0 ? (
              <div style={{ padding: '52px 24px', textAlign: 'center', color: '#94a3b8' }}>
                <div style={{ fontSize: 44, marginBottom: 12 }}>🕐</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#475569', marginBottom: 6 }}>No scheduled messages</div>
                <div style={{ fontSize: 13 }}>Click "+ Schedule Message" to queue one.</div>
              </div>
            ) : (
              scheduledMsgs.map(m => (
                <div key={m.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '15px 24px', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: '#fffbeb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>🕐</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 3 }}>{m.title||'(No title)'}</div>
                    <div style={{ fontSize: 13, color: '#475569', marginBottom: 6 }}>{m.message.slice(0,80)}{m.message.length>80?'…':''}</div>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 11.5, color: '#64748b', fontWeight: 500 }}>
                      <span>📅 {new Date(m.sendAt).toLocaleString('en-KE',{day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'})}</span>
                      <span>👥 {m.target==='all'?'All Users':m.target==='new'?'New Users':'Inactive Users'}</span>
                      <span style={{ background: '#fef3c7', color: '#b45309', borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>{m.status}</span>
                    </div>
                  </div>
                  <button onClick={() => handleDeleteScheduled(m.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: '6px 8px', borderRadius: 6 }}
                    onMouseEnter={e => e.currentTarget.style.background='#fee2e2'}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}>🗑️</button>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* ── Admin Management      </div>

      {/* ── Admin Management ─────────────────────────────────────────────── */}
      <div className="admin-management-section">
        <div className="section-header">
          <div className="admins-header-left">
            <h2>Admin Management</h2>
            {!adminsLoading && filteredAdmins.length > 5 && (
              <span className="admins-count-badge">{filteredAdmins.length} admins</span>
            )}
          </div>
          <div className="header-actions">
            <div className="search-box-compact">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                placeholder="Search admins…"
                value={adminSearchTerm}
                onChange={e => setAdminSearchTerm(e.target.value)}
              />
            </div>
            <button className="btn-secondary" onClick={() => setShowAddModal(true)}>
              + Add Admin
            </button>
          </div>
        </div>

        {error && (
          <div style={{
            padding: '12px 16px', marginBottom: 16,
            background: '#fff5f5', border: '1px solid #feb2b2',
            borderRadius: 8, color: '#c53030', fontSize: 14,
          }}>
            ⚠️ {error}
            <button
              onClick={loadAdmins}
              style={{
                marginLeft: 12, color: '#3182ce', background: 'none',
                border: 'none', cursor: 'pointer', textDecoration: 'underline',
              }}
            >
              Retry
            </button>
          </div>
        )}

        {adminsLoading ? (
          <div className="loading-container">
            <div className="spinner" />
            <p>Loading admins…</p>
          </div>
        ) : (
          <>
            <div className="admins-table-wrapper">
              {/* Sticky header — sits outside scroll zone */}
              <table className="admins-table admins-table-head">
                <thead>
                  <tr>
                    <th style={{ width: '20%' }}>ADMIN</th>
                    <th style={{ width: '18%' }}>EMAIL</th>
                    <th style={{ width: '11%' }}>PHONE</th>
                    <th style={{ width: '10%' }}>ROLE</th>
                    <th style={{ width: '9%' }}>STATUS</th>
                    <th style={{ width: '12%' }}>VERIFICATION</th>
                    <th style={{ width: '12%' }}>JOINED</th>
                    <th style={{ width: '8%' }} className="th-actions">ACTIONS</th>
                  </tr>
                </thead>
              </table>

              {/* Scrollable body — ~5 rows visible */}
              <div className="admins-scroll-body">
                <table className="admins-table admins-table-body">
                  <colgroup>
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '11%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '9%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '8%' }} />
                  </colgroup>
                  <tbody>
                    {filteredAdmins.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="empty-cell">
                          <div className="empty-state">
                            <span style={{ fontSize: 40 }}>👥</span>
                            <h3>No admins found</h3>
                            <p>
                              {adminSearchTerm
                                ? 'Try adjusting your search'
                                : 'Add your first admin above'}
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : filteredAdmins.map(admin => {
                      const role   = getRoleBadge(admin);
                      const status = getStatusBadge(admin);
                      return (
                        <tr key={admin.id}>
                          <td>
                            <div className="admin-cell">
                              <div className="admin-avatar">{getInitials(admin)}</div>
                              <div className="admin-info">
                                <div className="admin-name">
                                  {[admin.first_name, admin.last_name].filter(Boolean).join(' ') || admin.username}
                                </div>
                                <div className="admin-username">@{admin.username}</div>
                              </div>
                            </div>
                          </td>
                          <td className="email-cell">{admin.email || 'N/A'}</td>
                          <td className="phone-cell">{admin.phone_number || admin.phone || 'N/A'}</td>
                          <td>
                            <span className={`role-badge role-${role.cls}`}>{role.label}</span>
                          </td>
                          <td>
                            <span className={`status-badge status-${status.cls}`}>
                              ● {status.label}
                            </span>
                          </td>
                          <td>
                            {(() => {
                              const vs = admin.verification_status || 'unverified';
                              const map = {
                                verified:              { label: '✅ Verified',    bg: '#d1fae5', color: '#065f46' },
                                pending:               { label: '⏳ Pending',     bg: '#fef3c7', color: '#b45309' },
                                pending_reverification:{ label: '🔄 Re-verify',  bg: '#fff7ed', color: '#c2410c' },
                                unverified:            { label: '✗ Unverified',  bg: '#fee2e2', color: '#dc2626' },
                                partial:               { label: '◑ Partial',     bg: '#eff6ff', color: '#1d4ed8' },
                                rejected:              { label: '❌ Rejected',   bg: '#fee2e2', color: '#dc2626' },
                              };
                              const badge = map[vs] || map.unverified;
                              return (
                                <span style={{
                                  fontSize: 11, fontWeight: 700, padding: '3px 10px',
                                  borderRadius: 20, background: badge.bg, color: badge.color,
                                  whiteSpace: 'nowrap',
                                }}>
                                  {badge.label}
                                </span>
                              );
                            })()}
                          </td>
                          <td className="date-cell">
                            {formatDate(admin.date_joined || admin.created_at)}
                          </td>
                          <td>
                            <div className="actions-cell-dropdown">
                              <button
                                className="action-menu-btn"
                                onClick={e => {
                                  e.stopPropagation();
                                  setActiveDropdown(activeDropdown === admin.id ? null : admin.id);
                                }}
                              >
                                ⋮
                              </button>
                              {activeDropdown === admin.id && (
                                <div className="action-dropdown-menu">
                                  <button onClick={() => openEditModal(admin)}>✏️ Edit</button>
                                  <button onClick={() => handleVerifyAdmin(admin)}>
                                    {admin.verification_status === 'verified' ? '🔄 Re-verify' : '🪪 Verify User'}
                                  </button>
                                  <button onClick={() => handleToggleStatus(admin)}>
                                    {admin.is_active !== false ? '🔒 Suspend' : '✅ Activate'}
                                  </button>
                                  <button
                                    onClick={() => handleRevokeAdmin(admin)}
                                  >
                                    🚫 Revoke Admin
                                  </button>
                                  <button
                                    className="danger"
                                    onClick={() => handleDeleteAdmin(admin)}
                                  >
                                    🗑️ Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {filteredAdmins.length > 5 && (
              <p className="admins-scroll-hint">
                ↕ Scroll to see all {filteredAdmins.length} admins
              </p>
            )}
          </>
        )}
      </div>

      {/* ── Add Admin Modal ──────────────────────────────────────────────── */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Admin</h2>
              <button className="modal-close-btn" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <form className="modal-form" onSubmit={handleAddAdmin}>
              <div className="form-row-two">
                <div className="form-field">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={newAdminData.first_name}
                    onChange={e => setNewAdminData({ ...newAdminData, first_name: e.target.value })}
                    placeholder="John"
                  />
                </div>
                <div className="form-field">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={newAdminData.last_name}
                    onChange={e => setNewAdminData({ ...newAdminData, last_name: e.target.value })}
                    placeholder="Doe"
                  />
                </div>
              </div>
              <div className="form-row-two">
                <div className="form-field">
                  <label>Email *</label>
                  <input
                    type="email"
                    required
                    value={newAdminData.email}
                    onChange={e => setNewAdminData({ ...newAdminData, email: e.target.value })}
                    placeholder="admin@example.com"
                  />
                </div>
                <div className="form-field">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={newAdminData.phone}
                    onChange={e => setNewAdminData({ ...newAdminData, phone: e.target.value })}
                    placeholder="+254712345678"
                  />
                </div>
              </div>
              <div className="form-field">
                <label>Role</label>
                <select
                  value={newAdminData.role}
                  onChange={e => setNewAdminData({ ...newAdminData, role: e.target.value })}
                >
                  <option value="admin">Admin</option>
                  <option value="superadmin">Super Admin</option>
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={submitting}>
                  {submitting ? 'Creating…' : 'Create Admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Edit Admin Modal ─────────────────────────────────────────────── */}
      {showEditModal && selectedAdmin && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Admin</h2>
              <button className="modal-close-btn" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <form className="modal-form" onSubmit={handleEditAdmin}>
              <div className="form-row-two">
                <div className="form-field">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={editAdminData.first_name}
                    onChange={e => setEditAdminData({ ...editAdminData, first_name: e.target.value })}
                  />
                </div>
                <div className="form-field">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={editAdminData.last_name}
                    onChange={e => setEditAdminData({ ...editAdminData, last_name: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-row-two">
                <div className="form-field">
                  <label>Email</label>
                  <input
                    type="email"
                    value={editAdminData.email}
                    onChange={e => setEditAdminData({ ...editAdminData, email: e.target.value })}
                  />
                </div>
                <div className="form-field">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={editAdminData.phone}
                    onChange={e => setEditAdminData({ ...editAdminData, phone: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-field">
                <label>Username</label>
                <input
                  type="text"
                  value={editAdminData.username}
                  disabled
                  style={{ opacity: 0.6, cursor: 'not-allowed' }}
                />
              </div>
              <div className="form-checkbox">
                <input
                  type="checkbox"
                  id="edit_superuser"
                  checked={editAdminData.is_superuser}
                  onChange={e => setEditAdminData({ ...editAdminData, is_superuser: e.target.checked })}
                />
                <label htmlFor="edit_superuser">Super Admin (Full Access)</label>
              </div>
              <div className="form-checkbox">
                <input
                  type="checkbox"
                  id="edit_active"
                  checked={editAdminData.is_active}
                  onChange={e => setEditAdminData({ ...editAdminData, is_active: e.target.checked })}
                />
                <label htmlFor="edit_active">Active Account</label>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={submitting}>
                  {submitting ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuperAdminView;







































// import React, { useState, useEffect } from 'react';
// import './SuperAdminView.css';
// import TransactionsTable from './TransactionsTable';

// const API_BASE_URL = 'https://imselling-marketplace-production.up.railway.app/api';

// // ============================================================================
// // SAFE TOKEN HELPER — never crashes if localStorage is unavailable
// // ============================================================================
// const getToken = () => {
//   try { return localStorage.getItem('token') || ''; }
//   catch { return ''; }
// };

// // ============================================================================
// // INLINE API HELPERS
// // ============================================================================
// const api = {
//   headers: () => ({
//     'Authorization': `Token ${getToken()}`,
//     'Content-Type': 'application/json',
//   }),

//   async fetchAdmins() {
//     const res = await fetch(`${API_BASE_URL}/admin/admins/`, { headers: this.headers() });
//     if (!res.ok) throw new Error(`fetchAdmins: ${res.status}`);
//     const data = await res.json();
//     return Array.isArray(data) ? data : (data.results || []);
//   },

//   async createAdmin(payload) {
//     const res = await fetch(`${API_BASE_URL}/admin/admins/`, {
//       method: 'POST',
//       headers: this.headers(),
//       body: JSON.stringify(payload),
//     });
//     return res.json();
//   },

//   async updateAdmin(id, payload) {
//     const res = await fetch(`${API_BASE_URL}/admin/users/${id}/`, {
//       method: 'PATCH',
//       headers: this.headers(),
//       body: JSON.stringify(payload),
//     });
//     return res.json();
//   },

//   async toggleAdminStatus(id) {
//     const res = await fetch(`${API_BASE_URL}/admin/users/${id}/toggle-status/`, {
//       method: 'POST',
//       headers: this.headers(),
//     });
//     return res.json();
//   },

//   async removeAdminRights(id) {
//     const res = await fetch(`${API_BASE_URL}/admin/admins/${id}/`, {
//       method: 'DELETE',
//       headers: this.headers(),
//     });
//     if (res.status === 204) return { success: true };
//     return res.json();
//   },

//   async verifyUser(id, verdict) {
//     const res = await fetch(`${API_BASE_URL}/admin/users/${id}/verify/`, {
//       method: 'POST',
//       headers: this.headers(),
//       body: JSON.stringify({ verdict }),
//     });
//     return res.json();
//   },

//   async deleteUser(id) {
//     const res = await fetch(`${API_BASE_URL}/admin/users/${id}/`, {
//       method: 'DELETE',
//       headers: this.headers(),
//     });
//     if (res.status === 204 || res.status === 200) return { success: true };
//     return res.json();
//   },

//   async fetchActivityLogs() {
//     try {
//       const res = await fetch(`${API_BASE_URL}/admin/dashboard/activity/`, {
//         headers: this.headers(),
//       });
//       if (!res.ok) return [];
//       const data = await res.json();
//       return Array.isArray(data) ? data : [];
//     } catch {
//       return [];
//     }
//   },
// };

// // ============================================================================
// // EMPTY FORM STATES
// // ============================================================================
// const EMPTY_NEW_ADMIN = {
//   email: '', phone: '', first_name: '', last_name: '',
//   password: '', role: 'admin',
// };

// const EMPTY_EDIT_ADMIN = {
//   first_name: '', last_name: '', email: '', phone: '',
//   username: '', is_superuser: false, is_active: true,
// };

// // ============================================================================
// // SYSTEM CONFIG TABS — 6 tabs with full panel content
// // ============================================================================
// const CONFIG_TABS = [
//   {
//     id: 'security',
//     icon: '🔐',
//     label: 'Security',
//     title: 'Security Settings',
//     subtitle: 'Manage authentication, 2FA, session control and access policies',
//     color: '#6366f1',
//     lightColor: '#eef2ff',
//     fields: [
//       { label: 'Two-Factor Authentication', type: 'toggle', value: true,  hint: 'Require 2FA for all admin logins' },
//       { label: 'Session Timeout (minutes)', type: 'number', value: 30,    hint: 'Auto-logout after inactivity' },
//       { label: 'Max Login Attempts',        type: 'number', value: 5,     hint: 'Lockout after failed attempts' },
//       { label: 'Password Expiry (days)',    type: 'number', value: 90,    hint: 'Force password reset period' },
//       { label: 'IP Whitelist',              type: 'text',   value: '',    hint: 'Comma-separated allowed IPs' },
//       { label: 'Audit Log Retention (days)',type: 'number', value: 365,   hint: 'How long to keep activity logs' },
//     ],
//   },
//   {
//     id: 'payment',
//     icon: '💳',
//     label: 'Payment',
//     title: 'Payment Gateway',
//     subtitle: 'API keys, commission rates, payout schedules and currency settings',
//     color: '#059669',
//     lightColor: '#ecfdf5',
//     fields: [
//       { label: 'M-Pesa API Key',         type: 'password', value: '',    hint: 'Safaricom Daraja API key' },
//       { label: 'M-Pesa Shortcode',       type: 'text',     value: '',    hint: 'Business short code' },
//       { label: 'Platform Commission (%)', type: 'number',  value: 5,     hint: 'Percentage taken per transaction' },
//       { label: 'Minimum Withdrawal',     type: 'number',   value: 100,   hint: 'Minimum KES for payout request' },
//       { label: 'Payout Schedule',        type: 'select',   value: 'weekly', options: ['daily','weekly','monthly'], hint: 'How often payouts are processed' },
//       { label: 'Auto-approve Payouts',   type: 'toggle',   value: false, hint: 'Skip manual review for payouts' },
//     ],
//   },
//   {
//     id: 'email',
//     icon: '📧',
//     label: 'Email & SMS',
//     title: 'Email & SMS',
//     subtitle: 'Configure SMTP, SMS providers and notification templates',
//     color: '#0ea5e9',
//     lightColor: '#f0f9ff',
//     fields: [
//       { label: 'SMTP Host',           type: 'text',     value: 'smtp.gmail.com', hint: 'Outgoing mail server' },
//       { label: 'SMTP Port',           type: 'number',   value: 587,              hint: '587 for TLS, 465 for SSL' },
//       { label: 'SMTP Username',       type: 'text',     value: '',               hint: 'Email account username' },
//       { label: 'SMTP Password',       type: 'password', value: '',               hint: 'Email account password' },
//       { label: 'SMS Provider',        type: 'select',   value: 'africas_talking', options: ['africas_talking','twilio','nexmo'], hint: 'Bulk SMS gateway provider' },
//       { label: 'SMS API Key',         type: 'password', value: '',               hint: 'Provider API key' },
//     ],
//   },
//   {
//     id: 'backup',
//     icon: '🗄️',
//     label: 'Backup',
//     title: 'Database Backup',
//     subtitle: 'Automated backup schedules, retention policies and restore options',
//     color: '#d97706',
//     lightColor: '#fffbeb',
//     fields: [
//       { label: 'Auto Backup',               type: 'toggle', value: true,    hint: 'Enable scheduled backups' },
//       { label: 'Backup Frequency',          type: 'select', value: 'daily', options: ['hourly','daily','weekly'], hint: 'How often backups run' },
//       { label: 'Retain Backups (days)',      type: 'number', value: 30,      hint: 'Delete backups older than this' },
//       { label: 'Backup Storage Path',       type: 'text',   value: '/backups', hint: 'Server directory for backups' },
//       { label: 'Notify on Failure',         type: 'toggle', value: true,    hint: 'Send email alert if backup fails' },
//       { label: 'Compress Backups',          type: 'toggle', value: true,    hint: 'GZIP compression to save space' },
//     ],
//   },
//   {
//     id: 'premium',
//     icon: '👑',
//     label: 'Premium',
//     title: 'Premium Plans & Offers',
//     subtitle: 'Subscription tiers, featured listing packages and promotional deals',
//     color: '#7c3aed',
//     lightColor: '#f5f3ff',
//     // Premium uses a custom renderer — planCards + extras
//     planCards: [
//       {
//         key: 'basic',
//         title: 'Basic Ad Posting Rule',
//         subtitle: 'Base configuration for this ad tier',
//         accent: '#7c3aed',
//         fields: [
//           { key: 'price',     label: 'Price (KES)',  value: 500  },
//           { key: 'maxImages', label: 'Max Images',   value: 2    },
//           { key: 'days',      label: 'Days',         value: 14   },
//         ],
//       },
//       {
//         key: 'pro',
//         title: 'Pro Ad Posting Rule',
//         subtitle: 'Professional level ad configuration',
//         accent: '#7c3aed',
//         fields: [
//           { key: 'price',     label: 'Price (KES)',  value: 1500 },
//           { key: 'maxImages', label: 'Max Images',   value: 5    },
//           { key: 'days',      label: 'Days',         value: 30   },
//         ],
//       },
//       {
//         key: 'featuredBasic',
//         title: 'Featured Listing Fee (Basic)',
//         subtitle: 'Cost to feature a single listing',
//         accent: '#7c3aed',
//         fields: [
//           { key: 'price',     label: 'Price (KES)',  value: 200  },
//           { key: 'maxImages', label: 'Max Images',   value: 10   },
//           { key: 'days',      label: 'Days',         value: 30   },
//         ],
//       },
//       {
//         key: 'featuredPro',
//         title: 'Featured Listing Fee (Pro)',
//         subtitle: 'Cost to feature a single listing',
//         accent: '#7c3aed',
//         fields: [
//           { key: 'price',     label: 'Price (KES)',  value: 200  },
//           { key: 'maxImages', label: 'Max Images',   value: 10   },
//           { key: 'days',      label: 'Days',         value: 30   },
//         ],
//       },
//     ],
//     extras: [
//       { key: 'freeTrial',   label: 'Free Trial Days',    type: 'number', value: 7,    hint: 'Days before billing starts' },
//       { key: 'promoCodes',  label: 'Promo Codes Enabled',type: 'toggle', value: true, hint: 'Allow discount code redemption' },
//       { key: 'maxPromos',   label: 'Max Active Promos',  type: 'number', value: 10,   hint: 'Simultaneous active promotions' },
//     ],
//     fields: [], // empty — premium uses planCards + extras instead
//   },
//   {
//     id: 'analytics',
//     icon: '📊',
//     label: 'Analytics',
//     title: 'Analytics & Reporting',
//     subtitle: 'Data retention, report schedules and export format configuration',
//     color: '#db2777',
//     lightColor: '#fdf2f8',
//     fields: [
//       { label: 'Data Retention (months)',    type: 'number', value: 24,      hint: 'How long raw analytics are kept' },
//       { label: 'Report Schedule',            type: 'select', value: 'weekly', options: ['daily','weekly','monthly'], hint: 'Auto-send report frequency' },
//       { label: 'Report Recipients',          type: 'text',   value: '',      hint: 'Comma-separated email addresses' },
//       { label: 'Export Format',              type: 'select', value: 'csv',   options: ['csv','xlsx','pdf'],          hint: 'Default download format' },
//       { label: 'Track User Behaviour',       type: 'toggle', value: true,    hint: 'Collect page-view analytics' },
//       { label: 'Anonymise Data',             type: 'toggle', value: false,   hint: 'Strip PII from analytics data' },
//     ],
//   },
// ];

// // ============================================================================
// // MAIN COMPONENT
// // ============================================================================
// function SuperAdminView() {
//   const [activityLogs, setActivityLogs]       = useState([]);
//   const [logsLoading, setLogsLoading]         = useState(true);

//   const [admins, setAdmins]                   = useState([]);
//   const [adminsLoading, setAdminsLoading]     = useState(true);
//   const [adminSearchTerm, setAdminSearchTerm] = useState('');

//   const [activeConfigTab, setActiveConfigTab] = useState('security');
//   const [editingTab, setEditingTab]           = useState(null);
//   const [configValues, setConfigValues]       = useState(() =>
//     Object.fromEntries(CONFIG_TABS.map(t => [t.id, Object.fromEntries(t.fields.map(f => [f.label, f.value]))]))
//   );
//   const [draftConfig, setDraftConfig]         = useState(null);
//   const [draftPremiumCards, setDraftPremiumCards]   = useState(null);
//   const [draftPremiumExtras, setDraftPremiumExtras] = useState(null);

//   const premiumTab = CONFIG_TABS.find(t => t.id === 'premium');
//   const [premiumCards, setPremiumCards] = useState(() =>
//     Object.fromEntries(premiumTab.planCards.map(c => [
//       c.key,
//       Object.fromEntries(c.fields.map(f => [f.key, f.value])),
//     ]))
//   );
//   const [premiumExtras, setPremiumExtras] = useState(() =>
//     Object.fromEntries(premiumTab.extras.map(e => [e.key, e.value]))
//   );

//   const [showAddModal, setShowAddModal]       = useState(false);
//   const [showEditModal, setShowEditModal]     = useState(false);
//   const [selectedAdmin, setSelectedAdmin]     = useState(null);
//   const [activeDropdown, setActiveDropdown]   = useState(null);

//   const [newAdminData, setNewAdminData]       = useState(EMPTY_NEW_ADMIN);
//   const [editAdminData, setEditAdminData]     = useState(EMPTY_EDIT_ADMIN);
//   const [submitting, setSubmitting]           = useState(false);
//   const [error, setError]                     = useState(null);

//   // ── Communications Center state ─────────────────────────────────────────────
//   const [commTab, setCommTab]                 = useState('welcome');   // 'welcome' | 'broadcast' | 'history' | 'scheduled'
//   // Welcome message editor
//   const [welcomeMsg, setWelcomeMsg]           = useState(
//     "👋 Welcome to I'm Selling! Kenya's fastest-growing marketplace.\n\nBuy and sell anything easily — from electronics to property.\n\n✅ Post your first ad today and reach thousands of buyers!"
//   );
//   const [welcomeEditing, setWelcomeEditing]   = useState(false);
//   const [welcomeDraft, setWelcomeDraft]       = useState('');
//   const [welcomeSaving, setWelcomeSaving]     = useState(false);
//   // Broadcast message composer
//   const [broadcastTarget, setBroadcastTarget] = useState('all');
//   const [broadcastMsg, setBroadcastMsg]       = useState('');
//   const [broadcastTitle, setBroadcastTitle]   = useState('');
//   const [userSearch, setUserSearch]           = useState('');
//   const [allUsers, setAllUsers]               = useState([]);
//   const [selectedUsers, setSelectedUsers]     = useState([]);
//   const [usersLoading, setUsersLoading]       = useState(false);
//   const [broadcasting, setBroadcasting]       = useState(false);
//   const [broadcastResult, setBroadcastResult] = useState(null);
//   // Message history
//   const [msgHistory, setMsgHistory]           = useState([]);
//   const [historyLoading, setHistoryLoading]   = useState(false);
//   const [historySearch, setHistorySearch]     = useState('');
//   const [deletingMsgId, setDeletingMsgId]     = useState(null);
//   // Scheduled messages list
//   const [scheduledMsgs, setScheduledMsgs]     = useState([]);
//   const [scheduleForm, setScheduleForm]       = useState({ title: '', message: '', target: 'all', sendAt: '' });
//   const [showScheduleForm, setShowScheduleForm] = useState(false);

//   // --------------------------------------------------------------------------
//   useEffect(() => {
//     loadLogs();
//     loadAdmins();
//     loadHistory(); // ✅ Pre-fetch history so it's ready when user clicks the tab
//   }, []);

//   useEffect(() => {
//     const close = () => setActiveDropdown(null);
//     document.addEventListener('click', close);
//     return () => document.removeEventListener('click', close);
//   }, []);

//   // --------------------------------------------------------------------------
//   const loadLogs = async () => {
//     setLogsLoading(true);
//     try {
//       const logs = await api.fetchActivityLogs();
//       setActivityLogs(logs);
//     } catch (err) {
//       console.warn('⚠️ Logs fetch failed:', err.message);
//       setActivityLogs([]);
//     } finally {
//       setLogsLoading(false);
//     }
//   };

//   const loadAdmins = async () => {
//     setAdminsLoading(true);
//     setError(null);
//     try {
//       const data = await api.fetchAdmins();
//       setAdmins(data);
//     } catch (err) {
//       console.warn('⚠️ Admins fetch failed:', err.message);
//       setAdmins([]);
//       setError('Could not load admins. Check your connection or permissions.');
//     } finally {
//       setAdminsLoading(false);
//     }
//   };

//   // --------------------------------------------------------------------------
//   const filteredAdmins = admins.filter(a =>
//     [a.username, a.email, a.first_name, a.last_name]
//       .some(v => v?.toLowerCase().includes(adminSearchTerm.toLowerCase()))
//   );

//   const formatDate = (val) => {
//     if (!val) return 'N/A';
//     try {
//       return new Date(val).toLocaleString('en-US', {
//         year: 'numeric', month: '2-digit', day: '2-digit',
//         hour: '2-digit', minute: '2-digit', hour12: true,
//       });
//     } catch { return 'N/A'; }
//   };

//   const getInitials = (a) => {
//     if (a.first_name && a.last_name) return `${a.first_name[0]}${a.last_name[0]}`.toUpperCase();
//     if (a.username) return a.username.substring(0, 2).toUpperCase();
//     return 'A';
//   };

//   const getRoleBadge = (a) =>
//     a.is_superuser
//       ? { label: 'Super Admin', cls: 'super-admin' }
//       : { label: 'Admin', cls: 'admin' };

//   const getStatusBadge = (a) =>
//     a.is_active !== false
//       ? { label: 'Active', cls: 'active' }
//       : { label: 'Suspended', cls: 'suspended' };

//   const getLogBadgeClass = (action = '') => {
//     const l = action.toLowerCase();
//     if (l.includes('approved') || l.includes('activated')) return 'active';
//     if (l.includes('suspended') || l.includes('deleted'))  return 'inactive';
//     if (l.includes('created')  || l.includes('added'))     return 'new';
//     if (l.includes('updated')  || l.includes('modified'))  return 'updated';
//     return 'default';
//   };

//   // --------------------------------------------------------------------------
//   const handleAddAdmin = async (e) => {
//     e.preventDefault();
//     setSubmitting(true);
//     try {
//       const res = await api.createAdmin(newAdminData);
//       if (res.success !== false) {
//         alert('Admin created successfully!');
//         setShowAddModal(false);
//         setNewAdminData(EMPTY_NEW_ADMIN);
//         loadAdmins();
//       } else {
//         alert(res.error || 'Failed to create admin');
//       }
//     } catch (err) {
//       console.error('❌ createAdmin:', err);
//       alert('Failed to create admin');
//     } finally {
//       setSubmitting(false);
//     }
//   };

//   const openEditModal = (admin) => {
//     setSelectedAdmin(admin);
//     setEditAdminData({
//       first_name:   admin.first_name   || '',
//       last_name:    admin.last_name    || '',
//       email:        admin.email        || '',
//       phone:        admin.phone_number || admin.phone || '',
//       username:     admin.username     || '',
//       is_superuser: admin.is_superuser || false,
//       is_active:    admin.is_active    !== false,
//     });
//     setShowEditModal(true);
//     setActiveDropdown(null);
//   };

//   const handleEditAdmin = async (e) => {
//     e.preventDefault();
//     setSubmitting(true);
//     try {
//       const res = await api.updateAdmin(selectedAdmin.id, editAdminData);
//       if (res.success !== false) {
//         alert('Admin updated successfully!');
//         setShowEditModal(false);
//         setSelectedAdmin(null);
//         loadAdmins();
//       } else {
//         alert(res.error || 'Failed to update admin');
//       }
//     } catch (err) {
//       console.error('❌ updateAdmin:', err);
//       alert('Failed to update admin');
//     } finally {
//       setSubmitting(false);
//     }
//   };

//   const handleToggleStatus = async (admin) => {
//     const action = admin.is_active !== false ? 'suspend' : 'activate';
//     if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} ${admin.username}?`)) return;
//     try {
//       const res = await api.toggleAdminStatus(admin.id);
//       if (res.success !== false) {
//         setAdmins(prev => prev.map(a =>
//           a.id === admin.id ? { ...a, is_active: !a.is_active } : a
//         ));
//       } else {
//         alert(res.error || `Failed to ${action} admin`);
//       }
//     } catch (err) {
//       console.error('❌ toggleStatus:', err);
//       alert(`Failed to ${action} admin`);
//     }
//     setActiveDropdown(null);
//   };

//   const handleRevokeAdmin = async (admin) => {
//     if (!window.confirm(`Revoke admin rights for ${admin.username}?`)) return;
//     try {
//       const res = await api.removeAdminRights(admin.id);
//       if (res.success !== false) {
//         alert('Admin rights revoked.');
//         loadAdmins();
//       } else {
//         alert(res.error || 'Failed to revoke admin rights');
//       }
//     } catch (err) {
//       console.error('❌ revokeAdmin:', err);
//       alert('Failed to revoke admin rights');
//     }
//     setActiveDropdown(null);
//   };

//   const handleVerifyAdmin = async (admin) => {
//     const currentStatus = admin.verification_status || 'unverified';
//     const isVerified = currentStatus === 'verified';
//     const verdict = isVerified ? 'pending_reverification' : 'verified';
//     const label   = isVerified ? 'Mark for re-verification' : 'verify';
//     if (!window.confirm(`${label.charAt(0).toUpperCase() + label.slice(1)} ${admin.username}?`)) return;
//     try {
//       const res = await api.verifyUser(admin.id, verdict);
//       if (res.success !== false) {
//         alert(`✅ ${admin.username} marked as ${res.verification_status || verdict}.`);
//         loadAdmins(); // re-fetch so verification_status reflects DB value
//       } else {
//         alert(res.error || 'Failed to update verification');
//       }
//     } catch (err) {
//       console.error('❌ verifyAdmin:', err);
//       alert('Failed to update verification');
//     }
//     setActiveDropdown(null);
//   };

//   const handleDeleteAdmin = async (admin) => {
//     if (!window.confirm(`⚠️ Permanently delete ${admin.username}? This cannot be undone.`)) return;
//     try {
//       const res = await api.deleteUser(admin.id);
//       if (res.success !== false) {
//         setAdmins(prev => prev.filter(a => a.id !== admin.id));
//         alert(`🗑️ ${admin.username} deleted successfully.`);
//       } else {
//         alert(res.error || 'Failed to delete admin');
//       }
//     } catch (err) {
//       console.error('❌ deleteAdmin:', err);
//       alert('Failed to delete admin');
//     }
//     setActiveDropdown(null);
//   };

//   // --------------------------------------------------------------------------
//   // CONFIG EDIT MODE HANDLERS
//   // --------------------------------------------------------------------------
//   const handleConfigEdit = (tabId) => {
//     // Snapshot current values into draft
//     setDraftConfig({ ...configValues });
//     setDraftPremiumCards(JSON.parse(JSON.stringify(premiumCards)));
//     setDraftPremiumExtras({ ...premiumExtras });
//     setEditingTab(tabId);
//   };

//   const handleConfigSave = async (tab) => {
//     // Commit drafts to saved state
//     if (tab.id === 'premium') {
//       setPremiumCards(draftPremiumCards);
//       setPremiumExtras(draftPremiumExtras);
//       // TODO: await api.saveSystemConfig('premium', { cards: draftPremiumCards, extras: draftPremiumExtras });
//     } else {
//       setConfigValues(draftConfig);
//       // TODO: await api.saveSystemConfig(tab.id, draftConfig[tab.id]);
//     }
//     alert(`${tab.title} settings saved!`);
//     setEditingTab(null);
//     setDraftConfig(null);
//     setDraftPremiumCards(null);
//     setDraftPremiumExtras(null);
//   };

//   const handleConfigCancel = () => {
//     setEditingTab(null);
//     setDraftConfig(null);
//     setDraftPremiumCards(null);
//     setDraftPremiumExtras(null);
//   };

//   // ── Communications Center handlers ──────────────────────────────────────────
//   const handleWelcomeSave = async () => {
//     setWelcomeSaving(true);
//     try {
//       await fetch(`${API_BASE_URL}/auth/admin/broadcast/`, {
//         method: 'POST',
//         headers: { 'Authorization': `Token ${getToken()}`, 'Content-Type': 'application/json' },
//         body: JSON.stringify({ title: "Welcome Message Update", message: welcomeDraft, target: 'all' }),
//       });
//       setWelcomeMsg(welcomeDraft);
//       setWelcomeEditing(false);
//       alert("✅ Welcome message updated! New users will see this when they sign up.");
//     } catch {
//       setWelcomeMsg(welcomeDraft);
//       setWelcomeEditing(false);
//       alert("✅ Welcome message saved locally.");
//     } finally {
//       setWelcomeSaving(false);
//     }
//   };

//   const loadAllUsers = async () => {
//     setUsersLoading(true);
//     try {
//       const res = await fetch(`${API_BASE_URL}/admin/users/?page_size=200`, {
//         headers: { 'Authorization': `Token ${getToken()}` },
//       });
//       if (res.ok) {
//         const data = await res.json();
//         setAllUsers(data.results || data || []);
//       }
//     } catch { setAllUsers([]); }
//     finally { setUsersLoading(false); }
//   };

//   useEffect(() => {
//     if (commTab === 'broadcast') loadAllUsers();
//     if (commTab === 'history')   loadHistory();
//   }, [commTab]);

//   const loadHistory = async () => {
//     setHistoryLoading(true);
//     try {
//       const res = await fetch(`${API_BASE_URL}/auth/admin/broadcast-history/`, {
//         headers: { 'Authorization': `Token ${getToken()}` },
//       });
//       const data = await res.json();
//       if (res.ok) {
//         setMsgHistory(data.results || []);
//       } else {
//         console.error('broadcast_history error:', data.error || data);
//         setMsgHistory([]);
//       }
//     } catch (e) {
//       console.error('broadcast_history network error:', e);
//       setMsgHistory([]);
//     } finally { setHistoryLoading(false); }
//   };

//   const handleDeleteBroadcast = async (msgId, title) => {
//     if (!window.confirm(
//       `Delete "${title || 'this broadcast'}" from ALL users' I'm Selling inbox?\n\nThis cannot be undone.`
//     )) return;
//     setDeletingMsgId(msgId);
//     try {
//       const res = await fetch(`${API_BASE_URL}/auth/admin/broadcast-history/${msgId}/`, {
//         method: 'DELETE',
//         headers: { 'Authorization': `Token ${getToken()}` },
//       });
//       if (res.ok) {
//         const data = await res.json();
//         setMsgHistory(prev => prev.filter(m => m.id !== msgId));
//         alert(`✅ Deleted from ${data.deleted} user inbox${data.deleted !== 1 ? 'es' : ''}.`);
//       } else {
//         alert('Failed to delete. Please try again.');
//       }
//     } catch {
//       alert('Network error. Please try again.');
//     } finally {
//       setDeletingMsgId(null);
//     }
//   };

//   const toggleUserSelect = (uid) => {
//     setSelectedUsers(prev =>
//       prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]
//     );
//   };

//   const handleBroadcast = async () => {
//     if (!broadcastMsg.trim()) { alert('Please enter a message.'); return; }
//     if (broadcastTarget === 'selected' && selectedUsers.length === 0) {
//       alert('Please select at least one user.'); return;
//     }
//     if (!window.confirm(
//       broadcastTarget === 'all'
//         ? 'Send this message to ALL users?'
//         : `Send this message to ${selectedUsers.length} selected user(s)?`
//     )) return;

//     setBroadcasting(true);
//     setBroadcastResult(null);
//     try {
//       const payload = {
//         title:   broadcastTitle || "Message from I'm Selling",
//         message: broadcastMsg,
//         target:  broadcastTarget,
//         ...(broadcastTarget === 'selected' ? { user_ids: selectedUsers } : {}),
//       };
//       const res = await fetch(`${API_BASE_URL}/auth/admin/broadcast/`, {
//         method: 'POST',
//         headers: { 'Authorization': `Token ${getToken()}`, 'Content-Type': 'application/json' },
//         body: JSON.stringify(payload),
//       });
//       const data = await res.json();
//       if (res.ok) {
//         setBroadcastResult({ success: true, sent: data.sent, failed: data.failed || 0 });
//         setBroadcastMsg(''); setBroadcastTitle(''); setSelectedUsers([]);
//         loadHistory();
//       } else {
//         setBroadcastResult({ success: false, error: data.error || 'Send failed.' });
//       }
//     } catch (e) {
//       setBroadcastResult({ success: false, error: 'Network error. Check connection.' });
//     } finally {
//       setBroadcasting(false);
//     }
//   };

//   const handleAddScheduled = () => {
//     if (!scheduleForm.message.trim() || !scheduleForm.sendAt) {
//       alert('Please fill in message and send date/time.'); return;
//     }
//     setScheduledMsgs(prev => [
//       ...prev,
//       { id: Date.now(), ...scheduleForm, status: 'scheduled', createdAt: new Date().toISOString() }
//     ]);
//     setScheduleForm({ title: '', message: '', target: 'all', sendAt: '' });
//     setShowScheduleForm(false);
//   };

//   const handleDeleteScheduled = (id) => {
//     if (!window.confirm('Delete this scheduled message?')) return;
//     setScheduledMsgs(prev => prev.filter(m => m.id !== id));
//   };

//   // --------------------------------------------------------------------------
//   return (
//     <div className="super-admin-view">

//       {/* ── System Configuration ─────────────────────────────────────────── */}
//       <div className="system-settings-section">
//         <div className="section-header">
//           <h2>System Configuration</h2>
//           <span className="config-badge">6 modules</span>
//         </div>

//         {/* ── Tab Nav Bar ── */}
//         <div className="config-tab-bar">
//           {CONFIG_TABS.map(tab => (
//             <button
//               key={tab.id}
//               className={`config-tab-btn ${activeConfigTab === tab.id ? 'active' : ''}`}
//               style={activeConfigTab === tab.id ? { '--tab-color': tab.color, '--tab-light': tab.lightColor } : {}}
//               onClick={() => setActiveConfigTab(tab.id)}
//             >
//               <span className="config-tab-icon">{tab.icon}</span>
//               <span className="config-tab-label">{tab.label}</span>
//               {activeConfigTab === tab.id && (
//                 <span className="config-tab-indicator" style={{ background: tab.color }} />
//               )}
//             </button>
//           ))}
//         </div>

//         {/* ── Tab Panel ── */}
//         {CONFIG_TABS.map(tab => {
//           if (tab.id !== activeConfigTab) return null;
//           const isEditing = editingTab === tab.id;

//           // Shared panel header with Edit / Editing / Save / Cancel buttons
//           const PanelHeader = () => (
//             <div className="config-panel-header" style={{ borderLeftColor: tab.color }}>
//               <div className="config-panel-title-row">
//                 <div className="config-panel-icon" style={{ background: tab.lightColor, color: tab.color }}>
//                   {tab.icon}
//                 </div>
//                 <div>
//                   <h3 className="config-panel-title">{tab.title}</h3>
//                   <p className="config-panel-subtitle">{tab.subtitle}</p>
//                 </div>
//               </div>
//               <div className="config-header-actions">
//                 {!isEditing ? (
//                   /* ── View mode: only Edit button ── */
//                   <button
//                     className="config-edit-btn"
//                     style={{ '--edit-color': tab.color }}
//                     onClick={() => handleConfigEdit(tab.id)}
//                   >
//                     ✏️ Edit
//                   </button>
//                 ) : (
//                   /* ── Edit mode: Cancel + Save ── */
//                   <>
//                     <button
//                       className="config-cancel-btn"
//                       onClick={handleConfigCancel}
//                     >
//                       Cancel
//                     </button>
//                     <button
//                       className="config-save-btn"
//                       style={{ background: tab.color }}
//                       onClick={() => handleConfigSave(tab)}
//                     >
//                       Save Changes
//                     </button>
//                   </>
//                 )}
//               </div>
//             </div>
//           );

//           // ── PREMIUM custom renderer ──────────────────────────────────────
//           if (tab.id === 'premium') {
//             const cards  = isEditing ? draftPremiumCards  : premiumCards;
//             const extras = isEditing ? draftPremiumExtras : premiumExtras;

//             return (
//               <div key="premium" className={`config-panel ${isEditing ? 'editing' : ''}`}>
//                 <PanelHeader />

//                 {/* Edit mode banner */}
//                 {isEditing && (
//                   <div className="config-editing-banner" style={{ background: `${tab.color}12`, borderColor: `${tab.color}40` }}>
//                     <span style={{ color: tab.color }}>✏️</span>
//                     <span>You are now editing — make your changes and click <strong>Save Changes</strong> to apply.</span>
//                   </div>
//                 )}

//                 {/* Plan Cards — 2-column grid */}
//                 <div className="premium-plan-grid">
//                   {tab.planCards.map(card => (
//                     <div key={card.key} className={`premium-plan-card ${isEditing ? 'editable' : ''}`}>
//                       <div className="premium-plan-card-header">
//                         <div>
//                           <p className="premium-plan-card-title">{card.title}</p>
//                           <p className="premium-plan-card-sub">{card.subtitle}</p>
//                         </div>
//                       </div>
//                       <div className="premium-plan-fields">
//                         {card.fields.map(f => (
//                           <div key={f.key} className="premium-plan-field">
//                             <label className="premium-plan-field-label">{f.label}</label>
//                             <input
//                               className="premium-plan-input"
//                               type="number"
//                               value={cards[card.key][f.key]}
//                               readOnly={!isEditing}
//                               style={isEditing ? { '--focus-color': tab.color } : {}}
//                               onChange={e =>
//                                 isEditing && setDraftPremiumCards(prev => ({
//                                   ...prev,
//                                   [card.key]: { ...prev[card.key], [f.key]: e.target.value },
//                                 }))
//                               }
//                             />
//                           </div>
//                         ))}
//                       </div>
//                     </div>
//                   ))}
//                 </div>

//                 {/* Extras row — 3 columns */}
//                 <div className="premium-extras-grid">
//                   {tab.extras.map(extra => (
//                     <div key={extra.key} className={`config-field ${isEditing ? 'editable' : ''}`}>
//                       <label className="config-field-label">{extra.label}</label>
//                       <p className="config-field-hint">{extra.hint}</p>

//                       {extra.type === 'toggle' && (
//                         <button
//                           className={`config-toggle ${extras[extra.key] ? 'on' : 'off'} ${!isEditing ? 'locked' : ''}`}
//                           style={extras[extra.key] ? { '--toggle-color': tab.color } : {}}
//                           onClick={() =>
//                             isEditing && setDraftPremiumExtras(prev => ({ ...prev, [extra.key]: !prev[extra.key] }))
//                           }
//                         >
//                           <span className="config-toggle-thumb" />
//                           <span className="config-toggle-text">
//                             {extras[extra.key] ? 'Enabled' : 'Disabled'}
//                           </span>
//                         </button>
//                       )}

//                       {extra.type === 'number' && (
//                         <input
//                           className="config-input"
//                           type="number"
//                           value={extras[extra.key]}
//                           readOnly={!isEditing}
//                           style={isEditing ? { '--focus-color': tab.color } : {}}
//                           onChange={e =>
//                             isEditing && setDraftPremiumExtras(prev => ({ ...prev, [extra.key]: e.target.value }))
//                           }
//                         />
//                       )}
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             );
//           }

//           // ── DEFAULT renderer (all other tabs) ────────────────────────────
//           const values = isEditing ? draftConfig : configValues;

//           return (
//             <div key={tab.id} className={`config-panel ${isEditing ? 'editing' : ''}`}>
//               <PanelHeader />

//               {/* Edit mode banner */}
//               {isEditing && (
//                 <div className="config-editing-banner" style={{ background: `${tab.color}12`, borderColor: `${tab.color}40` }}>
//                   <span style={{ color: tab.color }}>✏️</span>
//                   <span>You are now editing — make your changes and click <strong>Save Changes</strong> to apply.</span>
//                 </div>
//               )}

//               {/* Fields grid */}
//               <div className="config-fields-grid">
//                 {tab.fields.map(field => (
//                   <div key={field.label} className={`config-field ${isEditing ? 'editable' : ''}`}>
//                     <label className="config-field-label">{field.label}</label>
//                     <p className="config-field-hint">{field.hint}</p>

//                     {field.type === 'toggle' && (
//                       <button
//                         className={`config-toggle ${values[tab.id][field.label] ? 'on' : 'off'} ${!isEditing ? 'locked' : ''}`}
//                         style={values[tab.id][field.label] ? { '--toggle-color': tab.color } : {}}
//                         onClick={() =>
//                           isEditing && setDraftConfig(prev => ({
//                             ...prev,
//                             [tab.id]: { ...prev[tab.id], [field.label]: !prev[tab.id][field.label] },
//                           }))
//                         }
//                       >
//                         <span className="config-toggle-thumb" />
//                         <span className="config-toggle-text">
//                           {values[tab.id][field.label] ? 'Enabled' : 'Disabled'}
//                         </span>
//                       </button>
//                     )}

//                     {field.type === 'select' && (
//                       <select
//                         className="config-input"
//                         value={values[tab.id][field.label]}
//                         disabled={!isEditing}
//                         style={isEditing ? { '--focus-color': tab.color } : {}}
//                         onChange={e =>
//                           isEditing && setDraftConfig(prev => ({
//                             ...prev,
//                             [tab.id]: { ...prev[tab.id], [field.label]: e.target.value },
//                           }))
//                         }
//                       >
//                         {field.options.map(o => (
//                           <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>
//                         ))}
//                       </select>
//                     )}

//                     {(field.type === 'text' || field.type === 'number' || field.type === 'password') && (
//                       <input
//                         className="config-input"
//                         type={field.type}
//                         value={values[tab.id][field.label]}
//                         readOnly={!isEditing}
//                         placeholder={field.type === 'password' ? '••••••••••••' : ''}
//                         style={isEditing ? { '--focus-color': tab.color } : {}}
//                         onChange={e =>
//                           isEditing && setDraftConfig(prev => ({
//                             ...prev,
//                             [tab.id]: { ...prev[tab.id], [field.label]: e.target.value },
//                           }))
//                         }
//                       />
//                     )}
//                   </div>
//                 ))}
//               </div>
//             </div>
//           );
//         })}
//       </div>

//       {/* ── Activity Logs ────────────────────────────────────────────────── */}
//       <div className="activity-logs-section">
//         <div className="section-header">
//           <div className="logs-header-left">
//             <h2>Admin Activity Logs</h2>
//             {!logsLoading && activityLogs.length > 5 && (
//               <span className="logs-count-badge">{activityLogs.length} entries</span>
//             )}
//           </div>
//           <button className="btn-secondary" onClick={() => alert('Export coming soon!')}>
//             ⬇️ Export Logs
//           </button>
//         </div>

//         {logsLoading ? (
//           <div className="loading-container">
//             <div className="spinner" />
//             <p>Loading activity logs…</p>
//           </div>
//         ) : (
//           <>
//             <div className="logs-table-wrapper">
//               {/* Sticky header — sits outside scroll zone */}
//               <table className="logs-table logs-table-head">
//                 <thead>
//                   <tr>
//                     <th style={{ width: '25%' }}>ADMIN</th>
//                     <th style={{ width: '18%' }}>ACTION</th>
//                     <th style={{ width: '25%' }}>RESOURCE</th>
//                     <th style={{ width: '12%' }}>IP ADDRESS</th>
//                     <th style={{ width: '20%' }}>TIMESTAMP</th>
//                   </tr>
//                 </thead>
//               </table>

//               {/* Scrollable body — ~5 rows visible */}
//               <div className="logs-scroll-body">
//                 <table className="logs-table logs-table-body">
//                   <colgroup>
//                     <col style={{ width: '25%' }} />
//                     <col style={{ width: '18%' }} />
//                     <col style={{ width: '25%' }} />
//                     <col style={{ width: '12%' }} />
//                     <col style={{ width: '20%' }} />
//                   </colgroup>
//                   <tbody>
//                     {activityLogs.length === 0 ? (
//                       <tr>
//                         <td colSpan="5" className="empty-cell">
//                           <div className="empty-state">
//                             <span style={{ fontSize: 40 }}>📋</span>
//                             <h3>No activity logs</h3>
//                             <p>Admin actions will appear here</p>
//                           </div>
//                         </td>
//                       </tr>
//                     ) : activityLogs.map((log, i) => (
//                       <tr key={log.id ?? i}>
//                         <td>
//                           <div className="admin-cell">
//                             <div className="admin-avatar">
//                               {(log.admin_email || log.user || 'A').charAt(0).toUpperCase()}
//                             </div>
//                             <div className="admin-info">
//                               <div className="admin-email">
//                                 {log.admin_email || log.user || 'Unknown'}
//                               </div>
//                               <div className="admin-role">{log.admin_role || 'Admin'}</div>
//                             </div>
//                           </div>
//                         </td>
//                         <td>
//                           <span className={`status-badge ${getLogBadgeClass(log.action || log.type)}`}>
//                             {log.action || log.title || '—'}
//                           </span>
//                         </td>
//                         <td className="resource-cell">
//                           {log.resource_type && (
//                             <span className="resource-type">{log.resource_type}</span>
//                           )}
//                           {log.resource_name || log.description || log.resource_id || 'N/A'}
//                         </td>
//                         <td className="ip-cell">{log.ip_address || 'N/A'}</td>
//                         <td className="timestamp-cell">
//                           {formatDate(log.created_at || log.timestamp)}
//                         </td>
//                       </tr>
//                     ))}
//                   </tbody>
//                 </table>
//               </div>
//             </div>

//             {activityLogs.length > 5 && (
//               <p className="logs-scroll-hint">
//                 ↕ Scroll to see all {activityLogs.length} entries
//               </p>
//             )}
//           </>
//         )}
//       </div>

//       {/* ════════════════════════════════════════════════════════════════════
//           TRANSACTION LEDGER
//           ════════════════════════════════════════════════════════════════════ */}
//       <div style={{
//         background: '#fff', borderRadius: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
//         marginBottom: 28, padding: '24px 28px', border: '1px solid #e2e8f0',
//       }}>
//         <TransactionsTable />
//       </div>

//       {/* ════════════════════════════════════════════════════════════════════
//           COMMUNICATIONS CENTER — redesigned with UserCommunications style
//           ════════════════════════════════════════════════════════════════════ */}
//       <div style={{
//         background: '#fff', borderRadius: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
//         marginBottom: 28, overflow: 'hidden', border: '1px solid #e2e8f0',
//       }}>
//         {/* ── Header ── */}
//         <div style={{
//           padding: '20px 28px 0', borderBottom: '1px solid #f1f5f9',
//         }}>
//           <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
//             <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>📡 Communications Center</h2>
//             <span style={{ background: '#eef2ff', color: '#6366f1', borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 700 }}>4 tools</span>
//           </div>
//           <p style={{ margin: '0 0 16px', fontSize: 12, color: '#94a3b8' }}>
//             Manage welcome messages · Broadcast to users via I'm Selling inbox · View & delete sent messages
//           </p>
//           {/* Stats row */}
//           <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
//             {[
//               { label: 'Total Sent',  value: msgHistory.length,                          color: '#6366f1' },
//               { label: 'Users',       value: allUsers.length || '—',                     color: '#0ea5e9' },
//               { label: 'Scheduled',   value: scheduledMsgs.length,                       color: '#f59e0b' },
//               { label: 'History',     value: msgHistory.reduce((s,m) => s + (m.sent_count||0), 0) || 0, color: '#10b981' },
//             ].map(s => (
//               <div key={s.label} style={{
//                 flex: 1, background: '#f8fafc', borderRadius: 8, padding: '8px 10px',
//                 textAlign: 'center', borderTop: `3px solid ${s.color}`,
//               }}>
//                 <div style={{ fontSize: 18, fontWeight: 800, color: s.color }}>{s.value}</div>
//                 <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{s.label}</div>
//               </div>
//             ))}
//           </div>
//           {/* Tab bar */}
//           <div style={{ display: 'flex', gap: 4 }}>
//             {[
//               { id: 'welcome',   icon: '👋', label: 'Welcome Message' },
//               { id: 'broadcast', icon: '📣', label: 'Broadcast' },
//               { id: 'history',   icon: '📬', label: `History ${msgHistory.length > 0 ? `(${msgHistory.length})` : ''}` },
//               { id: 'scheduled', icon: '🕐', label: `Scheduled ${scheduledMsgs.length > 0 ? `(${scheduledMsgs.length})` : ''}` },
//             ].map(t => (
//               <button key={t.id} onClick={() => setCommTab(t.id)} style={{
//                 padding: '10px 18px', border: 'none', borderRadius: '8px 8px 0 0',
//                 background: commTab === t.id ? '#fff' : 'transparent',
//                 color: commTab === t.id ? '#6366f1' : '#64748b',
//                 fontWeight: commTab === t.id ? 700 : 500,
//                 fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
//                 borderBottom: commTab === t.id ? '2px solid #6366f1' : '2px solid transparent',
//                 transition: 'all 0.15s',
//               }}>
//                 <span>{t.icon}</span><span>{t.label}</span>
//               </button>
//             ))}
//           </div>
//         </div>

//         {/* ─────────── WELCOME MESSAGE tab ─────────── */}
//         {commTab === 'welcome' && (
//           <div style={{ padding: 24 }}>
//             <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
//               <div>
//                 <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginBottom: 4 }}>👋 Welcome Message</div>
//                 <div style={{ fontSize: 12, color: '#94a3b8' }}>Sent to every new user when they sign up — appears in their I'm Selling inbox</div>
//               </div>
//               {!welcomeEditing ? (
//                 <button onClick={() => { setWelcomeDraft(welcomeMsg); setWelcomeEditing(true); }} style={{
//                   padding: '8px 18px', background: '#eef2ff', color: '#6366f1', border: '1.5px solid #c7d2fe',
//                   borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
//                 }}>✏️ Edit</button>
//               ) : (
//                 <div style={{ display: 'flex', gap: 8 }}>
//                   <button onClick={() => setWelcomeEditing(false)} style={{ padding: '8px 16px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
//                   <button onClick={handleWelcomeSave} disabled={welcomeSaving} style={{ padding: '8px 18px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: welcomeSaving ? 0.6 : 1 }}>
//                     {welcomeSaving ? 'Saving…' : '💾 Save'}
//                   </button>
//                 </div>
//               )}
//             </div>
//             {!welcomeEditing ? (
//               <div style={{ background: '#f1f5f9', borderRadius: 12, padding: '16px 18px', maxWidth: 560 }}>
//                 <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
//                   <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>🏪</div>
//                   <span style={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>I'm Selling</span>
//                   <span style={{ fontSize: 11, color: '#94a3b8' }}>Official</span>
//                 </div>
//                 <div style={{ fontSize: 13.5, color: '#334155', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{welcomeMsg}</div>
//               </div>
//             ) : (
//               <div>
//                 <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 8, padding: '9px 13px', marginBottom: 12, fontSize: 12, color: '#4338ca' }}>
//                   <span>✏️</span><span>Edit the welcome message — plain text and emoji supported</span>
//                 </div>
//                 <textarea value={welcomeDraft} onChange={e => setWelcomeDraft(e.target.value)} rows={8} style={{
//                   width: '100%', padding: '11px 13px', border: '1.5px solid #c7d2fe', borderRadius: 8,
//                   fontSize: 13.5, fontFamily: 'inherit', color: '#1e293b', background: '#f8fafc',
//                   resize: 'vertical', outline: 'none', boxSizing: 'border-box',
//                 }} />
//                 <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'right', marginTop: 4 }}>{welcomeDraft.length} characters</div>
//               </div>
//             )}
//           </div>
//         )}

//         {/* ─────────── BROADCAST tab ─────────── */}
//         {commTab === 'broadcast' && (
//           <div style={{ padding: 24 }}>
//             <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginBottom: 4 }}>📣 Broadcast Message</div>
//             <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 20 }}>Send a direct message via I'm Selling inbox to all users or selected users</div>

//             {broadcastResult && (
//               <div style={{
//                 display: 'flex', justifyContent: 'space-between', alignItems: 'center',
//                 padding: '11px 16px', borderRadius: 8, marginBottom: 18,
//                 background: broadcastResult.success ? '#dcfce7' : '#fee2e2',
//                 color: broadcastResult.success ? '#15803d' : '#dc2626',
//                 border: `1px solid ${broadcastResult.success ? '#86efac' : '#fca5a5'}`,
//                 fontSize: 13.5, fontWeight: 600,
//               }}>
//                 <span>{broadcastResult.success
//                   ? `✅ Sent to ${broadcastResult.sent} user${broadcastResult.sent !== 1 ? 's' : ''}${broadcastResult.failed > 0 ? ` (${broadcastResult.failed} failed)` : ''}!`
//                   : `❌ ${broadcastResult.error}`}
//                 </span>
//                 <button onClick={() => setBroadcastResult(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'inherit', lineHeight: 1 }}>×</button>
//               </div>
//             )}

//             <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
//               <div>
//                 <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
//                   Message Title <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: 12 }}>(optional)</span>
//                 </label>
//                 <input value={broadcastTitle} onChange={e => setBroadcastTitle(e.target.value)}
//                   placeholder="e.g. New Feature Alert 🎉"
//                   style={{ width: '100%', height: 38, padding: '0 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13.5, background: '#f8fafc', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
//               </div>
//               <div>
//                 <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Message *</label>
//                 <textarea value={broadcastMsg} onChange={e => setBroadcastMsg(e.target.value)}
//                   rows={5} placeholder="Type your message to users here…"
//                   style={{ width: '100%', padding: '11px 13px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13.5, fontFamily: 'inherit', color: '#1e293b', background: '#f8fafc', resize: 'vertical', outline: 'none', boxSizing: 'border-box' }} />
//                 <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'right', marginTop: 3 }}>{broadcastMsg.length} characters</div>
//               </div>
//               <div>
//                 <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 8 }}>Send To</label>
//                 <div style={{ display: 'flex', gap: 10 }}>
//                   {[{ v: 'all', label: `🌍 All Users`, count: allUsers.length }, { v: 'selected', label: '👤 Selected Users', count: selectedUsers.length }].map(opt => (
//                     <button key={opt.v} onClick={() => setBroadcastTarget(opt.v)} style={{
//                       display: 'flex', alignItems: 'center', gap: 8, padding: '9px 18px',
//                       border: `2px solid ${broadcastTarget === opt.v ? '#6366f1' : '#e2e8f0'}`,
//                       borderRadius: 9, background: broadcastTarget === opt.v ? '#eef2ff' : '#fff',
//                       color: broadcastTarget === opt.v ? '#6366f1' : '#475569',
//                       fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
//                     }}>
//                       {opt.label}
//                       {opt.count > 0 && (
//                         <span style={{ background: broadcastTarget === opt.v ? '#6366f1' : '#e2e8f0', color: broadcastTarget === opt.v ? '#fff' : '#475569', borderRadius: 12, padding: '1px 8px', fontSize: 11, fontWeight: 700 }}>{opt.count}</span>
//                       )}
//                     </button>
//                   ))}
//                 </div>
//               </div>

//               {broadcastTarget === 'selected' && (
//                 <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
//                   <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
//                     <input value={userSearch} onChange={e => setUserSearch(e.target.value)}
//                       placeholder="🔍 Search users by email or name…"
//                       style={{ width: '100%', height: 34, padding: '0 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, background: '#fff', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
//                   </div>
//                   {selectedUsers.length > 0 && (
//                     <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>
//                       {selectedUsers.map(uid => {
//                         const u = allUsers.find(x => x.id === uid);
//                         return u ? (
//                           <span key={uid} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#eef2ff', color: '#6366f1', border: '1px solid #c7d2fe', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>
//                             {u.email || u.username}
//                             <button onClick={() => toggleUserSelect(uid)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
//                           </span>
//                         ) : null;
//                       })}
//                     </div>
//                   )}
//                   <div style={{ maxHeight: 200, overflowY: 'auto' }}>
//                     {usersLoading ? (
//                       <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Loading users…</div>
//                     ) : allUsers.filter(u => {
//                       if (!userSearch) return true;
//                       const q = userSearch.toLowerCase();
//                       return (u.email||'').toLowerCase().includes(q) || (u.username||'').toLowerCase().includes(q) || (u.first_name||'').toLowerCase().includes(q);
//                     }).slice(0, 30).map(u => (
//                       <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 13px', cursor: 'pointer', borderBottom: '1px solid #f8fafc', background: selectedUsers.includes(u.id) ? '#f0f4ff' : 'white', transition: 'background 0.12s' }}>
//                         <input type="checkbox" checked={selectedUsers.includes(u.id)} onChange={() => toggleUserSelect(u.id)} style={{ accentColor: '#6366f1' }} />
//                         <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#6366f1', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
//                           {(u.email || u.username || '?')[0].toUpperCase()}
//                         </div>
//                         <div style={{ minWidth: 0 }}>
//                           <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{u.first_name ? `${u.first_name} ${u.last_name||''}`.trim() : u.username}</div>
//                           <div style={{ fontSize: 11, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
//                         </div>
//                       </label>
//                     ))}
//                   </div>
//                 </div>
//               )}

//               <button onClick={handleBroadcast} disabled={broadcasting || !broadcastMsg.trim()} style={{
//                 display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 28px',
//                 background: broadcasting || !broadcastMsg.trim() ? '#e2e8f0' : '#6366f1',
//                 color: broadcasting || !broadcastMsg.trim() ? '#94a3b8' : '#fff',
//                 border: 'none', borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: broadcasting || !broadcastMsg.trim() ? 'not-allowed' : 'pointer',
//                 transition: 'all 0.15s', alignSelf: 'flex-start',
//               }}>
//                 {broadcasting ? '⏳ Sending…' : broadcastTarget === 'all' ? '📣 Send to All Users' : `📣 Send to ${selectedUsers.length} User${selectedUsers.length !== 1 ? 's' : ''}`}
//               </button>
//             </div>
//           </div>
//         )}

//         {/* ─────────── HISTORY tab ─────────── */}
//         {commTab === 'history' && (
//           <div>
//             <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 12px' }}>
//               <div>
//                 <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginBottom: 4 }}>📬 Message History</div>
//                 <div style={{ fontSize: 12, color: '#94a3b8' }}>All admin broadcasts — delete removes from every user's I'm Selling inbox</div>
//               </div>
//               <button onClick={loadHistory} style={{ padding: '7px 16px', background: '#f5f3ff', color: '#7c3aed', border: '1.5px solid #ddd6fe', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>🔄 Refresh</button>
//             </div>
//             <div style={{ padding: '0 24px 14px' }}>
//               <input value={historySearch} onChange={e => setHistorySearch(e.target.value)}
//                 placeholder="🔍 Search by title or content…"
//                 style={{ width: '100%', height: 38, padding: '0 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#f8fafc', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
//             </div>
//             {historyLoading ? (
//               <div style={{ padding: '48px 24px', textAlign: 'center', color: '#94a3b8' }}>
//                 <div style={{ fontSize: 32, marginBottom: 10 }}>⏳</div>
//                 <div style={{ fontSize: 13 }}>Loading message history…</div>
//               </div>
//             ) : msgHistory.length === 0 ? (
//               <div style={{ padding: '52px 24px', textAlign: 'center', color: '#94a3b8' }}>
//                 <div style={{ fontSize: 44, marginBottom: 12 }}>📭</div>
//                 <div style={{ fontSize: 14, fontWeight: 600, color: '#475569', marginBottom: 6 }}>No broadcast messages sent yet</div>
//                 <div style={{ fontSize: 13 }}>Messages you broadcast will appear here with delete controls.</div>
//               </div>
//             ) : (
//               <div style={{ borderTop: '1px solid #f1f5f9' }}>
//                 {msgHistory.filter(m => {
//                   if (!historySearch) return true;
//                   const q = historySearch.toLowerCase();
//                   return (m.title||'').toLowerCase().includes(q) || (m.body||'').toLowerCase().includes(q);
//                 }).map(m => (
//                   <div key={m.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '16px 24px', borderBottom: '1px solid #f1f5f9', transition: 'background 0.12s' }}
//                     onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
//                     onMouseLeave={e => e.currentTarget.style.background='white'}>
//                     <div style={{ width: 42, height: 42, borderRadius: 10, background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>📣</div>
//                     <div style={{ flex: 1, minWidth: 0 }}>
//                       <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>{m.title || '(No title)'}</div>
//                       <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.55, marginBottom: 7, whiteSpace: 'pre-wrap' }}>{(m.body||'').slice(0, 140)}{(m.body||'').length > 140 ? '…' : ''}</div>
//                       <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 11.5, color: '#64748b', fontWeight: 500 }}>
//                         <span>📅 {m.created_at ? new Date(m.created_at).toLocaleString('en-KE',{day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : '—'}</span>
//                         <span style={{ color: '#6366f1', fontWeight: 700 }}>👥 {m.sent_count} recipient{m.sent_count !== 1 ? 's' : ''}</span>
//                         {m.recipients_preview?.length > 0 && (
//                           <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>
//                             {m.recipients_preview.slice(0,2).join(', ')}{m.sent_count > 2 ? ` +${m.sent_count-2} more` : ''}
//                           </span>
//                         )}
//                       </div>
//                     </div>
//                     <button disabled={deletingMsgId === m.id} onClick={() => handleDeleteBroadcast(m.id, m.title)}
//                       title="Delete from all inboxes"
//                       style={{ background: 'none', border: 'none', cursor: deletingMsgId === m.id ? 'not-allowed' : 'pointer', fontSize: 18, padding: '6px 8px', borderRadius: 6, transition: 'background 0.12s', flexShrink: 0, opacity: deletingMsgId === m.id ? 0.5 : 1 }}
//                       onMouseEnter={e => e.currentTarget.style.background='#fee2e2'}
//                       onMouseLeave={e => e.currentTarget.style.background='transparent'}>
//                       {deletingMsgId === m.id ? '⏳' : '🗑️'}
//                     </button>
//                   </div>
//                 ))}
//               </div>
//             )}
//           </div>
//         )}

//         {/* ─────────── SCHEDULED tab ─────────── */}
//         {commTab === 'scheduled' && (
//           <div>
//             <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 16px', borderBottom: '1px solid #f1f5f9' }}>
//               <div>
//                 <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginBottom: 4 }}>🕐 Scheduled Messages</div>
//                 <div style={{ fontSize: 12, color: '#94a3b8' }}>Queue messages to send automatically at a future date and time</div>
//               </div>
//               <button onClick={() => setShowScheduleForm(s => !s)} style={{ padding: '8px 16px', background: '#fffbeb', color: '#d97706', border: '1.5px solid #fde68a', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
//                 {showScheduleForm ? '✕ Cancel' : '+ Schedule Message'}
//               </button>
//             </div>

//             {showScheduleForm && (
//               <div style={{ padding: '18px 24px', borderBottom: '1px solid #f1f5f9', background: '#fffbeb' }}>
//                 <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 18px', marginBottom: 14 }}>
//                   {[
//                     { label: 'Title (optional)', key: 'title', type: 'text', placeholder: 'e.g. Weekly Tip', col: 1 },
//                     { label: 'Send To', key: 'target', type: 'select', col: 1 },
//                     { label: 'Send At *', key: 'sendAt', type: 'datetime-local', col: 1 },
//                   ].map(f => (
//                     <div key={f.key} style={{ gridColumn: f.col === 'all' ? '1/-1' : undefined }}>
//                       <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>{f.label}</label>
//                       {f.type === 'select' ? (
//                         <select value={scheduleForm.target} onChange={e => setScheduleForm(p => ({...p, target: e.target.value}))}
//                           style={{ width: '100%', height: 36, padding: '0 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, background: '#fff', fontFamily: 'inherit' }}>
//                           <option value="all">All Users</option>
//                           <option value="new">New Users (last 7 days)</option>
//                           <option value="inactive">Inactive Users (30+ days)</option>
//                         </select>
//                       ) : (
//                         <input type={f.type} value={scheduleForm[f.key]} placeholder={f.placeholder||''}
//                           min={f.type==='datetime-local' ? new Date().toISOString().slice(0,16) : undefined}
//                           onChange={e => setScheduleForm(p => ({...p, [f.key]: e.target.value}))}
//                           style={{ width: '100%', height: 36, padding: '0 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, background: '#fff', fontFamily: 'inherit', boxSizing: 'border-box' }} />
//                       )}
//                     </div>
//                   ))}
//                   <div style={{ gridColumn: '1/-1' }}>
//                     <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>Message *</label>
//                     <textarea value={scheduleForm.message} onChange={e => setScheduleForm(p => ({...p, message: e.target.value}))}
//                       rows={3} placeholder="Message content…"
//                       style={{ width: '100%', padding: '9px 11px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', outline: 'none', boxSizing: 'border-box' }} />
//                   </div>
//                 </div>
//                 <button onClick={handleAddScheduled} style={{ padding: '9px 22px', background: '#d97706', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>🕐 Add to Schedule</button>
//               </div>
//             )}

//             {scheduledMsgs.length === 0 ? (
//               <div style={{ padding: '52px 24px', textAlign: 'center', color: '#94a3b8' }}>
//                 <div style={{ fontSize: 44, marginBottom: 12 }}>🕐</div>
//                 <div style={{ fontSize: 14, fontWeight: 600, color: '#475569', marginBottom: 6 }}>No scheduled messages</div>
//                 <div style={{ fontSize: 13 }}>Click "+ Schedule Message" to queue one.</div>
//               </div>
//             ) : (
//               scheduledMsgs.map(m => (
//                 <div key={m.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '15px 24px', borderBottom: '1px solid #f1f5f9' }}>
//                   <div style={{ width: 40, height: 40, borderRadius: 10, background: '#fffbeb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>🕐</div>
//                   <div style={{ flex: 1, minWidth: 0 }}>
//                     <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 3 }}>{m.title||'(No title)'}</div>
//                     <div style={{ fontSize: 13, color: '#475569', marginBottom: 6 }}>{m.message.slice(0,80)}{m.message.length>80?'…':''}</div>
//                     <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 11.5, color: '#64748b', fontWeight: 500 }}>
//                       <span>📅 {new Date(m.sendAt).toLocaleString('en-KE',{day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'})}</span>
//                       <span>👥 {m.target==='all'?'All Users':m.target==='new'?'New Users':'Inactive Users'}</span>
//                       <span style={{ background: '#fef3c7', color: '#b45309', borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>{m.status}</span>
//                     </div>
//                   </div>
//                   <button onClick={() => handleDeleteScheduled(m.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: '6px 8px', borderRadius: 6 }}
//                     onMouseEnter={e => e.currentTarget.style.background='#fee2e2'}
//                     onMouseLeave={e => e.currentTarget.style.background='transparent'}>🗑️</button>
//                 </div>
//               ))
//             )}
//           </div>
//         )}
//       </div>

//       {/* ── Admin Management      </div>

//       {/* ── Admin Management ─────────────────────────────────────────────── */}
//       <div className="admin-management-section">
//         <div className="section-header">
//           <div className="admins-header-left">
//             <h2>Admin Management</h2>
//             {!adminsLoading && filteredAdmins.length > 5 && (
//               <span className="admins-count-badge">{filteredAdmins.length} admins</span>
//             )}
//           </div>
//           <div className="header-actions">
//             <div className="search-box-compact">
//               <span className="search-icon">🔍</span>
//               <input
//                 type="text"
//                 placeholder="Search admins…"
//                 value={adminSearchTerm}
//                 onChange={e => setAdminSearchTerm(e.target.value)}
//               />
//             </div>
//             <button className="btn-secondary" onClick={() => setShowAddModal(true)}>
//               + Add Admin
//             </button>
//           </div>
//         </div>

//         {error && (
//           <div style={{
//             padding: '12px 16px', marginBottom: 16,
//             background: '#fff5f5', border: '1px solid #feb2b2',
//             borderRadius: 8, color: '#c53030', fontSize: 14,
//           }}>
//             ⚠️ {error}
//             <button
//               onClick={loadAdmins}
//               style={{
//                 marginLeft: 12, color: '#3182ce', background: 'none',
//                 border: 'none', cursor: 'pointer', textDecoration: 'underline',
//               }}
//             >
//               Retry
//             </button>
//           </div>
//         )}

//         {adminsLoading ? (
//           <div className="loading-container">
//             <div className="spinner" />
//             <p>Loading admins…</p>
//           </div>
//         ) : (
//           <>
//             <div className="admins-table-wrapper">
//               {/* Sticky header — sits outside scroll zone */}
//               <table className="admins-table admins-table-head">
//                 <thead>
//                   <tr>
//                     <th style={{ width: '20%' }}>ADMIN</th>
//                     <th style={{ width: '18%' }}>EMAIL</th>
//                     <th style={{ width: '11%' }}>PHONE</th>
//                     <th style={{ width: '10%' }}>ROLE</th>
//                     <th style={{ width: '9%' }}>STATUS</th>
//                     <th style={{ width: '12%' }}>VERIFICATION</th>
//                     <th style={{ width: '12%' }}>JOINED</th>
//                     <th style={{ width: '8%' }} className="th-actions">ACTIONS</th>
//                   </tr>
//                 </thead>
//               </table>

//               {/* Scrollable body — ~5 rows visible */}
//               <div className="admins-scroll-body">
//                 <table className="admins-table admins-table-body">
//                   <colgroup>
//                     <col style={{ width: '20%' }} />
//                     <col style={{ width: '18%' }} />
//                     <col style={{ width: '11%' }} />
//                     <col style={{ width: '10%' }} />
//                     <col style={{ width: '9%' }} />
//                     <col style={{ width: '12%' }} />
//                     <col style={{ width: '12%' }} />
//                     <col style={{ width: '8%' }} />
//                   </colgroup>
//                   <tbody>
//                     {filteredAdmins.length === 0 ? (
//                       <tr>
//                         <td colSpan="8" className="empty-cell">
//                           <div className="empty-state">
//                             <span style={{ fontSize: 40 }}>👥</span>
//                             <h3>No admins found</h3>
//                             <p>
//                               {adminSearchTerm
//                                 ? 'Try adjusting your search'
//                                 : 'Add your first admin above'}
//                             </p>
//                           </div>
//                         </td>
//                       </tr>
//                     ) : filteredAdmins.map(admin => {
//                       const role   = getRoleBadge(admin);
//                       const status = getStatusBadge(admin);
//                       return (
//                         <tr key={admin.id}>
//                           <td>
//                             <div className="admin-cell">
//                               <div className="admin-avatar">{getInitials(admin)}</div>
//                               <div className="admin-info">
//                                 <div className="admin-name">
//                                   {[admin.first_name, admin.last_name].filter(Boolean).join(' ') || admin.username}
//                                 </div>
//                                 <div className="admin-username">@{admin.username}</div>
//                               </div>
//                             </div>
//                           </td>
//                           <td className="email-cell">{admin.email || 'N/A'}</td>
//                           <td className="phone-cell">{admin.phone_number || admin.phone || 'N/A'}</td>
//                           <td>
//                             <span className={`role-badge role-${role.cls}`}>{role.label}</span>
//                           </td>
//                           <td>
//                             <span className={`status-badge status-${status.cls}`}>
//                               ● {status.label}
//                             </span>
//                           </td>
//                           <td>
//                             {(() => {
//                               const vs = admin.verification_status || 'unverified';
//                               const map = {
//                                 verified:              { label: '✅ Verified',    bg: '#d1fae5', color: '#065f46' },
//                                 pending:               { label: '⏳ Pending',     bg: '#fef3c7', color: '#b45309' },
//                                 pending_reverification:{ label: '🔄 Re-verify',  bg: '#fff7ed', color: '#c2410c' },
//                                 unverified:            { label: '✗ Unverified',  bg: '#fee2e2', color: '#dc2626' },
//                                 partial:               { label: '◑ Partial',     bg: '#eff6ff', color: '#1d4ed8' },
//                                 rejected:              { label: '❌ Rejected',   bg: '#fee2e2', color: '#dc2626' },
//                               };
//                               const badge = map[vs] || map.unverified;
//                               return (
//                                 <span style={{
//                                   fontSize: 11, fontWeight: 700, padding: '3px 10px',
//                                   borderRadius: 20, background: badge.bg, color: badge.color,
//                                   whiteSpace: 'nowrap',
//                                 }}>
//                                   {badge.label}
//                                 </span>
//                               );
//                             })()}
//                           </td>
//                           <td className="date-cell">
//                             {formatDate(admin.date_joined || admin.created_at)}
//                           </td>
//                           <td>
//                             <div className="actions-cell-dropdown">
//                               <button
//                                 className="action-menu-btn"
//                                 onClick={e => {
//                                   e.stopPropagation();
//                                   setActiveDropdown(activeDropdown === admin.id ? null : admin.id);
//                                 }}
//                               >
//                                 ⋮
//                               </button>
//                               {activeDropdown === admin.id && (
//                                 <div className="action-dropdown-menu">
//                                   <button onClick={() => openEditModal(admin)}>✏️ Edit</button>
//                                   <button onClick={() => handleVerifyAdmin(admin)}>
//                                     {admin.verification_status === 'verified' ? '🔄 Re-verify' : '🪪 Verify User'}
//                                   </button>
//                                   <button onClick={() => handleToggleStatus(admin)}>
//                                     {admin.is_active !== false ? '🔒 Suspend' : '✅ Activate'}
//                                   </button>
//                                   <button
//                                     onClick={() => handleRevokeAdmin(admin)}
//                                   >
//                                     🚫 Revoke Admin
//                                   </button>
//                                   <button
//                                     className="danger"
//                                     onClick={() => handleDeleteAdmin(admin)}
//                                   >
//                                     🗑️ Delete
//                                   </button>
//                                 </div>
//                               )}
//                             </div>
//                           </td>
//                         </tr>
//                       );
//                     })}
//                   </tbody>
//                 </table>
//               </div>
//             </div>

//             {filteredAdmins.length > 5 && (
//               <p className="admins-scroll-hint">
//                 ↕ Scroll to see all {filteredAdmins.length} admins
//               </p>
//             )}
//           </>
//         )}
//       </div>

//       {/* ── Add Admin Modal ──────────────────────────────────────────────── */}
//       {showAddModal && (
//         <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
//           <div className="modal-box" onClick={e => e.stopPropagation()}>
//             <div className="modal-header">
//               <h2>Add New Admin</h2>
//               <button className="modal-close-btn" onClick={() => setShowAddModal(false)}>×</button>
//             </div>
//             <form className="modal-form" onSubmit={handleAddAdmin}>
//               <div className="form-row-two">
//                 <div className="form-field">
//                   <label>First Name</label>
//                   <input
//                     type="text"
//                     value={newAdminData.first_name}
//                     onChange={e => setNewAdminData({ ...newAdminData, first_name: e.target.value })}
//                     placeholder="John"
//                   />
//                 </div>
//                 <div className="form-field">
//                   <label>Last Name</label>
//                   <input
//                     type="text"
//                     value={newAdminData.last_name}
//                     onChange={e => setNewAdminData({ ...newAdminData, last_name: e.target.value })}
//                     placeholder="Doe"
//                   />
//                 </div>
//               </div>
//               <div className="form-row-two">
//                 <div className="form-field">
//                   <label>Email *</label>
//                   <input
//                     type="email"
//                     required
//                     value={newAdminData.email}
//                     onChange={e => setNewAdminData({ ...newAdminData, email: e.target.value })}
//                     placeholder="admin@example.com"
//                   />
//                 </div>
//                 <div className="form-field">
//                   <label>Phone</label>
//                   <input
//                     type="tel"
//                     value={newAdminData.phone}
//                     onChange={e => setNewAdminData({ ...newAdminData, phone: e.target.value })}
//                     placeholder="+254712345678"
//                   />
//                 </div>
//               </div>
//               <div className="form-field">
//                 <label>Role</label>
//                 <select
//                   value={newAdminData.role}
//                   onChange={e => setNewAdminData({ ...newAdminData, role: e.target.value })}
//                 >
//                   <option value="admin">Admin</option>
//                   <option value="superadmin">Super Admin</option>
//                 </select>
//               </div>
//               <div className="modal-actions">
//                 <button type="button" className="btn-cancel" onClick={() => setShowAddModal(false)}>
//                   Cancel
//                 </button>
//                 <button type="submit" className="btn-submit" disabled={submitting}>
//                   {submitting ? 'Creating…' : 'Create Admin'}
//                 </button>
//               </div>
//             </form>
//           </div>
//         </div>
//       )}

//       {/* ── Edit Admin Modal ─────────────────────────────────────────────── */}
//       {showEditModal && selectedAdmin && (
//         <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
//           <div className="modal-box" onClick={e => e.stopPropagation()}>
//             <div className="modal-header">
//               <h2>Edit Admin</h2>
//               <button className="modal-close-btn" onClick={() => setShowEditModal(false)}>×</button>
//             </div>
//             <form className="modal-form" onSubmit={handleEditAdmin}>
//               <div className="form-row-two">
//                 <div className="form-field">
//                   <label>First Name</label>
//                   <input
//                     type="text"
//                     value={editAdminData.first_name}
//                     onChange={e => setEditAdminData({ ...editAdminData, first_name: e.target.value })}
//                   />
//                 </div>
//                 <div className="form-field">
//                   <label>Last Name</label>
//                   <input
//                     type="text"
//                     value={editAdminData.last_name}
//                     onChange={e => setEditAdminData({ ...editAdminData, last_name: e.target.value })}
//                   />
//                 </div>
//               </div>
//               <div className="form-row-two">
//                 <div className="form-field">
//                   <label>Email</label>
//                   <input
//                     type="email"
//                     value={editAdminData.email}
//                     onChange={e => setEditAdminData({ ...editAdminData, email: e.target.value })}
//                   />
//                 </div>
//                 <div className="form-field">
//                   <label>Phone</label>
//                   <input
//                     type="tel"
//                     value={editAdminData.phone}
//                     onChange={e => setEditAdminData({ ...editAdminData, phone: e.target.value })}
//                   />
//                 </div>
//               </div>
//               <div className="form-field">
//                 <label>Username</label>
//                 <input
//                   type="text"
//                   value={editAdminData.username}
//                   disabled
//                   style={{ opacity: 0.6, cursor: 'not-allowed' }}
//                 />
//               </div>
//               <div className="form-checkbox">
//                 <input
//                   type="checkbox"
//                   id="edit_superuser"
//                   checked={editAdminData.is_superuser}
//                   onChange={e => setEditAdminData({ ...editAdminData, is_superuser: e.target.checked })}
//                 />
//                 <label htmlFor="edit_superuser">Super Admin (Full Access)</label>
//               </div>
//               <div className="form-checkbox">
//                 <input
//                   type="checkbox"
//                   id="edit_active"
//                   checked={editAdminData.is_active}
//                   onChange={e => setEditAdminData({ ...editAdminData, is_active: e.target.checked })}
//                 />
//                 <label htmlFor="edit_active">Active Account</label>
//               </div>
//               <div className="modal-actions">
//                 <button type="button" className="btn-cancel" onClick={() => setShowEditModal(false)}>
//                   Cancel
//                 </button>
//                 <button type="submit" className="btn-submit" disabled={submitting}>
//                   {submitting ? 'Saving…' : 'Save Changes'}
//                 </button>
//               </div>
//             </form>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }

// export default SuperAdminView;
























// import React, { useState, useEffect } from 'react';
// import './SuperAdminView.css';
// import TransactionsTable from './TransactionsTable';

// const API_BASE_URL = 'https://imselling-marketplace-production.up.railway.app/api';

// // ============================================================================
// // SAFE TOKEN HELPER — never crashes if localStorage is unavailable
// // ============================================================================
// const getToken = () => {
//   try { return localStorage.getItem('token') || ''; }
//   catch { return ''; }
// };

// // ============================================================================
// // INLINE API HELPERS
// // ============================================================================
// const api = {
//   headers: () => ({
//     'Authorization': `Token ${getToken()}`,
//     'Content-Type': 'application/json',
//   }),

//   async fetchAdmins() {
//     const res = await fetch(`${API_BASE_URL}/admin/admins/`, { headers: this.headers() });
//     if (!res.ok) throw new Error(`fetchAdmins: ${res.status}`);
//     const data = await res.json();
//     return Array.isArray(data) ? data : (data.results || []);
//   },

//   async createAdmin(payload) {
//     const res = await fetch(`${API_BASE_URL}/admin/admins/`, {
//       method: 'POST',
//       headers: this.headers(),
//       body: JSON.stringify(payload),
//     });
//     return res.json();
//   },

//   async updateAdmin(id, payload) {
//     const res = await fetch(`${API_BASE_URL}/admin/users/${id}/`, {
//       method: 'PATCH',
//       headers: this.headers(),
//       body: JSON.stringify(payload),
//     });
//     return res.json();
//   },

//   async toggleAdminStatus(id) {
//     const res = await fetch(`${API_BASE_URL}/admin/users/${id}/toggle-status/`, {
//       method: 'POST',
//       headers: this.headers(),
//     });
//     return res.json();
//   },

//   async removeAdminRights(id) {
//     const res = await fetch(`${API_BASE_URL}/admin/admins/${id}/`, {
//       method: 'DELETE',
//       headers: this.headers(),
//     });
//     if (res.status === 204) return { success: true };
//     return res.json();
//   },

//   async fetchActivityLogs() {
//     try {
//       const res = await fetch(`${API_BASE_URL}/admin/dashboard/activity/`, {
//         headers: this.headers(),
//       });
//       if (!res.ok) return [];
//       const data = await res.json();
//       return Array.isArray(data) ? data : [];
//     } catch {
//       return [];
//     }
//   },
// };

// // ============================================================================
// // EMPTY FORM STATES
// // ============================================================================
// const EMPTY_NEW_ADMIN = {
//   email: '', phone: '', first_name: '', last_name: '',
//   password: '', role: 'admin',
// };

// const EMPTY_EDIT_ADMIN = {
//   first_name: '', last_name: '', email: '', phone: '',
//   username: '', is_superuser: false, is_active: true,
// };

// // ============================================================================
// // SYSTEM CONFIG TABS — 6 tabs with full panel content
// // ============================================================================
// const CONFIG_TABS = [
//   {
//     id: 'security',
//     icon: '🔐',
//     label: 'Security',
//     title: 'Security Settings',
//     subtitle: 'Manage authentication, 2FA, session control and access policies',
//     color: '#6366f1',
//     lightColor: '#eef2ff',
//     fields: [
//       { label: 'Two-Factor Authentication', type: 'toggle', value: true,  hint: 'Require 2FA for all admin logins' },
//       { label: 'Session Timeout (minutes)', type: 'number', value: 30,    hint: 'Auto-logout after inactivity' },
//       { label: 'Max Login Attempts',        type: 'number', value: 5,     hint: 'Lockout after failed attempts' },
//       { label: 'Password Expiry (days)',    type: 'number', value: 90,    hint: 'Force password reset period' },
//       { label: 'IP Whitelist',              type: 'text',   value: '',    hint: 'Comma-separated allowed IPs' },
//       { label: 'Audit Log Retention (days)',type: 'number', value: 365,   hint: 'How long to keep activity logs' },
//     ],
//   },
//   {
//     id: 'payment',
//     icon: '💳',
//     label: 'Payment',
//     title: 'Payment Gateway',
//     subtitle: 'API keys, commission rates, payout schedules and currency settings',
//     color: '#059669',
//     lightColor: '#ecfdf5',
//     fields: [
//       { label: 'M-Pesa API Key',         type: 'password', value: '',    hint: 'Safaricom Daraja API key' },
//       { label: 'M-Pesa Shortcode',       type: 'text',     value: '',    hint: 'Business short code' },
//       { label: 'Platform Commission (%)', type: 'number',  value: 5,     hint: 'Percentage taken per transaction' },
//       { label: 'Minimum Withdrawal',     type: 'number',   value: 100,   hint: 'Minimum KES for payout request' },
//       { label: 'Payout Schedule',        type: 'select',   value: 'weekly', options: ['daily','weekly','monthly'], hint: 'How often payouts are processed' },
//       { label: 'Auto-approve Payouts',   type: 'toggle',   value: false, hint: 'Skip manual review for payouts' },
//     ],
//   },
//   {
//     id: 'email',
//     icon: '📧',
//     label: 'Email & SMS',
//     title: 'Email & SMS',
//     subtitle: 'Configure SMTP, SMS providers and notification templates',
//     color: '#0ea5e9',
//     lightColor: '#f0f9ff',
//     fields: [
//       { label: 'SMTP Host',           type: 'text',     value: 'smtp.gmail.com', hint: 'Outgoing mail server' },
//       { label: 'SMTP Port',           type: 'number',   value: 587,              hint: '587 for TLS, 465 for SSL' },
//       { label: 'SMTP Username',       type: 'text',     value: '',               hint: 'Email account username' },
//       { label: 'SMTP Password',       type: 'password', value: '',               hint: 'Email account password' },
//       { label: 'SMS Provider',        type: 'select',   value: 'africas_talking', options: ['africas_talking','twilio','nexmo'], hint: 'Bulk SMS gateway provider' },
//       { label: 'SMS API Key',         type: 'password', value: '',               hint: 'Provider API key' },
//     ],
//   },
//   {
//     id: 'backup',
//     icon: '🗄️',
//     label: 'Backup',
//     title: 'Database Backup',
//     subtitle: 'Automated backup schedules, retention policies and restore options',
//     color: '#d97706',
//     lightColor: '#fffbeb',
//     fields: [
//       { label: 'Auto Backup',               type: 'toggle', value: true,    hint: 'Enable scheduled backups' },
//       { label: 'Backup Frequency',          type: 'select', value: 'daily', options: ['hourly','daily','weekly'], hint: 'How often backups run' },
//       { label: 'Retain Backups (days)',      type: 'number', value: 30,      hint: 'Delete backups older than this' },
//       { label: 'Backup Storage Path',       type: 'text',   value: '/backups', hint: 'Server directory for backups' },
//       { label: 'Notify on Failure',         type: 'toggle', value: true,    hint: 'Send email alert if backup fails' },
//       { label: 'Compress Backups',          type: 'toggle', value: true,    hint: 'GZIP compression to save space' },
//     ],
//   },
//   {
//     id: 'premium',
//     icon: '👑',
//     label: 'Premium',
//     title: 'Premium Plans & Offers',
//     subtitle: 'Subscription tiers, featured listing packages and promotional deals',
//     color: '#7c3aed',
//     lightColor: '#f5f3ff',
//     // Premium uses a custom renderer — planCards + extras
//     planCards: [
//       {
//         key: 'basic',
//         title: 'Basic Ad Posting Rule',
//         subtitle: 'Base configuration for this ad tier',
//         accent: '#7c3aed',
//         fields: [
//           { key: 'price',     label: 'Price (KES)',  value: 500  },
//           { key: 'maxImages', label: 'Max Images',   value: 2    },
//           { key: 'days',      label: 'Days',         value: 14   },
//         ],
//       },
//       {
//         key: 'pro',
//         title: 'Pro Ad Posting Rule',
//         subtitle: 'Professional level ad configuration',
//         accent: '#7c3aed',
//         fields: [
//           { key: 'price',     label: 'Price (KES)',  value: 1500 },
//           { key: 'maxImages', label: 'Max Images',   value: 5    },
//           { key: 'days',      label: 'Days',         value: 30   },
//         ],
//       },
//       {
//         key: 'featuredBasic',
//         title: 'Featured Listing Fee (Basic)',
//         subtitle: 'Cost to feature a single listing',
//         accent: '#7c3aed',
//         fields: [
//           { key: 'price',     label: 'Price (KES)',  value: 200  },
//           { key: 'maxImages', label: 'Max Images',   value: 10   },
//           { key: 'days',      label: 'Days',         value: 30   },
//         ],
//       },
//       {
//         key: 'featuredPro',
//         title: 'Featured Listing Fee (Pro)',
//         subtitle: 'Cost to feature a single listing',
//         accent: '#7c3aed',
//         fields: [
//           { key: 'price',     label: 'Price (KES)',  value: 200  },
//           { key: 'maxImages', label: 'Max Images',   value: 10   },
//           { key: 'days',      label: 'Days',         value: 30   },
//         ],
//       },
//     ],
//     extras: [
//       { key: 'freeTrial',   label: 'Free Trial Days',    type: 'number', value: 7,    hint: 'Days before billing starts' },
//       { key: 'promoCodes',  label: 'Promo Codes Enabled',type: 'toggle', value: true, hint: 'Allow discount code redemption' },
//       { key: 'maxPromos',   label: 'Max Active Promos',  type: 'number', value: 10,   hint: 'Simultaneous active promotions' },
//     ],
//     fields: [], // empty — premium uses planCards + extras instead
//   },
//   {
//     id: 'analytics',
//     icon: '📊',
//     label: 'Analytics',
//     title: 'Analytics & Reporting',
//     subtitle: 'Data retention, report schedules and export format configuration',
//     color: '#db2777',
//     lightColor: '#fdf2f8',
//     fields: [
//       { label: 'Data Retention (months)',    type: 'number', value: 24,      hint: 'How long raw analytics are kept' },
//       { label: 'Report Schedule',            type: 'select', value: 'weekly', options: ['daily','weekly','monthly'], hint: 'Auto-send report frequency' },
//       { label: 'Report Recipients',          type: 'text',   value: '',      hint: 'Comma-separated email addresses' },
//       { label: 'Export Format',              type: 'select', value: 'csv',   options: ['csv','xlsx','pdf'],          hint: 'Default download format' },
//       { label: 'Track User Behaviour',       type: 'toggle', value: true,    hint: 'Collect page-view analytics' },
//       { label: 'Anonymise Data',             type: 'toggle', value: false,   hint: 'Strip PII from analytics data' },
//     ],
//   },
// ];

// // ============================================================================
// // MAIN COMPONENT
// // ============================================================================
// function SuperAdminView() {
//   const [activityLogs, setActivityLogs]       = useState([]);
//   const [logsLoading, setLogsLoading]         = useState(true);

//   const [admins, setAdmins]                   = useState([]);
//   const [adminsLoading, setAdminsLoading]     = useState(true);
//   const [adminSearchTerm, setAdminSearchTerm] = useState('');

//   const [activeConfigTab, setActiveConfigTab] = useState('security');
//   const [editingTab, setEditingTab]           = useState(null);
//   const [configValues, setConfigValues]       = useState(() =>
//     Object.fromEntries(CONFIG_TABS.map(t => [t.id, Object.fromEntries(t.fields.map(f => [f.label, f.value]))]))
//   );
//   const [draftConfig, setDraftConfig]         = useState(null);
//   const [draftPremiumCards, setDraftPremiumCards]   = useState(null);
//   const [draftPremiumExtras, setDraftPremiumExtras] = useState(null);

//   const premiumTab = CONFIG_TABS.find(t => t.id === 'premium');
//   const [premiumCards, setPremiumCards] = useState(() =>
//     Object.fromEntries(premiumTab.planCards.map(c => [
//       c.key,
//       Object.fromEntries(c.fields.map(f => [f.key, f.value])),
//     ]))
//   );
//   const [premiumExtras, setPremiumExtras] = useState(() =>
//     Object.fromEntries(premiumTab.extras.map(e => [e.key, e.value]))
//   );

//   const [showAddModal, setShowAddModal]       = useState(false);
//   const [showEditModal, setShowEditModal]     = useState(false);
//   const [selectedAdmin, setSelectedAdmin]     = useState(null);
//   const [activeDropdown, setActiveDropdown]   = useState(null);

//   const [newAdminData, setNewAdminData]       = useState(EMPTY_NEW_ADMIN);
//   const [editAdminData, setEditAdminData]     = useState(EMPTY_EDIT_ADMIN);
//   const [submitting, setSubmitting]           = useState(false);
//   const [error, setError]                     = useState(null);

//   // ── Communications Center state ─────────────────────────────────────────────
//   const [commTab, setCommTab]                 = useState('welcome');   // 'welcome' | 'broadcast' | 'history' | 'scheduled'
//   // Welcome message editor
//   const [welcomeMsg, setWelcomeMsg]           = useState(
//     "👋 Welcome to I'm Selling! Kenya's fastest-growing marketplace.\n\nBuy and sell anything easily — from electronics to property.\n\n✅ Post your first ad today and reach thousands of buyers!"
//   );
//   const [welcomeEditing, setWelcomeEditing]   = useState(false);
//   const [welcomeDraft, setWelcomeDraft]       = useState('');
//   const [welcomeSaving, setWelcomeSaving]     = useState(false);
//   // Broadcast message composer
//   const [broadcastTarget, setBroadcastTarget] = useState('all');
//   const [broadcastMsg, setBroadcastMsg]       = useState('');
//   const [broadcastTitle, setBroadcastTitle]   = useState('');
//   const [userSearch, setUserSearch]           = useState('');
//   const [allUsers, setAllUsers]               = useState([]);
//   const [selectedUsers, setSelectedUsers]     = useState([]);
//   const [usersLoading, setUsersLoading]       = useState(false);
//   const [broadcasting, setBroadcasting]       = useState(false);
//   const [broadcastResult, setBroadcastResult] = useState(null);
//   // Message history
//   const [msgHistory, setMsgHistory]           = useState([]);
//   const [historyLoading, setHistoryLoading]   = useState(false);
//   const [historySearch, setHistorySearch]     = useState('');
//   const [deletingMsgId, setDeletingMsgId]     = useState(null);
//   // Scheduled messages list
//   const [scheduledMsgs, setScheduledMsgs]     = useState([]);
//   const [scheduleForm, setScheduleForm]       = useState({ title: '', message: '', target: 'all', sendAt: '' });
//   const [showScheduleForm, setShowScheduleForm] = useState(false);

//   // --------------------------------------------------------------------------
//   useEffect(() => {
//     loadLogs();
//     loadAdmins();
//     loadHistory(); // ✅ Pre-fetch history so it's ready when user clicks the tab
//   }, []);

//   useEffect(() => {
//     const close = () => setActiveDropdown(null);
//     document.addEventListener('click', close);
//     return () => document.removeEventListener('click', close);
//   }, []);

//   // --------------------------------------------------------------------------
//   const loadLogs = async () => {
//     setLogsLoading(true);
//     try {
//       const logs = await api.fetchActivityLogs();
//       setActivityLogs(logs);
//     } catch (err) {
//       console.warn('⚠️ Logs fetch failed:', err.message);
//       setActivityLogs([]);
//     } finally {
//       setLogsLoading(false);
//     }
//   };

//   const loadAdmins = async () => {
//     setAdminsLoading(true);
//     setError(null);
//     try {
//       const data = await api.fetchAdmins();
//       setAdmins(data);
//     } catch (err) {
//       console.warn('⚠️ Admins fetch failed:', err.message);
//       setAdmins([]);
//       setError('Could not load admins. Check your connection or permissions.');
//     } finally {
//       setAdminsLoading(false);
//     }
//   };

//   // --------------------------------------------------------------------------
//   const filteredAdmins = admins.filter(a =>
//     [a.username, a.email, a.first_name, a.last_name]
//       .some(v => v?.toLowerCase().includes(adminSearchTerm.toLowerCase()))
//   );

//   const formatDate = (val) => {
//     if (!val) return 'N/A';
//     try {
//       return new Date(val).toLocaleString('en-US', {
//         year: 'numeric', month: '2-digit', day: '2-digit',
//         hour: '2-digit', minute: '2-digit', hour12: true,
//       });
//     } catch { return 'N/A'; }
//   };

//   const getInitials = (a) => {
//     if (a.first_name && a.last_name) return `${a.first_name[0]}${a.last_name[0]}`.toUpperCase();
//     if (a.username) return a.username.substring(0, 2).toUpperCase();
//     return 'A';
//   };

//   const getRoleBadge = (a) =>
//     a.is_superuser
//       ? { label: 'Super Admin', cls: 'super-admin' }
//       : { label: 'Admin', cls: 'admin' };

//   const getStatusBadge = (a) =>
//     a.is_active !== false
//       ? { label: 'Active', cls: 'active' }
//       : { label: 'Suspended', cls: 'suspended' };

//   const getLogBadgeClass = (action = '') => {
//     const l = action.toLowerCase();
//     if (l.includes('approved') || l.includes('activated')) return 'active';
//     if (l.includes('suspended') || l.includes('deleted'))  return 'inactive';
//     if (l.includes('created')  || l.includes('added'))     return 'new';
//     if (l.includes('updated')  || l.includes('modified'))  return 'updated';
//     return 'default';
//   };

//   // --------------------------------------------------------------------------
//   const handleAddAdmin = async (e) => {
//     e.preventDefault();
//     setSubmitting(true);
//     try {
//       const res = await api.createAdmin(newAdminData);
//       if (res.success !== false) {
//         alert('Admin created successfully!');
//         setShowAddModal(false);
//         setNewAdminData(EMPTY_NEW_ADMIN);
//         loadAdmins();
//       } else {
//         alert(res.error || 'Failed to create admin');
//       }
//     } catch (err) {
//       console.error('❌ createAdmin:', err);
//       alert('Failed to create admin');
//     } finally {
//       setSubmitting(false);
//     }
//   };

//   const openEditModal = (admin) => {
//     setSelectedAdmin(admin);
//     setEditAdminData({
//       first_name:   admin.first_name   || '',
//       last_name:    admin.last_name    || '',
//       email:        admin.email        || '',
//       phone:        admin.phone_number || admin.phone || '',
//       username:     admin.username     || '',
//       is_superuser: admin.is_superuser || false,
//       is_active:    admin.is_active    !== false,
//     });
//     setShowEditModal(true);
//     setActiveDropdown(null);
//   };

//   const handleEditAdmin = async (e) => {
//     e.preventDefault();
//     setSubmitting(true);
//     try {
//       const res = await api.updateAdmin(selectedAdmin.id, editAdminData);
//       if (res.success !== false) {
//         alert('Admin updated successfully!');
//         setShowEditModal(false);
//         setSelectedAdmin(null);
//         loadAdmins();
//       } else {
//         alert(res.error || 'Failed to update admin');
//       }
//     } catch (err) {
//       console.error('❌ updateAdmin:', err);
//       alert('Failed to update admin');
//     } finally {
//       setSubmitting(false);
//     }
//   };

//   const handleToggleStatus = async (admin) => {
//     const action = admin.is_active !== false ? 'suspend' : 'activate';
//     if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} ${admin.username}?`)) return;
//     try {
//       const res = await api.toggleAdminStatus(admin.id);
//       if (res.success !== false) {
//         setAdmins(prev => prev.map(a =>
//           a.id === admin.id ? { ...a, is_active: !a.is_active } : a
//         ));
//       } else {
//         alert(res.error || `Failed to ${action} admin`);
//       }
//     } catch (err) {
//       console.error('❌ toggleStatus:', err);
//       alert(`Failed to ${action} admin`);
//     }
//     setActiveDropdown(null);
//   };

//   const handleRevokeAdmin = async (admin) => {
//     if (!window.confirm(`Revoke admin rights for ${admin.username}?`)) return;
//     try {
//       const res = await api.removeAdminRights(admin.id);
//       if (res.success !== false) {
//         alert('Admin rights revoked.');
//         loadAdmins();
//       } else {
//         alert(res.error || 'Failed to revoke admin rights');
//       }
//     } catch (err) {
//       console.error('❌ revokeAdmin:', err);
//       alert('Failed to revoke admin rights');
//     }
//     setActiveDropdown(null);
//   };

//   // --------------------------------------------------------------------------
//   // CONFIG EDIT MODE HANDLERS
//   // --------------------------------------------------------------------------
//   const handleConfigEdit = (tabId) => {
//     // Snapshot current values into draft
//     setDraftConfig({ ...configValues });
//     setDraftPremiumCards(JSON.parse(JSON.stringify(premiumCards)));
//     setDraftPremiumExtras({ ...premiumExtras });
//     setEditingTab(tabId);
//   };

//   const handleConfigSave = async (tab) => {
//     // Commit drafts to saved state
//     if (tab.id === 'premium') {
//       setPremiumCards(draftPremiumCards);
//       setPremiumExtras(draftPremiumExtras);
//       // TODO: await api.saveSystemConfig('premium', { cards: draftPremiumCards, extras: draftPremiumExtras });
//     } else {
//       setConfigValues(draftConfig);
//       // TODO: await api.saveSystemConfig(tab.id, draftConfig[tab.id]);
//     }
//     alert(`${tab.title} settings saved!`);
//     setEditingTab(null);
//     setDraftConfig(null);
//     setDraftPremiumCards(null);
//     setDraftPremiumExtras(null);
//   };

//   const handleConfigCancel = () => {
//     setEditingTab(null);
//     setDraftConfig(null);
//     setDraftPremiumCards(null);
//     setDraftPremiumExtras(null);
//   };

//   // ── Communications Center handlers ──────────────────────────────────────────
//   const handleWelcomeSave = async () => {
//     setWelcomeSaving(true);
//     try {
//       await fetch(`${API_BASE_URL}/auth/admin/broadcast/`, {
//         method: 'POST',
//         headers: { 'Authorization': `Token ${getToken()}`, 'Content-Type': 'application/json' },
//         body: JSON.stringify({ title: "Welcome Message Update", message: welcomeDraft, target: 'all' }),
//       });
//       setWelcomeMsg(welcomeDraft);
//       setWelcomeEditing(false);
//       alert("✅ Welcome message updated! New users will see this when they sign up.");
//     } catch {
//       setWelcomeMsg(welcomeDraft);
//       setWelcomeEditing(false);
//       alert("✅ Welcome message saved locally.");
//     } finally {
//       setWelcomeSaving(false);
//     }
//   };

//   const loadAllUsers = async () => {
//     setUsersLoading(true);
//     try {
//       const res = await fetch(`${API_BASE_URL}/admin/users/?page_size=200`, {
//         headers: { 'Authorization': `Token ${getToken()}` },
//       });
//       if (res.ok) {
//         const data = await res.json();
//         setAllUsers(data.results || data || []);
//       }
//     } catch { setAllUsers([]); }
//     finally { setUsersLoading(false); }
//   };

//   useEffect(() => {
//     if (commTab === 'broadcast') loadAllUsers();
//     if (commTab === 'history')   loadHistory();
//   }, [commTab]);

//   const loadHistory = async () => {
//     setHistoryLoading(true);
//     try {
//       const res = await fetch(`${API_BASE_URL}/auth/admin/broadcast-history/`, {
//         headers: { 'Authorization': `Token ${getToken()}` },
//       });
//       const data = await res.json();
//       if (res.ok) {
//         setMsgHistory(data.results || []);
//       } else {
//         console.error('broadcast_history error:', data.error || data);
//         setMsgHistory([]);
//       }
//     } catch (e) {
//       console.error('broadcast_history network error:', e);
//       setMsgHistory([]);
//     } finally { setHistoryLoading(false); }
//   };

//   const handleDeleteBroadcast = async (msgId, title) => {
//     if (!window.confirm(
//       `Delete "${title || 'this broadcast'}" from ALL users' I'm Selling inbox?\n\nThis cannot be undone.`
//     )) return;
//     setDeletingMsgId(msgId);
//     try {
//       const res = await fetch(`${API_BASE_URL}/auth/admin/broadcast-history/${msgId}/`, {
//         method: 'DELETE',
//         headers: { 'Authorization': `Token ${getToken()}` },
//       });
//       if (res.ok) {
//         const data = await res.json();
//         setMsgHistory(prev => prev.filter(m => m.id !== msgId));
//         alert(`✅ Deleted from ${data.deleted} user inbox${data.deleted !== 1 ? 'es' : ''}.`);
//       } else {
//         alert('Failed to delete. Please try again.');
//       }
//     } catch {
//       alert('Network error. Please try again.');
//     } finally {
//       setDeletingMsgId(null);
//     }
//   };

//   const toggleUserSelect = (uid) => {
//     setSelectedUsers(prev =>
//       prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]
//     );
//   };

//   const handleBroadcast = async () => {
//     if (!broadcastMsg.trim()) { alert('Please enter a message.'); return; }
//     if (broadcastTarget === 'selected' && selectedUsers.length === 0) {
//       alert('Please select at least one user.'); return;
//     }
//     if (!window.confirm(
//       broadcastTarget === 'all'
//         ? 'Send this message to ALL users?'
//         : `Send this message to ${selectedUsers.length} selected user(s)?`
//     )) return;

//     setBroadcasting(true);
//     setBroadcastResult(null);
//     try {
//       const payload = {
//         title:   broadcastTitle || "Message from I'm Selling",
//         message: broadcastMsg,
//         target:  broadcastTarget,
//         ...(broadcastTarget === 'selected' ? { user_ids: selectedUsers } : {}),
//       };
//       const res = await fetch(`${API_BASE_URL}/auth/admin/broadcast/`, {
//         method: 'POST',
//         headers: { 'Authorization': `Token ${getToken()}`, 'Content-Type': 'application/json' },
//         body: JSON.stringify(payload),
//       });
//       const data = await res.json();
//       if (res.ok) {
//         setBroadcastResult({ success: true, sent: data.sent, failed: data.failed || 0 });
//         setBroadcastMsg(''); setBroadcastTitle(''); setSelectedUsers([]);
//         loadHistory();
//       } else {
//         setBroadcastResult({ success: false, error: data.error || 'Send failed.' });
//       }
//     } catch (e) {
//       setBroadcastResult({ success: false, error: 'Network error. Check connection.' });
//     } finally {
//       setBroadcasting(false);
//     }
//   };

//   const handleAddScheduled = () => {
//     if (!scheduleForm.message.trim() || !scheduleForm.sendAt) {
//       alert('Please fill in message and send date/time.'); return;
//     }
//     setScheduledMsgs(prev => [
//       ...prev,
//       { id: Date.now(), ...scheduleForm, status: 'scheduled', createdAt: new Date().toISOString() }
//     ]);
//     setScheduleForm({ title: '', message: '', target: 'all', sendAt: '' });
//     setShowScheduleForm(false);
//   };

//   const handleDeleteScheduled = (id) => {
//     if (!window.confirm('Delete this scheduled message?')) return;
//     setScheduledMsgs(prev => prev.filter(m => m.id !== id));
//   };

//   // --------------------------------------------------------------------------
//   return (
//     <div className="super-admin-view">

//       {/* ── System Configuration ─────────────────────────────────────────── */}
//       <div className="system-settings-section">
//         <div className="section-header">
//           <h2>System Configuration</h2>
//           <span className="config-badge">6 modules</span>
//         </div>

//         {/* ── Tab Nav Bar ── */}
//         <div className="config-tab-bar">
//           {CONFIG_TABS.map(tab => (
//             <button
//               key={tab.id}
//               className={`config-tab-btn ${activeConfigTab === tab.id ? 'active' : ''}`}
//               style={activeConfigTab === tab.id ? { '--tab-color': tab.color, '--tab-light': tab.lightColor } : {}}
//               onClick={() => setActiveConfigTab(tab.id)}
//             >
//               <span className="config-tab-icon">{tab.icon}</span>
//               <span className="config-tab-label">{tab.label}</span>
//               {activeConfigTab === tab.id && (
//                 <span className="config-tab-indicator" style={{ background: tab.color }} />
//               )}
//             </button>
//           ))}
//         </div>

//         {/* ── Tab Panel ── */}
//         {CONFIG_TABS.map(tab => {
//           if (tab.id !== activeConfigTab) return null;
//           const isEditing = editingTab === tab.id;

//           // Shared panel header with Edit / Editing / Save / Cancel buttons
//           const PanelHeader = () => (
//             <div className="config-panel-header" style={{ borderLeftColor: tab.color }}>
//               <div className="config-panel-title-row">
//                 <div className="config-panel-icon" style={{ background: tab.lightColor, color: tab.color }}>
//                   {tab.icon}
//                 </div>
//                 <div>
//                   <h3 className="config-panel-title">{tab.title}</h3>
//                   <p className="config-panel-subtitle">{tab.subtitle}</p>
//                 </div>
//               </div>
//               <div className="config-header-actions">
//                 {!isEditing ? (
//                   /* ── View mode: only Edit button ── */
//                   <button
//                     className="config-edit-btn"
//                     style={{ '--edit-color': tab.color }}
//                     onClick={() => handleConfigEdit(tab.id)}
//                   >
//                     ✏️ Edit
//                   </button>
//                 ) : (
//                   /* ── Edit mode: Cancel + Save ── */
//                   <>
//                     <button
//                       className="config-cancel-btn"
//                       onClick={handleConfigCancel}
//                     >
//                       Cancel
//                     </button>
//                     <button
//                       className="config-save-btn"
//                       style={{ background: tab.color }}
//                       onClick={() => handleConfigSave(tab)}
//                     >
//                       Save Changes
//                     </button>
//                   </>
//                 )}
//               </div>
//             </div>
//           );

//           // ── PREMIUM custom renderer ──────────────────────────────────────
//           if (tab.id === 'premium') {
//             const cards  = isEditing ? draftPremiumCards  : premiumCards;
//             const extras = isEditing ? draftPremiumExtras : premiumExtras;

//             return (
//               <div key="premium" className={`config-panel ${isEditing ? 'editing' : ''}`}>
//                 <PanelHeader />

//                 {/* Edit mode banner */}
//                 {isEditing && (
//                   <div className="config-editing-banner" style={{ background: `${tab.color}12`, borderColor: `${tab.color}40` }}>
//                     <span style={{ color: tab.color }}>✏️</span>
//                     <span>You are now editing — make your changes and click <strong>Save Changes</strong> to apply.</span>
//                   </div>
//                 )}

//                 {/* Plan Cards — 2-column grid */}
//                 <div className="premium-plan-grid">
//                   {tab.planCards.map(card => (
//                     <div key={card.key} className={`premium-plan-card ${isEditing ? 'editable' : ''}`}>
//                       <div className="premium-plan-card-header">
//                         <div>
//                           <p className="premium-plan-card-title">{card.title}</p>
//                           <p className="premium-plan-card-sub">{card.subtitle}</p>
//                         </div>
//                       </div>
//                       <div className="premium-plan-fields">
//                         {card.fields.map(f => (
//                           <div key={f.key} className="premium-plan-field">
//                             <label className="premium-plan-field-label">{f.label}</label>
//                             <input
//                               className="premium-plan-input"
//                               type="number"
//                               value={cards[card.key][f.key]}
//                               readOnly={!isEditing}
//                               style={isEditing ? { '--focus-color': tab.color } : {}}
//                               onChange={e =>
//                                 isEditing && setDraftPremiumCards(prev => ({
//                                   ...prev,
//                                   [card.key]: { ...prev[card.key], [f.key]: e.target.value },
//                                 }))
//                               }
//                             />
//                           </div>
//                         ))}
//                       </div>
//                     </div>
//                   ))}
//                 </div>

//                 {/* Extras row — 3 columns */}
//                 <div className="premium-extras-grid">
//                   {tab.extras.map(extra => (
//                     <div key={extra.key} className={`config-field ${isEditing ? 'editable' : ''}`}>
//                       <label className="config-field-label">{extra.label}</label>
//                       <p className="config-field-hint">{extra.hint}</p>

//                       {extra.type === 'toggle' && (
//                         <button
//                           className={`config-toggle ${extras[extra.key] ? 'on' : 'off'} ${!isEditing ? 'locked' : ''}`}
//                           style={extras[extra.key] ? { '--toggle-color': tab.color } : {}}
//                           onClick={() =>
//                             isEditing && setDraftPremiumExtras(prev => ({ ...prev, [extra.key]: !prev[extra.key] }))
//                           }
//                         >
//                           <span className="config-toggle-thumb" />
//                           <span className="config-toggle-text">
//                             {extras[extra.key] ? 'Enabled' : 'Disabled'}
//                           </span>
//                         </button>
//                       )}

//                       {extra.type === 'number' && (
//                         <input
//                           className="config-input"
//                           type="number"
//                           value={extras[extra.key]}
//                           readOnly={!isEditing}
//                           style={isEditing ? { '--focus-color': tab.color } : {}}
//                           onChange={e =>
//                             isEditing && setDraftPremiumExtras(prev => ({ ...prev, [extra.key]: e.target.value }))
//                           }
//                         />
//                       )}
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             );
//           }

//           // ── DEFAULT renderer (all other tabs) ────────────────────────────
//           const values = isEditing ? draftConfig : configValues;

//           return (
//             <div key={tab.id} className={`config-panel ${isEditing ? 'editing' : ''}`}>
//               <PanelHeader />

//               {/* Edit mode banner */}
//               {isEditing && (
//                 <div className="config-editing-banner" style={{ background: `${tab.color}12`, borderColor: `${tab.color}40` }}>
//                   <span style={{ color: tab.color }}>✏️</span>
//                   <span>You are now editing — make your changes and click <strong>Save Changes</strong> to apply.</span>
//                 </div>
//               )}

//               {/* Fields grid */}
//               <div className="config-fields-grid">
//                 {tab.fields.map(field => (
//                   <div key={field.label} className={`config-field ${isEditing ? 'editable' : ''}`}>
//                     <label className="config-field-label">{field.label}</label>
//                     <p className="config-field-hint">{field.hint}</p>

//                     {field.type === 'toggle' && (
//                       <button
//                         className={`config-toggle ${values[tab.id][field.label] ? 'on' : 'off'} ${!isEditing ? 'locked' : ''}`}
//                         style={values[tab.id][field.label] ? { '--toggle-color': tab.color } : {}}
//                         onClick={() =>
//                           isEditing && setDraftConfig(prev => ({
//                             ...prev,
//                             [tab.id]: { ...prev[tab.id], [field.label]: !prev[tab.id][field.label] },
//                           }))
//                         }
//                       >
//                         <span className="config-toggle-thumb" />
//                         <span className="config-toggle-text">
//                           {values[tab.id][field.label] ? 'Enabled' : 'Disabled'}
//                         </span>
//                       </button>
//                     )}

//                     {field.type === 'select' && (
//                       <select
//                         className="config-input"
//                         value={values[tab.id][field.label]}
//                         disabled={!isEditing}
//                         style={isEditing ? { '--focus-color': tab.color } : {}}
//                         onChange={e =>
//                           isEditing && setDraftConfig(prev => ({
//                             ...prev,
//                             [tab.id]: { ...prev[tab.id], [field.label]: e.target.value },
//                           }))
//                         }
//                       >
//                         {field.options.map(o => (
//                           <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>
//                         ))}
//                       </select>
//                     )}

//                     {(field.type === 'text' || field.type === 'number' || field.type === 'password') && (
//                       <input
//                         className="config-input"
//                         type={field.type}
//                         value={values[tab.id][field.label]}
//                         readOnly={!isEditing}
//                         placeholder={field.type === 'password' ? '••••••••••••' : ''}
//                         style={isEditing ? { '--focus-color': tab.color } : {}}
//                         onChange={e =>
//                           isEditing && setDraftConfig(prev => ({
//                             ...prev,
//                             [tab.id]: { ...prev[tab.id], [field.label]: e.target.value },
//                           }))
//                         }
//                       />
//                     )}
//                   </div>
//                 ))}
//               </div>
//             </div>
//           );
//         })}
//       </div>

//       {/* ── Activity Logs ────────────────────────────────────────────────── */}
//       <div className="activity-logs-section">
//         <div className="section-header">
//           <div className="logs-header-left">
//             <h2>Admin Activity Logs</h2>
//             {!logsLoading && activityLogs.length > 5 && (
//               <span className="logs-count-badge">{activityLogs.length} entries</span>
//             )}
//           </div>
//           <button className="btn-secondary" onClick={() => alert('Export coming soon!')}>
//             ⬇️ Export Logs
//           </button>
//         </div>

//         {logsLoading ? (
//           <div className="loading-container">
//             <div className="spinner" />
//             <p>Loading activity logs…</p>
//           </div>
//         ) : (
//           <>
//             <div className="logs-table-wrapper">
//               {/* Sticky header — sits outside scroll zone */}
//               <table className="logs-table logs-table-head">
//                 <thead>
//                   <tr>
//                     <th style={{ width: '25%' }}>ADMIN</th>
//                     <th style={{ width: '18%' }}>ACTION</th>
//                     <th style={{ width: '25%' }}>RESOURCE</th>
//                     <th style={{ width: '12%' }}>IP ADDRESS</th>
//                     <th style={{ width: '20%' }}>TIMESTAMP</th>
//                   </tr>
//                 </thead>
//               </table>

//               {/* Scrollable body — ~5 rows visible */}
//               <div className="logs-scroll-body">
//                 <table className="logs-table logs-table-body">
//                   <colgroup>
//                     <col style={{ width: '25%' }} />
//                     <col style={{ width: '18%' }} />
//                     <col style={{ width: '25%' }} />
//                     <col style={{ width: '12%' }} />
//                     <col style={{ width: '20%' }} />
//                   </colgroup>
//                   <tbody>
//                     {activityLogs.length === 0 ? (
//                       <tr>
//                         <td colSpan="5" className="empty-cell">
//                           <div className="empty-state">
//                             <span style={{ fontSize: 40 }}>📋</span>
//                             <h3>No activity logs</h3>
//                             <p>Admin actions will appear here</p>
//                           </div>
//                         </td>
//                       </tr>
//                     ) : activityLogs.map((log, i) => (
//                       <tr key={log.id ?? i}>
//                         <td>
//                           <div className="admin-cell">
//                             <div className="admin-avatar">
//                               {(log.admin_email || log.user || 'A').charAt(0).toUpperCase()}
//                             </div>
//                             <div className="admin-info">
//                               <div className="admin-email">
//                                 {log.admin_email || log.user || 'Unknown'}
//                               </div>
//                               <div className="admin-role">{log.admin_role || 'Admin'}</div>
//                             </div>
//                           </div>
//                         </td>
//                         <td>
//                           <span className={`status-badge ${getLogBadgeClass(log.action || log.type)}`}>
//                             {log.action || log.title || '—'}
//                           </span>
//                         </td>
//                         <td className="resource-cell">
//                           {log.resource_type && (
//                             <span className="resource-type">{log.resource_type}</span>
//                           )}
//                           {log.resource_name || log.description || log.resource_id || 'N/A'}
//                         </td>
//                         <td className="ip-cell">{log.ip_address || 'N/A'}</td>
//                         <td className="timestamp-cell">
//                           {formatDate(log.created_at || log.timestamp)}
//                         </td>
//                       </tr>
//                     ))}
//                   </tbody>
//                 </table>
//               </div>
//             </div>

//             {activityLogs.length > 5 && (
//               <p className="logs-scroll-hint">
//                 ↕ Scroll to see all {activityLogs.length} entries
//               </p>
//             )}
//           </>
//         )}
//       </div>

//       {/* ════════════════════════════════════════════════════════════════════
//           TRANSACTION LEDGER
//           ════════════════════════════════════════════════════════════════════ */}
//       <div style={{
//         background: '#fff', borderRadius: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
//         marginBottom: 28, padding: '24px 28px', border: '1px solid #e2e8f0',
//       }}>
//         <TransactionsTable />
//       </div>

//       {/* ════════════════════════════════════════════════════════════════════
//           COMMUNICATIONS CENTER — redesigned with UserCommunications style
//           ════════════════════════════════════════════════════════════════════ */}
//       <div style={{
//         background: '#fff', borderRadius: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
//         marginBottom: 28, overflow: 'hidden', border: '1px solid #e2e8f0',
//       }}>
//         {/* ── Header ── */}
//         <div style={{
//           padding: '20px 28px 0', borderBottom: '1px solid #f1f5f9',
//         }}>
//           <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
//             <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>📡 Communications Center</h2>
//             <span style={{ background: '#eef2ff', color: '#6366f1', borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 700 }}>4 tools</span>
//           </div>
//           <p style={{ margin: '0 0 16px', fontSize: 12, color: '#94a3b8' }}>
//             Manage welcome messages · Broadcast to users via I'm Selling inbox · View & delete sent messages
//           </p>
//           {/* Stats row */}
//           <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
//             {[
//               { label: 'Total Sent',  value: msgHistory.length,                          color: '#6366f1' },
//               { label: 'Users',       value: allUsers.length || '—',                     color: '#0ea5e9' },
//               { label: 'Scheduled',   value: scheduledMsgs.length,                       color: '#f59e0b' },
//               { label: 'History',     value: msgHistory.reduce((s,m) => s + (m.sent_count||0), 0) || 0, color: '#10b981' },
//             ].map(s => (
//               <div key={s.label} style={{
//                 flex: 1, background: '#f8fafc', borderRadius: 8, padding: '8px 10px',
//                 textAlign: 'center', borderTop: `3px solid ${s.color}`,
//               }}>
//                 <div style={{ fontSize: 18, fontWeight: 800, color: s.color }}>{s.value}</div>
//                 <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{s.label}</div>
//               </div>
//             ))}
//           </div>
//           {/* Tab bar */}
//           <div style={{ display: 'flex', gap: 4 }}>
//             {[
//               { id: 'welcome',   icon: '👋', label: 'Welcome Message' },
//               { id: 'broadcast', icon: '📣', label: 'Broadcast' },
//               { id: 'history',   icon: '📬', label: `History ${msgHistory.length > 0 ? `(${msgHistory.length})` : ''}` },
//               { id: 'scheduled', icon: '🕐', label: `Scheduled ${scheduledMsgs.length > 0 ? `(${scheduledMsgs.length})` : ''}` },
//             ].map(t => (
//               <button key={t.id} onClick={() => setCommTab(t.id)} style={{
//                 padding: '10px 18px', border: 'none', borderRadius: '8px 8px 0 0',
//                 background: commTab === t.id ? '#fff' : 'transparent',
//                 color: commTab === t.id ? '#6366f1' : '#64748b',
//                 fontWeight: commTab === t.id ? 700 : 500,
//                 fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
//                 borderBottom: commTab === t.id ? '2px solid #6366f1' : '2px solid transparent',
//                 transition: 'all 0.15s',
//               }}>
//                 <span>{t.icon}</span><span>{t.label}</span>
//               </button>
//             ))}
//           </div>
//         </div>

//         {/* ─────────── WELCOME MESSAGE tab ─────────── */}
//         {commTab === 'welcome' && (
//           <div style={{ padding: 24 }}>
//             <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
//               <div>
//                 <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginBottom: 4 }}>👋 Welcome Message</div>
//                 <div style={{ fontSize: 12, color: '#94a3b8' }}>Sent to every new user when they sign up — appears in their I'm Selling inbox</div>
//               </div>
//               {!welcomeEditing ? (
//                 <button onClick={() => { setWelcomeDraft(welcomeMsg); setWelcomeEditing(true); }} style={{
//                   padding: '8px 18px', background: '#eef2ff', color: '#6366f1', border: '1.5px solid #c7d2fe',
//                   borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
//                 }}>✏️ Edit</button>
//               ) : (
//                 <div style={{ display: 'flex', gap: 8 }}>
//                   <button onClick={() => setWelcomeEditing(false)} style={{ padding: '8px 16px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
//                   <button onClick={handleWelcomeSave} disabled={welcomeSaving} style={{ padding: '8px 18px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: welcomeSaving ? 0.6 : 1 }}>
//                     {welcomeSaving ? 'Saving…' : '💾 Save'}
//                   </button>
//                 </div>
//               )}
//             </div>
//             {!welcomeEditing ? (
//               <div style={{ background: '#f1f5f9', borderRadius: 12, padding: '16px 18px', maxWidth: 560 }}>
//                 <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
//                   <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>🏪</div>
//                   <span style={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>I'm Selling</span>
//                   <span style={{ fontSize: 11, color: '#94a3b8' }}>Official</span>
//                 </div>
//                 <div style={{ fontSize: 13.5, color: '#334155', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{welcomeMsg}</div>
//               </div>
//             ) : (
//               <div>
//                 <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 8, padding: '9px 13px', marginBottom: 12, fontSize: 12, color: '#4338ca' }}>
//                   <span>✏️</span><span>Edit the welcome message — plain text and emoji supported</span>
//                 </div>
//                 <textarea value={welcomeDraft} onChange={e => setWelcomeDraft(e.target.value)} rows={8} style={{
//                   width: '100%', padding: '11px 13px', border: '1.5px solid #c7d2fe', borderRadius: 8,
//                   fontSize: 13.5, fontFamily: 'inherit', color: '#1e293b', background: '#f8fafc',
//                   resize: 'vertical', outline: 'none', boxSizing: 'border-box',
//                 }} />
//                 <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'right', marginTop: 4 }}>{welcomeDraft.length} characters</div>
//               </div>
//             )}
//           </div>
//         )}

//         {/* ─────────── BROADCAST tab ─────────── */}
//         {commTab === 'broadcast' && (
//           <div style={{ padding: 24 }}>
//             <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginBottom: 4 }}>📣 Broadcast Message</div>
//             <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 20 }}>Send a direct message via I'm Selling inbox to all users or selected users</div>

//             {broadcastResult && (
//               <div style={{
//                 display: 'flex', justifyContent: 'space-between', alignItems: 'center',
//                 padding: '11px 16px', borderRadius: 8, marginBottom: 18,
//                 background: broadcastResult.success ? '#dcfce7' : '#fee2e2',
//                 color: broadcastResult.success ? '#15803d' : '#dc2626',
//                 border: `1px solid ${broadcastResult.success ? '#86efac' : '#fca5a5'}`,
//                 fontSize: 13.5, fontWeight: 600,
//               }}>
//                 <span>{broadcastResult.success
//                   ? `✅ Sent to ${broadcastResult.sent} user${broadcastResult.sent !== 1 ? 's' : ''}${broadcastResult.failed > 0 ? ` (${broadcastResult.failed} failed)` : ''}!`
//                   : `❌ ${broadcastResult.error}`}
//                 </span>
//                 <button onClick={() => setBroadcastResult(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'inherit', lineHeight: 1 }}>×</button>
//               </div>
//             )}

//             <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
//               <div>
//                 <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
//                   Message Title <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: 12 }}>(optional)</span>
//                 </label>
//                 <input value={broadcastTitle} onChange={e => setBroadcastTitle(e.target.value)}
//                   placeholder="e.g. New Feature Alert 🎉"
//                   style={{ width: '100%', height: 38, padding: '0 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13.5, background: '#f8fafc', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
//               </div>
//               <div>
//                 <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Message *</label>
//                 <textarea value={broadcastMsg} onChange={e => setBroadcastMsg(e.target.value)}
//                   rows={5} placeholder="Type your message to users here…"
//                   style={{ width: '100%', padding: '11px 13px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13.5, fontFamily: 'inherit', color: '#1e293b', background: '#f8fafc', resize: 'vertical', outline: 'none', boxSizing: 'border-box' }} />
//                 <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'right', marginTop: 3 }}>{broadcastMsg.length} characters</div>
//               </div>
//               <div>
//                 <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 8 }}>Send To</label>
//                 <div style={{ display: 'flex', gap: 10 }}>
//                   {[{ v: 'all', label: `🌍 All Users`, count: allUsers.length }, { v: 'selected', label: '👤 Selected Users', count: selectedUsers.length }].map(opt => (
//                     <button key={opt.v} onClick={() => setBroadcastTarget(opt.v)} style={{
//                       display: 'flex', alignItems: 'center', gap: 8, padding: '9px 18px',
//                       border: `2px solid ${broadcastTarget === opt.v ? '#6366f1' : '#e2e8f0'}`,
//                       borderRadius: 9, background: broadcastTarget === opt.v ? '#eef2ff' : '#fff',
//                       color: broadcastTarget === opt.v ? '#6366f1' : '#475569',
//                       fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
//                     }}>
//                       {opt.label}
//                       {opt.count > 0 && (
//                         <span style={{ background: broadcastTarget === opt.v ? '#6366f1' : '#e2e8f0', color: broadcastTarget === opt.v ? '#fff' : '#475569', borderRadius: 12, padding: '1px 8px', fontSize: 11, fontWeight: 700 }}>{opt.count}</span>
//                       )}
//                     </button>
//                   ))}
//                 </div>
//               </div>

//               {broadcastTarget === 'selected' && (
//                 <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
//                   <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
//                     <input value={userSearch} onChange={e => setUserSearch(e.target.value)}
//                       placeholder="🔍 Search users by email or name…"
//                       style={{ width: '100%', height: 34, padding: '0 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, background: '#fff', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
//                   </div>
//                   {selectedUsers.length > 0 && (
//                     <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>
//                       {selectedUsers.map(uid => {
//                         const u = allUsers.find(x => x.id === uid);
//                         return u ? (
//                           <span key={uid} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#eef2ff', color: '#6366f1', border: '1px solid #c7d2fe', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>
//                             {u.email || u.username}
//                             <button onClick={() => toggleUserSelect(uid)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
//                           </span>
//                         ) : null;
//                       })}
//                     </div>
//                   )}
//                   <div style={{ maxHeight: 200, overflowY: 'auto' }}>
//                     {usersLoading ? (
//                       <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Loading users…</div>
//                     ) : allUsers.filter(u => {
//                       if (!userSearch) return true;
//                       const q = userSearch.toLowerCase();
//                       return (u.email||'').toLowerCase().includes(q) || (u.username||'').toLowerCase().includes(q) || (u.first_name||'').toLowerCase().includes(q);
//                     }).slice(0, 30).map(u => (
//                       <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 13px', cursor: 'pointer', borderBottom: '1px solid #f8fafc', background: selectedUsers.includes(u.id) ? '#f0f4ff' : 'white', transition: 'background 0.12s' }}>
//                         <input type="checkbox" checked={selectedUsers.includes(u.id)} onChange={() => toggleUserSelect(u.id)} style={{ accentColor: '#6366f1' }} />
//                         <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#6366f1', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
//                           {(u.email || u.username || '?')[0].toUpperCase()}
//                         </div>
//                         <div style={{ minWidth: 0 }}>
//                           <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{u.first_name ? `${u.first_name} ${u.last_name||''}`.trim() : u.username}</div>
//                           <div style={{ fontSize: 11, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
//                         </div>
//                       </label>
//                     ))}
//                   </div>
//                 </div>
//               )}

//               <button onClick={handleBroadcast} disabled={broadcasting || !broadcastMsg.trim()} style={{
//                 display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 28px',
//                 background: broadcasting || !broadcastMsg.trim() ? '#e2e8f0' : '#6366f1',
//                 color: broadcasting || !broadcastMsg.trim() ? '#94a3b8' : '#fff',
//                 border: 'none', borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: broadcasting || !broadcastMsg.trim() ? 'not-allowed' : 'pointer',
//                 transition: 'all 0.15s', alignSelf: 'flex-start',
//               }}>
//                 {broadcasting ? '⏳ Sending…' : broadcastTarget === 'all' ? '📣 Send to All Users' : `📣 Send to ${selectedUsers.length} User${selectedUsers.length !== 1 ? 's' : ''}`}
//               </button>
//             </div>
//           </div>
//         )}

//         {/* ─────────── HISTORY tab ─────────── */}
//         {commTab === 'history' && (
//           <div>
//             <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 12px' }}>
//               <div>
//                 <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginBottom: 4 }}>📬 Message History</div>
//                 <div style={{ fontSize: 12, color: '#94a3b8' }}>All admin broadcasts — delete removes from every user's I'm Selling inbox</div>
//               </div>
//               <button onClick={loadHistory} style={{ padding: '7px 16px', background: '#f5f3ff', color: '#7c3aed', border: '1.5px solid #ddd6fe', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>🔄 Refresh</button>
//             </div>
//             <div style={{ padding: '0 24px 14px' }}>
//               <input value={historySearch} onChange={e => setHistorySearch(e.target.value)}
//                 placeholder="🔍 Search by title or content…"
//                 style={{ width: '100%', height: 38, padding: '0 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#f8fafc', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
//             </div>
//             {historyLoading ? (
//               <div style={{ padding: '48px 24px', textAlign: 'center', color: '#94a3b8' }}>
//                 <div style={{ fontSize: 32, marginBottom: 10 }}>⏳</div>
//                 <div style={{ fontSize: 13 }}>Loading message history…</div>
//               </div>
//             ) : msgHistory.length === 0 ? (
//               <div style={{ padding: '52px 24px', textAlign: 'center', color: '#94a3b8' }}>
//                 <div style={{ fontSize: 44, marginBottom: 12 }}>📭</div>
//                 <div style={{ fontSize: 14, fontWeight: 600, color: '#475569', marginBottom: 6 }}>No broadcast messages sent yet</div>
//                 <div style={{ fontSize: 13 }}>Messages you broadcast will appear here with delete controls.</div>
//               </div>
//             ) : (
//               <div style={{ borderTop: '1px solid #f1f5f9' }}>
//                 {msgHistory.filter(m => {
//                   if (!historySearch) return true;
//                   const q = historySearch.toLowerCase();
//                   return (m.title||'').toLowerCase().includes(q) || (m.body||'').toLowerCase().includes(q);
//                 }).map(m => (
//                   <div key={m.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '16px 24px', borderBottom: '1px solid #f1f5f9', transition: 'background 0.12s' }}
//                     onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
//                     onMouseLeave={e => e.currentTarget.style.background='white'}>
//                     <div style={{ width: 42, height: 42, borderRadius: 10, background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>📣</div>
//                     <div style={{ flex: 1, minWidth: 0 }}>
//                       <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>{m.title || '(No title)'}</div>
//                       <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.55, marginBottom: 7, whiteSpace: 'pre-wrap' }}>{(m.body||'').slice(0, 140)}{(m.body||'').length > 140 ? '…' : ''}</div>
//                       <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 11.5, color: '#64748b', fontWeight: 500 }}>
//                         <span>📅 {m.created_at ? new Date(m.created_at).toLocaleString('en-KE',{day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : '—'}</span>
//                         <span style={{ color: '#6366f1', fontWeight: 700 }}>👥 {m.sent_count} recipient{m.sent_count !== 1 ? 's' : ''}</span>
//                         {m.recipients_preview?.length > 0 && (
//                           <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>
//                             {m.recipients_preview.slice(0,2).join(', ')}{m.sent_count > 2 ? ` +${m.sent_count-2} more` : ''}
//                           </span>
//                         )}
//                       </div>
//                     </div>
//                     <button disabled={deletingMsgId === m.id} onClick={() => handleDeleteBroadcast(m.id, m.title)}
//                       title="Delete from all inboxes"
//                       style={{ background: 'none', border: 'none', cursor: deletingMsgId === m.id ? 'not-allowed' : 'pointer', fontSize: 18, padding: '6px 8px', borderRadius: 6, transition: 'background 0.12s', flexShrink: 0, opacity: deletingMsgId === m.id ? 0.5 : 1 }}
//                       onMouseEnter={e => e.currentTarget.style.background='#fee2e2'}
//                       onMouseLeave={e => e.currentTarget.style.background='transparent'}>
//                       {deletingMsgId === m.id ? '⏳' : '🗑️'}
//                     </button>
//                   </div>
//                 ))}
//               </div>
//             )}
//           </div>
//         )}

//         {/* ─────────── SCHEDULED tab ─────────── */}
//         {commTab === 'scheduled' && (
//           <div>
//             <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 16px', borderBottom: '1px solid #f1f5f9' }}>
//               <div>
//                 <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginBottom: 4 }}>🕐 Scheduled Messages</div>
//                 <div style={{ fontSize: 12, color: '#94a3b8' }}>Queue messages to send automatically at a future date and time</div>
//               </div>
//               <button onClick={() => setShowScheduleForm(s => !s)} style={{ padding: '8px 16px', background: '#fffbeb', color: '#d97706', border: '1.5px solid #fde68a', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
//                 {showScheduleForm ? '✕ Cancel' : '+ Schedule Message'}
//               </button>
//             </div>

//             {showScheduleForm && (
//               <div style={{ padding: '18px 24px', borderBottom: '1px solid #f1f5f9', background: '#fffbeb' }}>
//                 <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 18px', marginBottom: 14 }}>
//                   {[
//                     { label: 'Title (optional)', key: 'title', type: 'text', placeholder: 'e.g. Weekly Tip', col: 1 },
//                     { label: 'Send To', key: 'target', type: 'select', col: 1 },
//                     { label: 'Send At *', key: 'sendAt', type: 'datetime-local', col: 1 },
//                   ].map(f => (
//                     <div key={f.key} style={{ gridColumn: f.col === 'all' ? '1/-1' : undefined }}>
//                       <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>{f.label}</label>
//                       {f.type === 'select' ? (
//                         <select value={scheduleForm.target} onChange={e => setScheduleForm(p => ({...p, target: e.target.value}))}
//                           style={{ width: '100%', height: 36, padding: '0 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, background: '#fff', fontFamily: 'inherit' }}>
//                           <option value="all">All Users</option>
//                           <option value="new">New Users (last 7 days)</option>
//                           <option value="inactive">Inactive Users (30+ days)</option>
//                         </select>
//                       ) : (
//                         <input type={f.type} value={scheduleForm[f.key]} placeholder={f.placeholder||''}
//                           min={f.type==='datetime-local' ? new Date().toISOString().slice(0,16) : undefined}
//                           onChange={e => setScheduleForm(p => ({...p, [f.key]: e.target.value}))}
//                           style={{ width: '100%', height: 36, padding: '0 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, background: '#fff', fontFamily: 'inherit', boxSizing: 'border-box' }} />
//                       )}
//                     </div>
//                   ))}
//                   <div style={{ gridColumn: '1/-1' }}>
//                     <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>Message *</label>
//                     <textarea value={scheduleForm.message} onChange={e => setScheduleForm(p => ({...p, message: e.target.value}))}
//                       rows={3} placeholder="Message content…"
//                       style={{ width: '100%', padding: '9px 11px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', outline: 'none', boxSizing: 'border-box' }} />
//                   </div>
//                 </div>
//                 <button onClick={handleAddScheduled} style={{ padding: '9px 22px', background: '#d97706', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>🕐 Add to Schedule</button>
//               </div>
//             )}

//             {scheduledMsgs.length === 0 ? (
//               <div style={{ padding: '52px 24px', textAlign: 'center', color: '#94a3b8' }}>
//                 <div style={{ fontSize: 44, marginBottom: 12 }}>🕐</div>
//                 <div style={{ fontSize: 14, fontWeight: 600, color: '#475569', marginBottom: 6 }}>No scheduled messages</div>
//                 <div style={{ fontSize: 13 }}>Click "+ Schedule Message" to queue one.</div>
//               </div>
//             ) : (
//               scheduledMsgs.map(m => (
//                 <div key={m.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '15px 24px', borderBottom: '1px solid #f1f5f9' }}>
//                   <div style={{ width: 40, height: 40, borderRadius: 10, background: '#fffbeb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>🕐</div>
//                   <div style={{ flex: 1, minWidth: 0 }}>
//                     <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 3 }}>{m.title||'(No title)'}</div>
//                     <div style={{ fontSize: 13, color: '#475569', marginBottom: 6 }}>{m.message.slice(0,80)}{m.message.length>80?'…':''}</div>
//                     <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 11.5, color: '#64748b', fontWeight: 500 }}>
//                       <span>📅 {new Date(m.sendAt).toLocaleString('en-KE',{day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'})}</span>
//                       <span>👥 {m.target==='all'?'All Users':m.target==='new'?'New Users':'Inactive Users'}</span>
//                       <span style={{ background: '#fef3c7', color: '#b45309', borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>{m.status}</span>
//                     </div>
//                   </div>
//                   <button onClick={() => handleDeleteScheduled(m.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: '6px 8px', borderRadius: 6 }}
//                     onMouseEnter={e => e.currentTarget.style.background='#fee2e2'}
//                     onMouseLeave={e => e.currentTarget.style.background='transparent'}>🗑️</button>
//                 </div>
//               ))
//             )}
//           </div>
//         )}
//       </div>

//       {/* ── Admin Management      </div>

//       {/* ── Admin Management ─────────────────────────────────────────────── */}
//       <div className="admin-management-section">
//         <div className="section-header">
//           <div className="admins-header-left">
//             <h2>Admin Management</h2>
//             {!adminsLoading && filteredAdmins.length > 5 && (
//               <span className="admins-count-badge">{filteredAdmins.length} admins</span>
//             )}
//           </div>
//           <div className="header-actions">
//             <div className="search-box-compact">
//               <span className="search-icon">🔍</span>
//               <input
//                 type="text"
//                 placeholder="Search admins…"
//                 value={adminSearchTerm}
//                 onChange={e => setAdminSearchTerm(e.target.value)}
//               />
//             </div>
//             <button className="btn-secondary" onClick={() => setShowAddModal(true)}>
//               + Add Admin
//             </button>
//           </div>
//         </div>

//         {error && (
//           <div style={{
//             padding: '12px 16px', marginBottom: 16,
//             background: '#fff5f5', border: '1px solid #feb2b2',
//             borderRadius: 8, color: '#c53030', fontSize: 14,
//           }}>
//             ⚠️ {error}
//             <button
//               onClick={loadAdmins}
//               style={{
//                 marginLeft: 12, color: '#3182ce', background: 'none',
//                 border: 'none', cursor: 'pointer', textDecoration: 'underline',
//               }}
//             >
//               Retry
//             </button>
//           </div>
//         )}

//         {adminsLoading ? (
//           <div className="loading-container">
//             <div className="spinner" />
//             <p>Loading admins…</p>
//           </div>
//         ) : (
//           <>
//             <div className="admins-table-wrapper">
//               {/* Sticky header — sits outside scroll zone */}
//               <table className="admins-table admins-table-head">
//                 <thead>
//                   <tr>
//                     <th style={{ width: '22%' }}>ADMIN</th>
//                     <th style={{ width: '20%' }}>EMAIL</th>
//                     <th style={{ width: '13%' }}>PHONE</th>
//                     <th style={{ width: '12%' }}>ROLE</th>
//                     <th style={{ width: '11%' }}>STATUS</th>
//                     <th style={{ width: '14%' }}>JOINED</th>
//                     <th style={{ width: '8%' }} className="th-actions">ACTIONS</th>
//                   </tr>
//                 </thead>
//               </table>

//               {/* Scrollable body — ~5 rows visible */}
//               <div className="admins-scroll-body">
//                 <table className="admins-table admins-table-body">
//                   <colgroup>
//                     <col style={{ width: '22%' }} />
//                     <col style={{ width: '20%' }} />
//                     <col style={{ width: '13%' }} />
//                     <col style={{ width: '12%' }} />
//                     <col style={{ width: '11%' }} />
//                     <col style={{ width: '14%' }} />
//                     <col style={{ width: '8%' }} />
//                   </colgroup>
//                   <tbody>
//                     {filteredAdmins.length === 0 ? (
//                       <tr>
//                         <td colSpan="7" className="empty-cell">
//                           <div className="empty-state">
//                             <span style={{ fontSize: 40 }}>👥</span>
//                             <h3>No admins found</h3>
//                             <p>
//                               {adminSearchTerm
//                                 ? 'Try adjusting your search'
//                                 : 'Add your first admin above'}
//                             </p>
//                           </div>
//                         </td>
//                       </tr>
//                     ) : filteredAdmins.map(admin => {
//                       const role   = getRoleBadge(admin);
//                       const status = getStatusBadge(admin);
//                       return (
//                         <tr key={admin.id}>
//                           <td>
//                             <div className="admin-cell">
//                               <div className="admin-avatar">{getInitials(admin)}</div>
//                               <div className="admin-info">
//                                 <div className="admin-name">
//                                   {[admin.first_name, admin.last_name].filter(Boolean).join(' ') || admin.username}
//                                 </div>
//                                 <div className="admin-username">@{admin.username}</div>
//                               </div>
//                             </div>
//                           </td>
//                           <td className="email-cell">{admin.email || 'N/A'}</td>
//                           <td className="phone-cell">{admin.phone_number || admin.phone || 'N/A'}</td>
//                           <td>
//                             <span className={`role-badge role-${role.cls}`}>{role.label}</span>
//                           </td>
//                           <td>
//                             <span className={`status-badge status-${status.cls}`}>
//                               ● {status.label}
//                             </span>
//                           </td>
//                           <td className="date-cell">
//                             {formatDate(admin.date_joined || admin.created_at)}
//                           </td>
//                           <td>
//                             <div className="actions-cell-dropdown">
//                               <button
//                                 className="action-menu-btn"
//                                 onClick={e => {
//                                   e.stopPropagation();
//                                   setActiveDropdown(activeDropdown === admin.id ? null : admin.id);
//                                 }}
//                               >
//                                 ⋮
//                               </button>
//                               {activeDropdown === admin.id && (
//                                 <div className="action-dropdown-menu">
//                                   <button onClick={() => openEditModal(admin)}>✏️ Edit</button>
//                                   <button onClick={() => handleToggleStatus(admin)}>
//                                     {admin.is_active !== false ? '🔒 Suspend' : '✅ Activate'}
//                                   </button>
//                                   <button
//                                     className="danger"
//                                     onClick={() => handleRevokeAdmin(admin)}
//                                   >
//                                     🚫 Revoke Admin
//                                   </button>
//                                 </div>
//                               )}
//                             </div>
//                           </td>
//                         </tr>
//                       );
//                     })}
//                   </tbody>
//                 </table>
//               </div>
//             </div>

//             {filteredAdmins.length > 5 && (
//               <p className="admins-scroll-hint">
//                 ↕ Scroll to see all {filteredAdmins.length} admins
//               </p>
//             )}
//           </>
//         )}
//       </div>

//       {/* ── Add Admin Modal ──────────────────────────────────────────────── */}
//       {showAddModal && (
//         <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
//           <div className="modal-box" onClick={e => e.stopPropagation()}>
//             <div className="modal-header">
//               <h2>Add New Admin</h2>
//               <button className="modal-close-btn" onClick={() => setShowAddModal(false)}>×</button>
//             </div>
//             <form className="modal-form" onSubmit={handleAddAdmin}>
//               <div className="form-row-two">
//                 <div className="form-field">
//                   <label>First Name</label>
//                   <input
//                     type="text"
//                     value={newAdminData.first_name}
//                     onChange={e => setNewAdminData({ ...newAdminData, first_name: e.target.value })}
//                     placeholder="John"
//                   />
//                 </div>
//                 <div className="form-field">
//                   <label>Last Name</label>
//                   <input
//                     type="text"
//                     value={newAdminData.last_name}
//                     onChange={e => setNewAdminData({ ...newAdminData, last_name: e.target.value })}
//                     placeholder="Doe"
//                   />
//                 </div>
//               </div>
//               <div className="form-row-two">
//                 <div className="form-field">
//                   <label>Email *</label>
//                   <input
//                     type="email"
//                     required
//                     value={newAdminData.email}
//                     onChange={e => setNewAdminData({ ...newAdminData, email: e.target.value })}
//                     placeholder="admin@example.com"
//                   />
//                 </div>
//                 <div className="form-field">
//                   <label>Phone</label>
//                   <input
//                     type="tel"
//                     value={newAdminData.phone}
//                     onChange={e => setNewAdminData({ ...newAdminData, phone: e.target.value })}
//                     placeholder="+254712345678"
//                   />
//                 </div>
//               </div>
//               <div className="form-field">
//                 <label>Role</label>
//                 <select
//                   value={newAdminData.role}
//                   onChange={e => setNewAdminData({ ...newAdminData, role: e.target.value })}
//                 >
//                   <option value="admin">Admin</option>
//                   <option value="superadmin">Super Admin</option>
//                 </select>
//               </div>
//               <div className="modal-actions">
//                 <button type="button" className="btn-cancel" onClick={() => setShowAddModal(false)}>
//                   Cancel
//                 </button>
//                 <button type="submit" className="btn-submit" disabled={submitting}>
//                   {submitting ? 'Creating…' : 'Create Admin'}
//                 </button>
//               </div>
//             </form>
//           </div>
//         </div>
//       )}

//       {/* ── Edit Admin Modal ─────────────────────────────────────────────── */}
//       {showEditModal && selectedAdmin && (
//         <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
//           <div className="modal-box" onClick={e => e.stopPropagation()}>
//             <div className="modal-header">
//               <h2>Edit Admin</h2>
//               <button className="modal-close-btn" onClick={() => setShowEditModal(false)}>×</button>
//             </div>
//             <form className="modal-form" onSubmit={handleEditAdmin}>
//               <div className="form-row-two">
//                 <div className="form-field">
//                   <label>First Name</label>
//                   <input
//                     type="text"
//                     value={editAdminData.first_name}
//                     onChange={e => setEditAdminData({ ...editAdminData, first_name: e.target.value })}
//                   />
//                 </div>
//                 <div className="form-field">
//                   <label>Last Name</label>
//                   <input
//                     type="text"
//                     value={editAdminData.last_name}
//                     onChange={e => setEditAdminData({ ...editAdminData, last_name: e.target.value })}
//                   />
//                 </div>
//               </div>
//               <div className="form-row-two">
//                 <div className="form-field">
//                   <label>Email</label>
//                   <input
//                     type="email"
//                     value={editAdminData.email}
//                     onChange={e => setEditAdminData({ ...editAdminData, email: e.target.value })}
//                   />
//                 </div>
//                 <div className="form-field">
//                   <label>Phone</label>
//                   <input
//                     type="tel"
//                     value={editAdminData.phone}
//                     onChange={e => setEditAdminData({ ...editAdminData, phone: e.target.value })}
//                   />
//                 </div>
//               </div>
//               <div className="form-field">
//                 <label>Username</label>
//                 <input
//                   type="text"
//                   value={editAdminData.username}
//                   disabled
//                   style={{ opacity: 0.6, cursor: 'not-allowed' }}
//                 />
//               </div>
//               <div className="form-checkbox">
//                 <input
//                   type="checkbox"
//                   id="edit_superuser"
//                   checked={editAdminData.is_superuser}
//                   onChange={e => setEditAdminData({ ...editAdminData, is_superuser: e.target.checked })}
//                 />
//                 <label htmlFor="edit_superuser">Super Admin (Full Access)</label>
//               </div>
//               <div className="form-checkbox">
//                 <input
//                   type="checkbox"
//                   id="edit_active"
//                   checked={editAdminData.is_active}
//                   onChange={e => setEditAdminData({ ...editAdminData, is_active: e.target.checked })}
//                 />
//                 <label htmlFor="edit_active">Active Account</label>
//               </div>
//               <div className="modal-actions">
//                 <button type="button" className="btn-cancel" onClick={() => setShowEditModal(false)}>
//                   Cancel
//                 </button>
//                 <button type="submit" className="btn-submit" disabled={submitting}>
//                   {submitting ? 'Saving…' : 'Save Changes'}
//                 </button>
//               </div>
//             </form>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }

// export default SuperAdminView;

